import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Container, Grid,
    FormControl, InputLabel, Select, MenuItem, TextField,
    RadioGroup, FormControlLabel, Radio, Button, Card, CardContent,
    CircularProgress, Alert, Chip, Fade, Grow, Dialog, DialogTitle,
    DialogContent, DialogActions, LinearProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, IconButton, Slide, Tooltip, Checkbox
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/Upload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import RuleIcon from '@mui/icons-material/Rule';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Header from '../Header'; // Assuming Header component exists
import { HorecaUpload } from '../RateUpload/HorecaUpload';
import * as XLSX from 'xlsx';
// Assuming local_url is defined in a constants file
import { local_signage, local_url, retail_uat } from '../Const';
import { getServerTodayStr, getCachedServerTodayStr } from '../utils/serverDate';
// const local_url = "https://your-api-url.com"; // Placeholder for local_url

// --- Enhanced Style & Color Definitions ---
const colors = {
    primary: '#4f46e5', // Modern indigo
    primaryLight: '#6366f1',
    primaryDark: '#3730a3',
    primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',

    success: '#10b981', // Modern emerald
    successLight: '#34d399',
    successDark: '#059669',
    successGradient: 'linear-gradient(135deg, #a7f3d0 0%, #10b981 100%)',

    error: '#ef4444', // Modern red
    errorLight: '#f87171',
    errorDark: '#dc2626',

    warning: '#f59e0b', // Modern amber
    warningLight: '#fbbf24',
    warningDark: '#d97706',

    info: '#3b82f6', // Modern blue
    infoLight: '#60a5fa',
    infoDark: '#2563eb',

    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',

    border: '#e2e8f0',
    borderLight: '#f1f5f9',
    borderDark: '#cbd5e1',

    background: '#fafafa',
    backgroundCard: '#ffffff',
    backgroundGray: '#f8fafc',
    backgroundAccent: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',

    white: '#ffffff',
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    shadowXl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
};

// --- Enhanced Glassmorphism Style Object ---
const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid rgba(255, 255, 255, 0.3)`,
    boxShadow: colors.shadowXl,
    borderRadius: '16px'
};

// --- Modern Card Style ---
const modernCardStyle = {
    background: colors.backgroundCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '16px',
    boxShadow: colors.shadow,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: colors.shadowLg,
        borderColor: colors.borderDark
    }
};

// --- Gradient Button Styles ---
const gradientButtonStyle = {
    background: colors.primaryGradient,
    color: colors.white,
    border: 'none',
    borderRadius: '12px',
    fontWeight: 600,
    textTransform: 'none',
    padding: '10px 24px',
    boxShadow: colors.shadowMd,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: colors.shadowLg,
        background: `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
    },
    '&:active': {
        transform: 'translateY(0px)'
    }
};

// --- Reusable UI Components (Moved Outside Main Component) ---

const CustomerSearchModal = ({ open, onClose, searchResults, selectedCustomers, handleCustomerSelect, handleConfirmCustomerSelection }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonSearchIcon sx={{ mr: 1, color: colors.primary }} /> Select Customers (Multiple Selection)
            </Box>
            <Chip
                label={`${selectedCustomers.length} Selected`}
                color="primary"
                size="small"
                sx={{ backgroundColor: colors.success, color: 'white' }}
            />
        </DialogTitle>
        <DialogContent>
            <Grid container spacing={1.5} sx={{ mt: 1 }}>
                {searchResults.map((customer, index) => {
                    const customerId = customer.custID || customer.customerCode;
                    const isSelected = selectedCustomers.some(c => (c.custID || c.customerCode) === customerId);
                    return (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card onClick={(event) => handleCustomerSelect(customer, event)} sx={{ border: isSelected ? `2px solid ${colors.success}` : `1px solid ${colors.border}`, cursor: 'pointer', position: 'relative', transition: 'all 0.2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }, backgroundColor: 'rgba(255,255,255,0.8)' }}>
                                {isSelected && <CheckCircleIcon sx={{ position: 'absolute', top: 6, right: 6, color: colors.success, fontSize: '18px', backgroundColor: 'white', borderRadius: '50%' }} />}
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Typography variant="subtitle2" noWrap sx={{ display: 'flex', alignItems: 'center' }}><PersonOutlineIcon sx={{ mr: 0.5, fontSize: '16px' }} />{customer.customerName}</Typography>
                                    <Chip icon={<PhoneIcon sx={{ fontSize: '14px' }} />} label={customer.mobile || 'N/A'} size="small" sx={{ mt: 1, fontSize: '0.75rem', backgroundColor: '#e0e0e0' }} />
                                    <Chip label={`ID: ${customer.custID || customer.customerCode || 'N/A'}`} size="small" sx={{ mt: 1, ml: 1, fontSize: '0.75rem', backgroundColor: '#004D00', color: 'white' }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={handleConfirmCustomerSelection} sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryDark } }} disabled={selectedCustomers.length === 0}>Confirm Selection ({selectedCustomers.length})</Button></DialogActions>
    </Dialog>
);

const MappingProgressDialog = ({ open, loadingMapping, onClose, mappingProgress, mappingStatus, totalCustomersToUpload, currentCustomerUploadIndex, uploadResults }) => (
    <Dialog open={open} onClose={loadingMapping ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}><UploadIcon sx={{ mr: 1, color: colors.primary }} /> Processing Upload</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            {totalCustomersToUpload > 1 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Uploading for Customer {currentCustomerUploadIndex + 1} of {totalCustomersToUpload}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={(currentCustomerUploadIndex / totalCustomersToUpload) * 100}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            mb: 1,
                            '& .MuiLinearProgress-bar': { backgroundColor: colors.info }
                        }}
                    />
                </Box>
            )}

            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Current Upload: {mappingProgress.toFixed(0)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={mappingProgress} sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: colors.success } }} />
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: colors.textSecondary }}>{mappingStatus}</Typography>
            </Box>

            {uploadResults && uploadResults.length > 0 && (
                <Box sx={{ maxHeight: 150, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 2, p: 1 }}>
                    {uploadResults.map((result, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {result.success ? (
                                <CheckCircleIcon sx={{ color: colors.success, fontSize: '16px', mr: 1 }} />
                            ) : (
                                <CloseIcon sx={{ color: colors.error, fontSize: '16px', mr: 1 }} />
                            )}
                            <Typography variant="caption" sx={{ color: result.success ? colors.success : colors.error }}>
                                {result.customerName}: {result.success ? 'Success' : result.error}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </DialogContent>
        <DialogActions><Button onClick={onClose} disabled={loadingMapping}>{loadingMapping ? 'Processing...' : 'Close'}</Button></DialogActions>
    </Dialog>
);

const ValidationProgressDialog = ({ open, validationProgress, validationStatus, totalCustomers, onClose }) => (
    <Dialog open={open} onClose={undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <RuleIcon sx={{ mr: 1, color: colors.primary }} /> Validating Excel Data
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <CircularProgress size={40} sx={{ color: colors.primary }} />
            </Box>

            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Validation Progress: {validationProgress.toFixed(0)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={validationProgress}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': { backgroundColor: colors.primary }
                    }}
                />
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: colors.textSecondary }}>
                    {validationStatus}
                </Typography>
            </Box>

            {totalCustomers > 0 && (
                <Box sx={{
                    backgroundColor: colors.backgroundGray,
                    borderRadius: 2,
                    p: 2,
                    border: `1px solid ${colors.border}`
                }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: colors.textPrimary }}>
                        Selected Customers: {totalCustomers}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        Progress calculated as: {totalCustomers > 0 ? `${(100 / totalCustomers).toFixed(1)}% per customer` : '100% total'}
                    </Typography>
                </Box>
            )}
        </DialogContent>
    </Dialog>
);

const DeleteProgressDialog = ({ open, deleteProgress, deleteStatus, totalCustomers, deleteResults, onClose }) => (
    <Dialog open={open} onClose={undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <CloseIcon sx={{ mr: 1, color: colors.error }} /> Deleting Customer Schemes
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <CircularProgress size={40} sx={{ color: colors.error }} />
            </Box>

            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Delete Progress: {deleteProgress.toFixed(0)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={deleteProgress}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': { backgroundColor: colors.error }
                    }}
                />
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: colors.textSecondary }}>
                    {deleteStatus}
                </Typography>
            </Box>

            {totalCustomers > 1 && (
                <Box sx={{
                    backgroundColor: colors.backgroundGray,
                    borderRadius: 2,
                    p: 2,
                    border: `1px solid ${colors.border}`,
                    mb: 2
                }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: colors.textPrimary }}>
                        Deleting schemes for {totalCustomers} customers
                    </Typography>
                </Box>
            )}

            {deleteResults && deleteResults.length > 0 && (
                <Box sx={{ maxHeight: 150, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 2, p: 1 }}>
                    {deleteResults.map((result, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {result.success ? (
                                <CheckCircleIcon sx={{ color: colors.success, fontSize: '16px', mr: 1 }} />
                            ) : (
                                <CloseIcon sx={{ color: colors.error, fontSize: '16px', mr: 1 }} />
                            )}
                            <Typography variant="caption" sx={{ color: result.success ? colors.success : colors.error }}>
                                {result.customerName}: {result.success ? 'Deleted' : result.error}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </DialogContent>
        <DialogActions><Button onClick={onClose} disabled={deleteProgress > 0 && deleteProgress < 100}>Close</Button></DialogActions>
    </Dialog>
);

const CustomerMappingConfirmDialog = ({ open, onClose, customers, areaName, onConfirm }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonSearchIcon sx={{ mr: 1, color: colors.warning }} />
            Confirm Customer Mapping
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, color: colors.textPrimary }}>
                Are you sure you want to map {customers.length > 1 ? 'these customers' : 'this customer'} as HORECA in <strong>{areaName}</strong> AREA?
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 2, p: 2 }}>
                {customers.map((customer, index) => (
                    <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: colors.backgroundLight, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                            {customer.customerName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                            ID: {customer.custID || customer.CustCode || customer.CustomerId || customer.custCode}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Typography variant="caption" sx={{ mt: 2, color: colors.textSecondary, display: 'block' }}>
                This action will add {customers.length > 1 ? 'these customers' : 'this customer'} to the HORECA rebate scheme for the selected area.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} sx={{ color: colors.textSecondary }}>
                Cancel
            </Button>
            <Button
                variant="contained"
                onClick={onConfirm}
                sx={{
                    backgroundColor: colors.warning,
                    '&:hover': { backgroundColor: colors.warningDark }
                }}
            >
                Yes, Map Customer{customers.length > 1 ? 's' : ''}
            </Button>
        </DialogActions>
    </Dialog>
);

const MasterCustomerMappingDialog = ({ open, onClose, unmappedCustomers, onMapCustomer, onBulkMapping }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonAddIcon sx={{ mr: 1, color: colors.warning }} />
                Customer Mapping Required
            </Box>
            <Button onClick={onBulkMapping} variant="contained" size="small" sx={{ borderRadius: '8px', background: colors.primaryGradient }}>
                Map All
            </Button>
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
                The following customers are not mapped to any area and need to be mapped before upload:
            </Typography>
            <TableContainer sx={{ ...glassmorphismStyle, borderRadius: '8px' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: colors.textPrimary }}>Customer ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.textPrimary }}>Target Area ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.textPrimary }}>Row Numbers</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.textPrimary }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {unmappedCustomers.map((customer, index) => (
                            <TableRow key={index}>
                                <TableCell>{customer.CustomerID}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={customer.AreaID}
                                        size="small"
                                        sx={{
                                            background: colors.primaryGradient,
                                            color: colors.white,
                                            fontWeight: 500
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                        {customer.rowNumbers.join(', ')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => onMapCustomer(customer)}
                                        sx={{
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            borderColor: colors.primary,
                                            color: colors.primary,
                                            '&:hover': {
                                                borderColor: colors.primaryDark,
                                                backgroundColor: `${colors.primary}10`
                                            }
                                        }}
                                    >
                                        Map Now
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
            <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '8px' }}>Close</Button>
        </DialogActions>
    </Dialog>
);

const MapSchemeModal = ({
    open,
    onClose,
    customer,
    availableSchemes,
    selectedScheme,
    onSchemeSelect,
    schemeRateData,
    loadingSchemes,
    loadingRateData,
    onMapScheme
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon sx={{ mr: 1, color: colors.primary }} />
                Map Rebate Scheme
            </Box>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Customer: {customer?.CustomerName}
            </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, color: colors.textPrimary }}>
                        Select Rebate Scheme
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Rebate Scheme</InputLabel>
                        <Select
                            value={selectedScheme}
                            label="Rebate Scheme"
                            onChange={(e) => onSchemeSelect(e.target.value)}
                            disabled={loadingSchemes}
                        >
                            {availableSchemes.map((scheme) => (
                                <MenuItem key={scheme.RebateSchemeId} value={scheme.RebateSchemeId}>
                                    {scheme.RebateSchemeName}
                                </MenuItem>
                            ))}
                        </Select>
                        {loadingSchemes && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, color: colors.textPrimary }}>
                        Scheme Rate Details
                    </Typography>
                    {loadingRateData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : selectedScheme && schemeRateData.length > 0 ? (
                        <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Area Name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Item Name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Rate
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Qualify Qty
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {schemeRateData.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{row.AreaName}</TableCell>
                                            <TableCell>{row.ItemName}</TableCell>
                                            <TableCell>{row.Rate}</TableCell>
                                            <TableCell>{row.QualifyQty}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : selectedScheme && schemeRateData.length === 0 ? (
                        <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', mt: 4 }}>
                            No rate data available for selected scheme
                        </Typography>
                    ) : (
                        <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', mt: 4 }}>
                            Select a scheme to view rate details
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>
                Cancel
            </Button>
            <Button
                variant="contained"
                disabled={!selectedScheme}
                onClick={onMapScheme}
                sx={{
                    backgroundColor: colors.primary,
                    '&:hover': {
                        backgroundColor: colors.primaryDark
                    }
                }}
            >
                Map Scheme
            </Button>
        </DialogActions>
    </Dialog>
);

const StepBox = ({ title, icon, children }) => (
    <Box sx={{
        ...modernCardStyle,
        p: 1.5,
        height: '100%',
        background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundGray} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: colors.primaryGradient,
        }
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{
                p: 0.5,
                borderRadius: '8px',
                background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}25 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1
            }}>
                {React.cloneElement(icon, { sx: { ...icon.props.sx, fontSize: '18px' } })}
            </Box>
            <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                fontSize: '0.85rem',
                color: colors.textPrimary,
                background: colors.primaryGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
            }}>
                {title}
            </Typography>
        </Box>
        {children}
    </Box>
);


const UploadRateCustomerLookup = () => {
    const token = localStorage.getItem('AuthorizationToken');
    const userId = localStorage.getItem('userId') || 'UnknownUser';

    // --- State Definitions ---
    const [activeView, setActiveView] = useState('customer'); // 'customer' or 'master'
    const [userFilterType, setUserFilterType] = useState('existing'); // 'existing', 'multiple', or 'pos-master'

    // Existing User Filter states
    const [existingUserAreas, setExistingUserAreas] = useState([]);
    const [selectedExistingArea, setSelectedExistingArea] = useState('');
    const [existingCustomers, setExistingCustomers] = useState([]);
    const [selectedExistingCustomer, setSelectedExistingCustomer] = useState(null);
    const [loadingExistingCustomers, setLoadingExistingCustomers] = useState(false);
    const [customerDetailsData, setCustomerDetailsData] = useState([]);
    const [customerSchemeId, setCustomerSchemeId] = useState('');
    const [loadingCustomerDetails, setLoadingCustomerDetails] = useState(false);
    const [existingCustomerFilter, setExistingCustomerFilter] = useState("");

    // Read-only "View Rate" dialog state — separate from the New Rate edit flow above
    const [viewRateDialogOpen, setViewRateDialogOpen] = useState(false);
    const [viewRateCustomer, setViewRateCustomer] = useState(null);
    const [viewRateData, setViewRateData] = useState([]);
    const [loadingViewRate, setLoadingViewRate] = useState(false);
    const [isInvalidatingRate, setIsInvalidatingRate] = useState(false);
    const [invalidateConfirmOpen, setInvalidateConfirmOpen] = useState(false);
    const [customerToInvalidate, setCustomerToInvalidate] = useState(null);
    const [isInvalidatingAreaRate, setIsInvalidatingAreaRate] = useState(false);
    const [invalidateAreaConfirmOpen, setInvalidateAreaConfirmOpen] = useState(false);
    const [areaToInvalidate, setAreaToInvalidate] = useState(null);

    // "Upload Rate" popup — reuses the same selection/upload state as the inline New Rate flow
    const [uploadRateDialogOpen, setUploadRateDialogOpen] = useState(false);

    // Filter tab states for Rate Details vs Customers List
    const [detailsViewFilter, setDetailsViewFilter] = useState('rates'); // 'rates' or 'customers'
    const [customersListData, setCustomersListData] = useState([]);
    const [loadingCustomersList, setLoadingCustomersList] = useState(false);
    const [selectedCustomersToDelete, setSelectedCustomersToDelete] = useState([]);

    // Bulk Rates State for Master Upload
    const [bulkRates, setBulkRates] = useState(null);
    const [isLoadingBulkRates, setIsLoadingBulkRates] = useState(false);

    // Existing User Excel Upload states
    const [existingUserExcelFile, setExistingUserExcelFile] = useState(null);
    const [existingUserExcelFileName, setExistingUserExcelFileName] = useState('');
    const [existingUserExcelData, setExistingUserExcelData] = useState([]);
    const [existingUserValidationErrors, setExistingUserValidationErrors] = useState([]);
    const [isExistingUserExcelProcessing, setIsExistingUserExcelProcessing] = useState(false);
    const [isExistingUserUploading, setIsExistingUserUploading] = useState(false);
    const existingUserFileInputRef = useRef(null);
    const [loadingMapping, setLoadingMapping] = useState(false);
    const [areas, setAreas] = useState([]);
    const [areaCodeToIdMap, setAreaCodeToIdMap] = useState({});
    const [selectedArea, setSelectedArea] = useState('');

    // Multiple User states
    const [multipleUserAreas, setMultipleUserAreas] = useState([]);
    const [selectedMultipleArea, setSelectedMultipleArea] = useState('');
    const [rebateSchemes, setRebateSchemes] = useState([]);
    const [selectedRebateScheme, setSelectedRebateScheme] = useState('');
    const [customerTypes, setCustomerTypes] = useState([]);
    const [selectedCustomerType, setSelectedCustomerType] = useState('');
    const [multipleSearchType, setMultipleSearchType] = useState('CUSTCODE');
    const [multipleSearchInput, setMultipleSearchInput] = useState('');
    const [multipleSearchResults, setMultipleSearchResults] = useState([]);
    const [multipleSelectedCustomers, setMultipleSelectedCustomers] = useState([]);
    const [loadingMultipleSearch, setLoadingMultipleSearch] = useState(false);
    const [loadingMultipleAreas, setLoadingMultipleAreas] = useState(false);

    // MAP Scheme Modal states
    const [mapSchemeModalOpen, setMapSchemeModalOpen] = useState(false);
    const [selectedCustomerForMapping, setSelectedCustomerForMapping] = useState(null);
    const [availableRebateSchemes, setAvailableRebateSchemes] = useState([]);
    const [selectedMappingScheme, setSelectedMappingScheme] = useState('');
    const [loadingAvailableSchemes, setLoadingAvailableSchemes] = useState(false);
    const [schemeRateData, setSchemeRateData] = useState([]);
    const [loadingSchemeRateData, setLoadingSchemeRateData] = useState(false);
    const [loadingRebateSchemes, setLoadingRebateSchemes] = useState(false);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [savingMultipleCustomers, setSavingMultipleCustomers] = useState(false);
    const [searchType, setSearchType] = useState('CUSTCODE');
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [loadingCustomerTypes, setLoadingCustomerTypes] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [showCustomerSearchModal, setShowCustomerSearchModal] = useState(false);
    const [showMappingProgressDialog, setShowMappingProgressDialog] = useState(false);
    const [schemeChoice, setSchemeChoice] = useState('create');
    const [excelFile, setExcelFile] = useState(null);
    const [excelFileName, setExcelFileName] = useState('');
    const [excelDataPreview, setExcelDataPreview] = useState([]);
    const [excelValidationErrors, setExcelValidationErrors] = useState([]);
    const [isExcelProcessing, setIsExcelProcessing] = useState(false);
    const [validationProgress, setValidationProgress] = useState(0);
    const [validationStatus, setValidationStatus] = useState('');
    const [showValidationProgress, setShowValidationProgress] = useState(false);

    // Delete scheme states
    const [existingUserMode, setExistingUserMode] = useState('new-rate'); // 'new-rate' or 'delete-scheme'
    const [selectedCustomersForDelete, setSelectedCustomersForDelete] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState(0);
    const [deleteStatus, setDeleteStatus] = useState('');
    const [showDeleteProgress, setShowDeleteProgress] = useState(false);
    const [deleteResults, setDeleteResults] = useState([]);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [isExportingCustomers, setIsExportingCustomers] = useState(false);
    const [exportingCustomerId, setExportingCustomerId] = useState(null);
    const [showNewCustomerInExisting, setShowNewCustomerInExisting] = useState(false);
    const [showBulkAreaUpload, setShowBulkAreaUpload] = useState(false);
    const [showBulkRateUploadInExisting, setShowBulkRateUploadInExisting] = useState(false);
    const [areaRebateSchemeData, setAreaRebateSchemeData] = useState([]);
    const [loadingAreaRebateScheme, setLoadingAreaRebateScheme] = useState(false);

    // View Rate popup for an area's rate details
    const [viewAreaRateDialogOpen, setViewAreaRateDialogOpen] = useState(false);
    const [viewAreaRateRow, setViewAreaRateRow] = useState(null);
    const [viewAreaRateData, setViewAreaRateData] = useState([]);
    const [loadingViewAreaRate, setLoadingViewAreaRate] = useState(false);
    const [exportingAreaRateId, setExportingAreaRateId] = useState(null);
    const [isExportingAllAreaRates, setIsExportingAllAreaRates] = useState(false);
    const [areaRateSearch, setAreaRateSearch] = useState('');

    // Central API chain processing loader
    const [isApiChainProcessing, setIsApiChainProcessing] = useState(false);
    const [apiChainMessage, setApiChainMessage] = useState('');
    const fileInputRef = useRef(null);
    const [rateSchemes, setRateSchemes] = useState([]);
    const [selectedRateScheme, setSelectedRateScheme] = useState('');
    const [loadingRateSchemes, setLoadingRateSchemes] = useState(false);
    const [existingSchemeDetails, setExistingSchemeDetails] = useState([]);
    const [loadingSchemeDetails, setLoadingSchemeDetails] = useState(false);
    const [mappingProgress, setMappingProgress] = useState(0);
    const [mappingStatus, setMappingStatus] = useState('');

    // POS Master Upload states
    const [posMasterExcelFile, setPosMasterExcelFile] = useState(null);
    const [posMasterExcelFileName, setPosMasterExcelFileName] = useState('');
    const [posMasterExcelData, setPosMasterExcelData] = useState([]);
    const [posMasterValidationErrors, setPosMasterValidationErrors] = useState([]);
    const [isPosMasterProcessing, setIsPosMasterProcessing] = useState(false);
    const [posMasterSelectedCustomers, setPosMasterSelectedCustomers] = useState([]);
    const [posMasterSelectedArea, setPosMasterSelectedArea] = useState('');
    const [posMasterSelectedCustomerType, setPosMasterSelectedCustomerType] = useState('');
    const [posMasterSearchInput, setPosMasterSearchInput] = useState('');
    const [posMasterSearchType, setPosMasterSearchType] = useState('CUSTCODE');
    const [posMasterSearchResults, setPosMasterSearchResults] = useState([]);
    const [showPosMasterSearchModal, setShowPosMasterSearchModal] = useState(false);
    const [posMasterCustomerDataMap, setPosMasterCustomerDataMap] = useState({});
    const posMasterFileInputRef = useRef(null);
    const [totalCustomersToUpload, setTotalCustomersToUpload] = useState(0);
    const [currentCustomerUploadIndex, setCurrentCustomerUploadIndex] = useState(0);
    const [uploadResults, setUploadResults] = useState([]);

    // Master view specific states
    const [masterExcelFile, setMasterExcelFile] = useState(null);
    const [masterExcelFileName, setMasterExcelFileName] = useState('');
    const [masterExcelData, setMasterExcelData] = useState([]);
    const [masterValidationErrors, setMasterValidationErrors] = useState([]);
    const [masterRowErrors, setMasterRowErrors] = useState({});
    const [isMasterExcelProcessing, setIsMasterExcelProcessing] = useState(false);
    const [isMasterUploading, setIsMasterUploading] = useState(false);
    const masterFileInputRef = useRef(null);

    // Master validation states
    const [rebateSchemeCustomerData, setRebateSchemeCustomerData] = useState([]);
    const [isLoadingRebateSchemeData, setIsLoadingRebateSchemeData] = useState(false);
    const [masterUnmappedCustomers, setMasterUnmappedCustomers] = useState([]);
    const [showMasterMappingDialog, setShowMasterMappingDialog] = useState(false);

    // Search state variables
    const [customerListSearch, setCustomerListSearch] = useState('');
    // Active = ItemCount > 0 (rates exist), Inactive = ItemCount 0 (no rates yet).
    // Single checkbox: unchecked (default) shows active customers, checked shows inactive.
    const [showInactiveCustomers, setShowInactiveCustomers] = useState(false);
    const [rateTableSearch, setRateTableSearch] = useState('');

    // Global search bar state
    const [globalSearchInput, setGlobalSearchInput] = useState('');

    // Customer mapping confirmation dialog states
    const [showMappingConfirmDialog, setShowMappingConfirmDialog] = useState(false);
    const [customersToMap, setCustomersToMap] = useState([]);
    const [selectedAreaName, setSelectedAreaName] = useState('');

    // --- Self-Contained Toast State ---
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const toastTimer = useRef();


    const handleExistingUserExcelUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setExistingUserExcelFile(file);
            setExistingUserExcelFileName(file.name);
            setExistingUserExcelData([]);
            setExistingUserValidationErrors([]);
        }
    };


    const showToast = (message, severity = 'info') => {
        clearTimeout(toastTimer.current);
        setToast({ open: true, message, severity });
        toastTimer.current = setTimeout(() => {
            setToast(prev => ({ ...prev, open: false }));
        }, 3000);
    };

    const handleCloseToast = () => {
        clearTimeout(toastTimer.current);
        setToast(prev => ({ ...prev, open: false }));
    };

    const normalizeAreaCode = (value) => String(value || '').trim().toUpperCase();

    // Download sample Excel file with template
    const downloadSampleExcel = () => {
        try {
            // Create sample data with headers and example rows
            const sampleData = [
                {
                    ItemCode: 'ITEM001',
                    QualifyQty: 100,
                    Rate: 5.50,
                    ValidityUpto: '2025-12-31T23:59:59'
                },
                {
                    ItemCode: 'ITEM001',
                    QualifyQty: 200,
                    Rate: 7.00,
                    ValidityUpto: '2025-12-31T23:59:59'
                },
                {
                    ItemCode: 'ITEM002',
                    QualifyQty: 150,
                    Rate: 6.00,
                    ValidityUpto: '2025-12-31T23:59:59'
                },
                {
                    ItemCode: 'ITEM003',
                    QualifyQty: 50,
                    Rate: 4.50,
                    ValidityUpto: '2025-12-31T23:59:59'
                }
            ];

            // Create workbook and worksheet
            const worksheet = XLSX.utils.json_to_sheet(sampleData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'RateUpload');

            // Set column widths for better visibility
            worksheet['!cols'] = [
                { wch: 15 }, // ItemCode
                { wch: 15 }, // QualifyQty
                { wch: 12 }, // Rate
                { wch: 30 }  // ValidityUpto
            ];

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `HORECA_Rate_Template_${timestamp}.xlsx`;

            // Download the file
            XLSX.writeFile(workbook, filename);
            showToast('Sample Excel file downloaded successfully!', 'success');
        } catch (error) {
            console.error('Error downloading sample Excel:', error);
            showToast('Failed to download sample Excel file.', 'error');
        }
    };

    const downloadPosMasterSampleExcel = () => {
        try {
            const fallbackAreaCodes = ['NAGPUR', 'RAIPUR', 'DURGBHIL'];
            const availableAreaCodes = areas
                .map(area => normalizeAreaCode(area.AreaCode))
                .filter(Boolean);

            const sampleAreaCodes = (availableAreaCodes.length > 0 ? availableAreaCodes : fallbackAreaCodes).slice(0, 3);
            while (sampleAreaCodes.length < 3) {
                sampleAreaCodes.push(fallbackAreaCodes[sampleAreaCodes.length] || 'NAGPUR');
            }

            const sampleData = [
                { CustomerID: 'CUST001', AreaCode: sampleAreaCodes[0], ItemCode: 'ITEM001', QualifyQty: 100, Rate: 5.50, ValidityUpto: '2025-12-31T23:59:59' },
                { CustomerID: 'CUST001', AreaCode: sampleAreaCodes[0], ItemCode: 'ITEM002', QualifyQty: 200, Rate: 7.00, ValidityUpto: '2025-12-31T23:59:59' },
                { CustomerID: 'CUST002', AreaCode: sampleAreaCodes[1], ItemCode: 'ITEM001', QualifyQty: 150, Rate: 6.00, ValidityUpto: '2025-12-31T23:59:59' },
                { CustomerID: 'CUST003', AreaCode: sampleAreaCodes[2], ItemCode: 'ITEM003', QualifyQty: 50, Rate: 4.50, ValidityUpto: '2025-12-31T23:59:59' },
            ];

            const worksheet = XLSX.utils.json_to_sheet(sampleData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'POSMasterUpload');
            worksheet['!cols'] = [
                { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 28 }
            ];

            const areaReferenceData = (areas.length > 0 ? areas : fallbackAreaCodes.map(code => ({
                AreaCode: code,
                AreaName: '',
                AreaId: ''
            }))).map(area => ({
                AreaCode: normalizeAreaCode(area.AreaCode),
                AreaName: area.AreaName || '',
                AreaId: area.AreaId || ''
            }));

            const areaReferenceSheet = XLSX.utils.json_to_sheet(areaReferenceData);
            areaReferenceSheet['!cols'] = [
                { wch: 15 },
                { wch: 28 },
                { wch: 12 }
            ];
            XLSX.utils.book_append_sheet(workbook, areaReferenceSheet, 'AreaCodeReference');

            XLSX.writeFile(workbook, `POS_Master_Template_${new Date().toISOString().split('T')[0]}.xlsx`);
            showToast('POS Master sample Excel downloaded!', 'success');
        } catch (error) {
            showToast('Failed to download sample Excel.', 'error');
        }
    };

    const tableData = schemeChoice === 'create' ? excelDataPreview : existingSchemeDetails;

    // --- Effects ---
    useEffect(() => {
        if (token) {
            fetchAreas();
            fetchCustomerTypes();
            // Set areas for existing users as well (using same area list)
            setExistingUserAreas(areas);
        } else {
            showToast('No authorization token found. Please login first.', 'error');
        }
    }, [token]);

    // Update existing user areas when main areas are loaded
    useEffect(() => {
        setExistingUserAreas(areas);
    }, [areas]);

    // Fetch customers list when switching to customers view
    useEffect(() => {
        if (detailsViewFilter === 'customers' && customerSchemeId && selectedExistingArea) {
            fetchRebateSchemeCustomers();
        }
    }, [detailsViewFilter, customerSchemeId, selectedExistingArea]);

    // Load data for Multiple User when selected
    useEffect(() => {
        if (userFilterType === 'multiple' && token) {
            fetchMultipleUserAreas();
            fetchRebateSchemes();
            fetchMultipleCustomerTypes();
        }
    }, [userFilterType, token]);

    // Load area rebate scheme summary for the Area Wise Upload tab —
    // always pulls every area (AreaId=ALL), no per-area filter.
    useEffect(() => {
        if (userFilterType === 'area-wise') {
            fetchAreaRebateScheme('ALL');
        }
    }, [userFilterType]);

    useEffect(() => {
        if (schemeChoice === 'existing' && rateSchemes.length === 0) {
            fetchRateSchemes();
        }
        setExcelDataPreview([]);
        setExcelValidationErrors([]);
        if (schemeChoice === 'create') {
            setExistingSchemeDetails([]);
            setSelectedRateScheme('');
        }
    }, [schemeChoice]);

    // Load rebate scheme customer data when Master view is accessed
    useEffect(() => {
        if (activeView === 'master' && token && rebateSchemeCustomerData.length === 0) {
            fetchRebateSchemeCustomerData();
        }
    }, [activeView, token, rebateSchemeCustomerData.length]);

    // --- API & Data Handling ---
    const handleApiError = (message) => showToast(message, 'error');

    // --- Global Search (searches within the selected area, or across all areas if none selected) ---
    const handleGlobalSearch = async () => {
        const query = globalSearchInput.trim();
        if (query && query.length < 4) {
            showToast('Please enter minimum 4 characters to search.', 'warning');
            return;
        }
        const searchText = query || 'ALL';
        const areaId = selectedExistingArea || 'ALL';
        if (!selectedExistingArea) {
            setSelectedExistingArea('ALL');
            setSelectedExistingCustomer(null);
            setCustomerDetailsData([]);
        }
        await fetchExistingCustomersByArea(areaId, searchText);
    };
    const handleGlobalSearchKeyPress = (e) => { if (e.key === 'Enter') handleGlobalSearch(); };
    const handleCloseGlobalSearch = () => {
        setGlobalSearchInput('');
        const areaId = selectedExistingArea || 'ALL';
        fetchExistingCustomersByArea(areaId, 'ALL');
    };

    const fetchAreas = async () => {
        setLoadingAreas(true);
        try {
            const response = await fetch(`${local_url}/api/SFArea/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch areas');
            const data = await response.json();
            setAreas(data.map(area => ({ AreaId: area.sfAreaId, AreaName: area.sfAreaName, AreaCode: area.areaCode })));
            const codeToIdMap = {};
            data.forEach(area => {
                const normalizedAreaCode = normalizeAreaCode(area.areaCode);
                if (normalizedAreaCode) codeToIdMap[normalizedAreaCode] = area.sfAreaId;
            });
            setAreaCodeToIdMap(codeToIdMap);
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingAreas(false);
        }
    };

    const fetchCustomerTypes = async () => {
        setLoadingCustomerTypes(true);
        try {
            const response = await fetch(`${local_url}/api/CustomerList/GetCustomerTypes/C`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch customer types');
            setCustomerTypes(await response.json());
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingCustomerTypes(false);
        }
    };

    // Fetch existing customers by area for existing user filter
    const fetchExistingCustomersByArea = async (areaId, searchText = '', options = {}) => {
        const { silent = false } = options;
        if (!areaId) return;
        setLoadingExistingCustomers(true);
        try {
            const query = (searchText || '').trim();
            const hasSearch = Boolean(query) && query.toUpperCase() !== 'ALL';
            const url = `${local_signage}/api/v1/fetchrebateschemecustomerbyarea?AreaId=${areaId}&SearchText=${encodeURIComponent(query || 'ALL')}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch existing customers');
            const data = await response.json();
            console.log('Existing customers data:', data);
            const results = Array.isArray(data) ? data : [];
            // Backend doesn't reliably filter by SearchText, so filter client-side as a safety net.
            const filtered = hasSearch
                ? results.filter(c =>
                    (c.CustomerId || '').toLowerCase().includes(query.toLowerCase()) ||
                    (c.CustomerName || '').toLowerCase().includes(query.toLowerCase()) ||
                    (c.Mobile || '').toLowerCase().includes(query.toLowerCase())
                )
                : results;
            // AreaId=ALL can return one row per scheme/area mapping for the same customer,
            // which made a customer show as both active and inactive depending on which
            // duplicate row survived the filter. Merge duplicates by CustomerId so each
            // customer appears exactly once, using the highest ItemCount to decide active status.
            const dedupedByCustomerId = Object.values(
                filtered.reduce((acc, c) => {
                    const id = c.CustomerId;
                    if (!id) {
                        acc[`__no_id_${Object.keys(acc).length}`] = c;
                        return acc;
                    }
                    const existing = acc[id];
                    if (!existing) {
                        acc[id] = c;
                    } else if ((c.ItemCount ?? 0) > (existing.ItemCount ?? 0)) {
                        acc[id] = { ...existing, ...c };
                    }
                    return acc;
                }, {})
            );
            setExistingCustomers(dedupedByCustomerId);
            if (!silent) {
                if (dedupedByCustomerId.length > 0) {
                    showToast(`Found ${dedupedByCustomerId.length} existing customers in selected area.`, 'success');
                } else {
                    showToast('No existing customers found in selected area.', 'info');
                }
            }
        } catch (error) {
            handleApiError(error.message);
            setExistingCustomers([]);
        } finally {
            setLoadingExistingCustomers(false);
        }
    };

    const fetchAreaRebateScheme = async (areaId) => {
        if (!areaId) return;
        setLoadingAreaRebateScheme(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetcharearebatescheme?AreaId=${areaId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch area rebate scheme');
            const data = await response.json();
            setAreaRebateSchemeData(Array.isArray(data) ? data : []);
        } catch (error) {
            handleApiError(error.message);
            setAreaRebateSchemeData([]);
        } finally {
            setLoadingAreaRebateScheme(false);
        }
    };

    const fetchAreaRateItems = async (areaId) => {
        const itemsResponse = await fetch(
            `${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=AREA&AreaId=${areaId}&CustomerID=ALL`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const items = itemsResponse.ok ? await itemsResponse.json() : [];
        return Array.isArray(items) ? items : [];
    };

    const handleViewAreaRateClick = async (row) => {
        setViewAreaRateRow(row);
        setViewAreaRateDialogOpen(true);
        setViewAreaRateData([]);
        setLoadingViewAreaRate(true);
        try {
            setViewAreaRateData(await fetchAreaRateItems(row.SFAreaId));
        } catch (err) {
            console.error('Error fetching area rate details:', err);
            setViewAreaRateData([]);
        } finally {
            setLoadingViewAreaRate(false);
        }
    };

    const handleCloseViewAreaRateDialog = () => {
        setViewAreaRateDialogOpen(false);
        setViewAreaRateRow(null);
        setViewAreaRateData([]);
    };

    const buildAreaRateRows = async (row) => {
        const items = await fetchAreaRateItems(row.SFAreaId);
        return (items.length > 0 ? items : [{}]).map(item => ({
            'Area ID': row.SFAreaId || '-',
            'Area Name': row.SFAreaName || '-',
            'Scheme ID': row.RebateSchemeId || '-',
            'Scheme Name': row.RebateSchemeName || '-',
            'Item Code': item.ItemCode || '-',
            'Item Name': item.ItemName || '-',
            'Qualify Qty': item.QualifyQty ?? '-',
            'Rate': item.Rate ?? '-',
            'Valid Till': formatYyyymmdd(item.validtill || item.ValidTill),
        }));
    };

    const writeAreaRateWorkbook = (rows, filename) => {
        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet['!cols'] = [
            { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 20 },
            { wch: 14 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Area Rate');
        XLSX.writeFile(workbook, filename);
    };

    const exportAreaRateToExcel = async (row) => {
        setExportingAreaRateId(row.RebateSchemeId);
        try {
            const rows = await buildAreaRateRows(row);
            writeAreaRateWorkbook(rows, `Area_${row.SFAreaId}_Rates_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (err) {
            console.error('Error exporting area rate:', err);
            showToast('Failed to export area rate data.', 'error');
        } finally {
            setExportingAreaRateId(null);
        }
    };

    const exportAllAreaRatesToExcel = async () => {
        if (areaRebateSchemeData.length === 0) {
            showToast('No area rate data to export', 'warning');
            return;
        }

        setIsExportingAllAreaRates(true);
        try {
            const rows = [];
            for (const row of areaRebateSchemeData) {
                rows.push(...(await buildAreaRateRows(row)));
            }
            writeAreaRateWorkbook(rows, `All_Area_Rates_${new Date().toISOString().split('T')[0]}.xlsx`);
            showToast(`Exported rate details for ${areaRebateSchemeData.length} area scheme(s)!`, 'success');
        } catch (err) {
            console.error('Error exporting all area rates:', err);
            showToast('Failed to export area rate data.', 'error');
        } finally {
            setIsExportingAllAreaRates(false);
        }
    };

    // Insert areawisecustomer when customer doesn't exist in rebate scheme
    const insertAreawiseCustomer = async (areaId, customerId) => {
        try {
            const payload = {
                option: "I",
                Areaid: areaId,
                CustomerId: customerId,
                PricingSchemeType: "HORECA",
                CreditType: "CR",
                CreditLimitProposed: 0.00,
                CreatedUserId: userId
            };

            const response = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to insert areawisecustomer');
            const data = await response.json();
            console.log('Customer inserted successfully:', data);
            showToast('Customer added to area-wise scheme successfully!', 'success');
            return data;
        } catch (error) {
            console.error('Error inserting areawisecustomer:', error);
            showToast('Failed to add customer to area-wise scheme: ' + error.message, 'error');
            throw error;
        }
    };

    // Fetch customer rate details. Pass `silent: true` to suppress the
    // found/not-found toasts (used for post-upload retry polling below).
    const fetchCustomerRateDetails = async (areaId, customerId, options = {}) => {
        const { silent = false } = options;
        if (!areaId || !customerId) return [];
        setLoadingCustomerDetails(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${areaId}&CustomerID=${customerId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch customer rate details');
            const data = await response.json();
            console.log('Customer rate details:', data);

            if (data.length > 0) {
                // Transform API data to match table structure
                const transformedData = data.map((item, index) => ({
                    sno: index + 1,
                    itemName: item.ItemName,
                    itemCode: item.ItemCode,
                    qualifyQty: item.QualifyQty,
                    rate: item.Rate
                }));

                setCustomerDetailsData(transformedData);
                // Get scheme ID from first item (all items should have same scheme ID)
                setCustomerSchemeId(data[0].RebateSchemeId || '');
                if (!silent) showToast(`Found ${data.length} rate details for customer.`, 'success');
            } else {
                setCustomerDetailsData([]);
                setCustomerSchemeId('');
                if (!silent) showToast('No rate details found for this customer.', 'info');
            }
            return data;
        } catch (error) {
            handleApiError(error.message);
            setCustomerDetailsData([]);
            setCustomerSchemeId('');
            return [];
        } finally {
            setLoadingCustomerDetails(false);
        }
    };

    // Fetch rebate scheme customers list
    const fetchRebateSchemeCustomers = async () => {
        if (!selectedExistingArea || !customerSchemeId) {
            console.log('Missing area or scheme ID for customer list fetch');
            return;
        }

        setLoadingCustomersList(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/getrebateschemecustomers?RebateschemeId=${customerSchemeId}&AreaId=${selectedExistingArea}&CustomerId=${selectedExistingCustomer?.CustomerId || ''}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Failed to fetch rebate scheme customers');
            const data = await response.json();
            console.log('Rebate scheme customers:', data);

            setCustomersListData(data);
            if (data.length === 0) {
                showToast('No customers found for this rebate scheme.', 'info');
            } else {
                showToast(`Found ${data.length} customers in this rebate scheme.`, 'success');
            }
        } catch (error) {
            console.error('Error fetching rebate scheme customers:', error);
            handleApiError(error.message);
            setCustomersListData([]);
        } finally {
            setLoadingCustomersList(false);
        }
    };

    // Handle customer selection for deletion
    const handleCustomerSelection = (customerId, isSelected) => {
        if (isSelected) {
            setSelectedCustomersToDelete([...selectedCustomersToDelete, customerId]);
        } else {
            setSelectedCustomersToDelete(selectedCustomersToDelete.filter(id => id !== customerId));
        }
    };

    // Handle select all customers
    const handleSelectAllCustomers = (isSelected) => {
        if (isSelected) {
            setSelectedCustomersToDelete(customersListData.map(customer => customer.CustomerId));
        } else {
            setSelectedCustomersToDelete([]);
        }
    };

    // Delete selected customers from rebate scheme

    const handleCustomerSearch = async () => {
        if (!selectedCustomerType || !searchInput.trim() || !selectedArea) {
            showToast('Please select Area, Customer Type, and enter a search term.', 'warning');
            return;
        }
        setLoadingSearch(true);
        try {
            const response = await fetch(`${local_url}/api/POSCustomerSerch/Get/${selectedCustomerType}/${searchType}/${searchInput}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error(`Search failed: ${response.statusText}`);
            const data = await response.json();
            let results = Array.isArray(data.customers) ? data.customers : [];

            results = results.map(customer => {
                if (customer.custID === "L102 1539") {
                    return { ...customer, schemeId: 'PRE-MAPPED-SCHEME-01' };
                }
                return customer;
            });

            setSearchResults(results);
            setSelectedCustomers([]); // Start each new search with a clean selection
            setShowNewCustomerInExisting(false); // Close the search-criteria popup now that results are shown
            setShowCustomerSearchModal(true); // Always show modal on new search
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingSearch(false);
        }
    };

    const processExcelForNewScheme = async () => {
        if (!excelFile) return;
        setIsExcelProcessing(true);
        setShowValidationProgress(true);
        setValidationProgress(0);
        setValidationStatus('Reading Excel file...');

        const totalCustomers = selectedCustomers.length;
        const progressStep = totalCustomers > 0 ? 100 / totalCustomers : 100;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                setValidationProgress(10);
                setValidationStatus('Validating Excel structure...');
                await new Promise(resolve => setTimeout(resolve, 300));

                if (jsonData.length < 2) throw new Error('No data found in Excel file.');
                const headers = jsonData[0].map(h => String(h).trim());
                const requiredHeaders = ['ItemCode', 'Rate', 'QualifyQty'];
                if (requiredHeaders.some(h => !headers.includes(h))) throw new Error('Missing required columns: ItemCode, Rate, QualifyQty.');

                // Check if ValidityUpto column exists (optional but recommended)
                const hasValidityUpto = headers.includes('ValidityUpto');

                setValidationProgress(20);
                setValidationStatus('Processing Excel data...');
                await new Promise(resolve => setTimeout(resolve, 300));

                const processedData = [], newErrors = [];
                let hasPastDateError = false;

                // Filter out empty rows before processing and keep track of original row numbers
                const dataRowsWithOriginalIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    // Check if row has any meaningful data (not all empty/null/undefined)
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithOriginalIndex.push({ row, originalRowNumber: originalIndex + 2 }); // +2 for header and 1-based indexing
                    }
                });

                const totalRows = dataRowsWithOriginalIndex.length;

                // Warm the server-date cache once before validating rows, so
                // ValidityUpto is checked against the server's date, not the browser's.
                await getServerTodayStr();

                // Process rows with progressive updates
                for (let i = 0; i < totalRows; i++) {
                    const { row, originalRowNumber } = dataRowsWithOriginalIndex[i];
                    let hasRowError = false;
                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();
                    const qty = Number(row[headers.indexOf('QualifyQty')]);
                    const rate = Number(row[headers.indexOf('Rate')]);
                    const validityUptoCellValue = hasValidityUpto ? String(row[headers.indexOf('ValidityUpto')] || '').trim() : '';

                    // Validation logic
                    if (!itemCode) { newErrors.push(`Row ${originalRowNumber}: ItemCode missing.`); hasRowError = true; }
                    if (isNaN(qty) || qty < 0) { newErrors.push(`Row ${originalRowNumber}: Invalid Quantity - cannot be negative.`); hasRowError = true; }
                    if (isNaN(rate) || rate < 0) { newErrors.push(`Row ${originalRowNumber}: Invalid Rate - cannot be negative.`); hasRowError = true; }

                    // Validate ValidityUpto if provided - should be ISO date format
                    if (validityUptoCellValue && hasValidityUpto) {
                        const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
                        if (!dateRegex.test(validityUptoCellValue)) {
                            newErrors.push(`Row ${originalRowNumber}: Invalid ValidityUpto format. Use YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS format.`);
                            hasRowError = true;
                        } else if (isValidityUptoBeforeToday(validityUptoCellValue)) {
                            hasPastDateError = true;
                            hasRowError = true;
                        }
                    }

                    // Business rule validation
                    if (!hasRowError) {
                        if (qty === 0 && rate > 0) { newErrors.push(`Row ${originalRowNumber}: QualifyQty cannot be 0 when Rate is greater than 0.`); hasRowError = true; }
                        if (rate === 0 && qty > 0) { newErrors.push(`Row ${originalRowNumber}: Rate cannot be 0 when QualifyQty is greater than 0.`); hasRowError = true; }
                    }

                    if (!hasRowError) {
                        const itemData = { ItemCode: itemCode, QualifyQty: qty, Rate: parseFloat(rate.toFixed(2)) };
                        // Add ValidityUpto if provided
                        if (validityUptoCellValue && hasValidityUpto) {
                            itemData.ValidityUpto = validityUptoCellValue;
                        }
                        processedData.push(itemData);
                    }

                    // Update progress based on row processing + customer count calculation
                    const rowProgress = 20 + (60 * (i + 1) / totalRows); // 20-80% for row processing
                    setValidationProgress(rowProgress);
                    setValidationStatus(`Processing row ${i + 1} of ${totalRows}...`);

                    // Small delay every 10 rows for visual effect
                    if ((i + 1) % 10 === 0) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                // Final validation step based on customers
                if (totalCustomers > 0) {
                    setValidationStatus(`Calculating upload requirements for ${totalCustomers} customers...`);
                    for (let customerIndex = 0; customerIndex < totalCustomers; customerIndex++) {
                        const customer = selectedCustomers[customerIndex];
                        const customerName = customer.customerName || customer.CustomerName || 'Unknown';
                        setValidationStatus(`Validating for ${customerName}...`);
                        setValidationProgress(80 + (20 * (customerIndex + 1) / totalCustomers));
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                } else {
                    setValidationProgress(100);
                    setValidationStatus('Validation complete!');
                }

                // Set final data
                setExcelDataPreview(processedData);
                setExcelValidationErrors(newErrors);

                // Show completion message
                if (hasPastDateError) {
                    setValidationStatus('Validation failed');
                    showToast("ValidityUpto cannot be less than today's date.", 'warning');
                } else if (newErrors.length > 0) {
                    setValidationStatus(`Validation completed with ${newErrors.length} errors`);
                    showToast(`Validation failed with ${newErrors.length} errors.`, 'warning');
                } else {
                    setValidationStatus('Excel validated successfully!');
                    showToast(`Excel validated successfully! Ready to upload to ${totalCustomers || 1} customer(s).`, 'success');
                }

                // Close dialog after delay
                setTimeout(() => {
                    setShowValidationProgress(false);
                    setValidationProgress(0);
                }, 1500);

            } catch (err) {
                setValidationStatus('Validation failed');
                handleApiError('Error parsing Excel: ' + err.message);
                setTimeout(() => {
                    setShowValidationProgress(false);
                    setValidationProgress(0);
                }, 1500);
            } finally {
                setIsExcelProcessing(false);
            }
        };
        reader.readAsArrayBuffer(excelFile);
    };

    // The CustomerRate API reads the expiry date from a "validTill" field
    // formatted as YYYYMMDD — sending it under any other name/format (e.g.
    // "validityUpto") is silently ignored and the row is saved with a
    // 1900-01-01 default, which then reads as already-expired.
    const toValidTill = (value) => {
        if (!value) return '20991231';
        const datePart = String(value).split('T')[0];
        return datePart.replace(/-/g, '') || '20991231';
    };

    const buildDetailsFromRows = (rows) => rows.map(row => {
        const detail = {
            itemCode: row.ItemCode || row['Item Code'] || row['ItemId'] || row['Item ID'] || row['Product Code'] || row['SKU'] || '',
            qualifyQty: parseFloat(row.QualifyQty || row.QualifyQuantity || row['Qualify Qty'] || row['Quantity'] || row['Qty'] || 0) || 0,
            rate: parseFloat(row.Rate || row['Price'] || row['Unit Price'] || row['Rate Per Unit'] || 0) || 0,
            validTill: toValidTill(row.ValidityUpto),
            dml: 'I'
        };
        return detail;
    });

    const performUpload = async (rebateSchemeType, isAreaWise = false, overrideCustomers = null, overrideExcelData = null, overrideCustomerDataMap = null, isPOSMaster = false) => {
        const customersToUpload = overrideCustomers || selectedCustomers;
        const excelDataToUse = overrideExcelData || excelDataPreview;

        setLoadingMapping(true);
        setShowMappingProgressDialog(true);
        setUploadResults([]);
        const totalCustomers = customersToUpload.length;
        setTotalCustomersToUpload(totalCustomers);
        setCurrentCustomerUploadIndex(0);

        const results = [];

        try {
            // Prepare common details only when not using per-customer map
            const commonDetails = overrideCustomerDataMap ? null : buildDetailsFromRows(excelDataToUse);

            if (!overrideCustomerDataMap && (!commonDetails || commonDetails.length === 0)) {
                throw new Error('No valid items found in Excel file. Please check the data format.');
            }

            // Upload for each selected customer
            for (let i = 0; i < customersToUpload.length; i++) {
                const customer = customersToUpload[i];
                const customerName = customer.customerName || customer.CustomerName || 'Unknown Customer';
                const customerId = customer.custID || customer.CustCode || customer.CustomerId || customer.custCode;

                const details = overrideCustomerDataMap
                    ? buildDetailsFromRows(overrideCustomerDataMap[customerId] || [])
                    : commonDetails;

                if (details.length === 0) continue;

                setCurrentCustomerUploadIndex(i);
                setMappingStatus(`Uploading for ${customerName}...`);
                setMappingProgress(0);

                try {
                    const nowIST = (() => {
                        const now = new Date();
                        const istOffset = 5.5 * 60 * 60 * 1000;
                        return new Date(now.getTime() + istOffset).toISOString();
                    })();

                    const requestBody = isPOSMaster ? {
                        uniqueId: "",
                        rebateSchemeType: "CUST",
                        areaId: customer.sfAreaId || null,
                        rebateSchemeId: "",
                        customerId: customerId,
                        dateOfEntry: nowIST,
                        createdUserCode: userId,
                        ratedetail: (overrideCustomerDataMap ? (overrideCustomerDataMap[customerId] || []) : details).map(row => {
                            let validTill = '20991231';
                            if (row.ValidityUpto) {
                                const datePart = String(row.ValidityUpto).split('T')[0];
                                validTill = datePart.replace(/-/g, '');
                            }
                            return {
                                itemCode: row.ItemCode || row.itemCode || '',
                                qualifyQty: parseFloat(row.QualifyQty || row.qualifyQty || 0) || 0,
                                rate: parseFloat(row.Rate || row.rate || 0) || 0,
                                validTill,
                                dml: 'I'
                            };
                        })
                    } : {
                        uniqueId: "",
                        rebateSchemeType: 'CUST',
                        areaId: isAreaWise ? selectedArea : (overrideCustomerDataMap ? (customer.sfAreaId || null) : null),
                        rebateSchemeId: '',
                        customerId: customerId,
                        dateOfEntry: nowIST,
                        createdUserCode: userId,
                        ratedetail: details
                    };

                    console.log(`Uploading for customer ${i + 1}/${totalCustomers}:`, { customerName, customerId, requestBody });

                    setMappingProgress(30);
                    setMappingStatus(`Sending data for ${customerName}...`);

                    // Log POST API request details
                    const apiUrl = `${local_url}/api/CustomerRate`;
                    const apiPayload = JSON.stringify(requestBody);

                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log(`📤 POST API REQUEST - ${new Date().toISOString()}`);
                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log('URL:', apiUrl);
                    console.log('Method:', 'POST');
                    console.log('Headers:', {
                        'Content-Type': 'application/json',
                        'Authorization': '***Bearer Token***'
                    });
                    console.log('Request Body:', JSON.parse(apiPayload));
                    const logRateItems = requestBody.ratedetail || requestBody.rateDetail || [];
                    console.log('Total Items in Payload:', logRateItems.length);
                    console.log('Items Details:', logRateItems.map((item, idx) => ({
                        index: idx + 1,
                        itemCode: item.itemCode,
                        qualifyQty: item.qualifyQty,
                        rate: item.rate,
                        validityUpto: item.validityUpto || 'N/A',
                        dml: item.dml
                    })));
                    console.log('═══════════════════════════════════════════════════════════════');

                    const response = await fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: apiPayload
                    });

                    setMappingProgress(70);
                    setMappingStatus(`Processing response for ${customerName}...`);

                    if (!response.ok) {
                        const errorData = await response.text();
                        console.error('❌ API ERROR RESPONSE:', {
                            status: response.status,
                            statusText: response.statusText,
                            errorData: errorData
                        });
                        throw new Error(`API Error (${response.status}): ${errorData}`);
                    }

                    const result = await response.json();
                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log(`✅ POST API RESPONSE - ${new Date().toISOString()}`);
                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log('Response Status:', response.status, response.statusText);
                    console.log('Response Data:', result);
                    console.log('Response type:', typeof result);
                    console.log('Response keys:', Object.keys(result || {}));
                    console.log('Description value:', result?.description);
                    console.log('═══════════════════════════════════════════════════════════════');

                    setMappingProgress(100);

                    if (result?.result === 0) {
                        const errMsg = result?.description
                            ? result.description.replace(/^Fail/, '').trim()
                            : 'Upload failed';
                        throw new Error(errMsg);
                    }

                    setMappingStatus(`Completed processing for ${customerName}!`);

                    results.push({
                        customerName: customerName,
                        success: true,
                        result: result
                    });

                    setUploadResults([...results]);

                    // Small delay between uploads
                    if (i < customersToUpload.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }

                } catch (err) {
                    console.error(`Upload error for customer ${customerName}:`, err);
                    results.push({
                        customerName: customerName,
                        success: false,
                        error: err.message
                    });
                    setUploadResults([...results]);
                }
            }

            setCurrentCustomerUploadIndex(totalCustomers);

            // Show final summary
            const successCount = results.filter(r => r.success).length;
            const failureCount = results.filter(r => !r.success).length;

            if (successCount === totalCustomers) {
                setMappingStatus('All uploads completed successfully!');
                showToast(`Successfully uploaded scheme for all ${totalCustomers} customers!`, 'success');

                // Refresh the customer table immediately so the newly mapped
                // customer shows up right away, without waiting for the dialog to auto-close.
                if (selectedExistingArea) {
                    setShowNewCustomerInExisting(false);
                    fetchExistingCustomersByArea(selectedExistingArea);
                }

                // Auto-close only when all succeed
                setTimeout(() => {
                    resetAllState();
                    setShowMappingProgressDialog(false);
                    setMappingProgress(0);
                    setUploadResults([]);
                    setTotalCustomersToUpload(0);
                    setCurrentCustomerUploadIndex(0);
                }, 3000);
            } else if (successCount > 0) {
                setMappingStatus(`${successCount} succeeded, ${failureCount} failed — close this dialog to continue`);
                showToast(`${successCount} successful, ${failureCount} failed uploads`, 'warning');
            } else {
                setMappingStatus('All uploads failed — review errors below and close when done');
                showToast('All uploads failed. Please check the errors.', 'error');
            }

        } catch (err) {
            console.error('Upload process error:', err);
            handleApiError(`Upload process error: ${err.message}`);
            setMappingStatus('Upload process failed');
            setMappingProgress(0);
        } finally {
            setLoadingMapping(false);
        }
    };

    // --- Other handlers and utility functions ---
    const handleKeyPress = (event) => { if (event.key === 'Enter') handleCustomerSearch(); };
    const getSearchInputLabel = () => ({ 'CUSTCODE': 'Customer Code', 'MOBILE': 'Mobile Number', 'CUSTNAME': 'Customer Name' })[searchType] || 'Search Term';
    const handleFileChange = (event) => { const file = event.target.files[0]; if (file) { setExcelFile(file); setExcelFileName(file.name); setExcelDataPreview([]); setExcelValidationErrors([]); } };

    // Multiple User handlers
    const handleMultipleCustomerSearch = async () => {
        if (!selectedCustomerType || !multipleSearchInput.trim()) {
            handleApiError('Please select customer type and enter search term');
            return;
        }

        setLoadingMultipleSearch(true);
        try {
            const response = await fetch(
                `${local_url}/api/POSCustomerSerch/Get/${selectedCustomerType}/${multipleSearchType}/${encodeURIComponent(multipleSearchInput)}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (!response.ok) throw new Error('Search failed');
            const data = await response.json();

            // Handle the API response structure
            if (data.customers && Array.isArray(data.customers)) {
                setMultipleSearchResults(data.customers);
            } else if (Array.isArray(data)) {
                setMultipleSearchResults(data);
            } else {
                setMultipleSearchResults([data]);
            }
        } catch (error) {
            handleApiError('Search failed. Please try again.');
            console.error('Error searching customers:', error);
            setMultipleSearchResults([]);
        } finally {
            setLoadingMultipleSearch(false);
        }
    };

    const handleMultipleCustomerSelect = async (customer) => {
        const customerId = customer.custID || customer.customerCode;
        const isSelected = multipleSelectedCustomers.some(c => (c.custID || c.customerCode) === customerId);

        if (isSelected) {
            // Remove customer from selection
            setMultipleSelectedCustomers(prev => prev.filter(c => (c.custID || c.customerCode) !== customerId));
        } else {
            // Add customer to selection and check mapping status
            if (!selectedMultipleArea) {
                handleApiError('Please select an area first');
                return;
            }

            showToast(`Checking mapping status for ${customer.customerName}...`, 'info');

            try {
                // Call fetchrebateschemerates API to check if customer is already mapped
                const response = await fetch(`${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${selectedMultipleArea}&CustomerID=${customerId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    handleApiError('Failed to check customer mapping status');
                    return;
                }

                const data = await response.json();

                // Add mapping status to customer object
                const customerWithStatus = {
                    ...customer,
                    mappingStatus: data.length > 0 ? 'Mapped' : 'Not Mapped',
                    needsMapping: data.length === 0
                };

                if (data.length === 0) {
                    // Customer not mapped, show confirmation
                    const shouldMap = window.confirm(
                        `Customer "${customer.customerName}" is not mapped to the selected area and scheme. Do you want to map this customer to the area?`
                    );

                    if (shouldMap) {
                        // Just add customer to selection - will be mapped when "Save Selected" is clicked
                        customerWithStatus.mappingStatus = 'Ready for mapping';
                        customerWithStatus.needsMapping = false;

                        /*
                        // Removed individual API call - will be called on "Save Selected"
                        try {
                            showToast(`Mapping ${customer.customerName} to selected area...`, 'info');
                            
                            const mapResponse = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                    AreaId: selectedMultipleArea,
                                    CustomerID: customerId,
                                    CustomerName: customer.customerName,
                                    Mobile: customer.mobile,
                                    Address: customer.address || '',
                                    CreditType: "CR",
                                    CreditLimitProposed: 0.00,
                                    CreatedUserId: userId
                                })
                                
                            });

                            if (!mapResponse.ok) {
                                throw new Error('Failed to map customer');
                            }

                            customerWithStatus.mappingStatus = 'Mapped';
                            customerWithStatus.needsMapping = false;
                            showToast(`${customer.customerName} successfully mapped to area!`, 'success');
                        */
                    } else {
                        showToast('Customer selection cancelled', 'info');
                        return;
                    }
                }

                setMultipleSelectedCustomers(prev => [...prev, customerWithStatus]);

                if (data.length > 0) {
                    showToast(`${customer.customerName} is already mapped to the area`, 'success');
                } else {
                    showToast(`${customer.customerName} added to selection and mapped successfully`, 'success');
                }

            } catch (error) {
                console.error('Error checking customer mapping:', error);
                handleApiError('Failed to check customer mapping status');
            }
        }
    };

    const handleMapCustomer = async (customer) => {
        if (!selectedMultipleArea) {
            handleApiError('Please select an area first');
            return;
        }

        const customerId = customer.custID || customer.customerCode;
        showToast(`Mapping ${customer.customerName} to selected area...`, 'info');

        // Note: insertareawisecustomer API call removed - will be called on "Save Selected"
        /*
        try {
            const response = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    AreaId: selectedMultipleArea,
                    CustomerID: customerId,
                    CustomerName: customer.customerName,
                    Mobile: customer.mobile,
                    Address: customer.address || '',
                    CreditType: "CR",
                    CreditLimitProposed: 0.00,
                    CreatedUserId: userId
                })
            });

            if (!response.ok) {
                throw new Error('Failed to map customer');
            }

            const data = await response.json();
        */

        // Update customer status to show it's ready for batch processing
        setMultipleSelectedCustomers(prev =>
            prev.map(c => {
                const cId = c.custID || c.customerCode;
                if (cId === customerId) {
                    return {
                        ...c,
                        mappingStatus: 'Ready for mapping',
                        needsMapping: false
                    };
                }
                return c;
            })
        );
    };

    const handleSaveMultipleCustomers = async () => {
        if (multipleSelectedCustomers.length === 0) {
            handleApiError('Please select at least one customer');
            return;
        }

        if (!selectedRebateScheme) {
            handleApiError('Please select a rebate scheme');
            return;
        }

        // Show enhanced confirmation toast
        const customerNames = multipleSelectedCustomers.slice(0, 3).map(c => c.customerName).join(', ');
        const moreText = multipleSelectedCustomers.length > 3 ? ` and ${multipleSelectedCustomers.length - 3} more` : '';

        showToast(
            `🎯 Ready to map ${multipleSelectedCustomers.length} customers (${customerNames}${moreText}) to the selected rebate scheme. Click OK to proceed!`,
            'warning'
        );

        const confirmed = window.confirm(
            `🎯 CONFIRM MAPPING\n\nYou are about to map ${multipleSelectedCustomers.length} selected customers:\n• ${customerNames}${moreText}\n\nTo the selected Rebate Scheme.\n\nThis action will update their pricing. Continue?`
        );

        if (!confirmed) return;

        setSavingMultipleCustomers(true);
        const results = [];
        const totalCustomers = multipleSelectedCustomers.length;

        try {
            showToast('Starting customer mapping process...', 'info');

            for (let i = 0; i < multipleSelectedCustomers.length; i++) {
                const customer = multipleSelectedCustomers[i];
                const customerId = customer.custID || customer.customerCode;

                try {
                    showToast(`Processing ${customer.customerName} (${i + 1}/${totalCustomers})...`, 'info');

                    // First, call insertareawisecustomer API with new format
                    const areaCustomerBody = {
                        option: "I",
                        Areaid: selectedMultipleArea,
                        CustomerId: customerId,
                        PricingSchemeType: "HORECA",
                        CreditType: "CS",
                        CreditLimitProposed: 75000.00,
                        CreatedUserId: userId
                    };

                    const areaResponse = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(areaCustomerBody)
                    });

                    let skipToSecondApi = false;

                    if (!areaResponse.ok) {
                        // Check if it's a 500 error indicating duplicate/already mapped customer
                        if (areaResponse.status === 500) {
                            try {
                                const errorData = await areaResponse.json();
                                if (errorData.details && errorData.details.includes('PRIMARY KEY constraint') &&
                                    errorData.details.includes('SFAreaCustomerRelationTable')) {
                                    // Customer is already mapped to area, skip to second API
                                    skipToSecondApi = true;
                                    showToast(`${customer.customerName} is already mapped to area, proceeding with rebate scheme mapping...`, 'info');
                                }
                            } catch (jsonError) {
                                // If we can't parse the error response, treat as regular error
                                throw new Error(`Failed to add customer to area: ${areaResponse.statusText}`);
                            }
                        }

                        if (!skipToSecondApi) {
                            throw new Error(`Failed to add customer to area: ${areaResponse.statusText}`);
                        }
                    }

                    // Then, map customer to rebate scheme using the correct API
                    const requestBody = {
                        AreaId: selectedMultipleArea,
                        CustomerId: customerId,
                        RebateSchemeId: selectedRebateScheme,
                        AddOnDiscountPc: 0,
                        DayCreditAllowed: true
                    };

                    const response = await fetch(`${local_signage}/api/v1/insertcustomerrebatescheme`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(requestBody)
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to map customer: ${response.statusText}`);
                    }

                    const result = await response.json();

                    results.push({ customer, status: 'success', result });

                    // Small delay between requests
                    if (i < multipleSelectedCustomers.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 300));
                    }

                } catch (error) {
                    console.error(`Error mapping customer ${customer.customerName}:`, error);
                    results.push({ customer, status: 'error', error: error.message });
                }
            }

            // Process results
            const successCount = results.filter(r => r.status === 'success').length;
            const errorCount = results.filter(r => r.status === 'error').length;

            if (successCount === totalCustomers) {
                showToast(`Successfully mapped all ${successCount} customers to the rebate scheme!`, 'success');
                setMultipleSelectedCustomers([]);
            } else if (successCount > 0) {
                showToast(`Mapped ${successCount} customers successfully, ${errorCount} failed`, 'warning');
            } else {
                showToast('Failed to map any customers. Please check and try again.', 'error');
            }

        } catch (error) {
            console.error('Error in save process:', error);
            showToast('Failed to complete customer mapping process. Please try again.', 'error');
        } finally {
            setSavingMultipleCustomers(false);
        }
    };

    // Multiple User fetch functions
    const fetchMultipleUserAreas = async () => {
        setLoadingMultipleAreas(true);
        try {
            const response = await fetch(`${local_url}/api/SFArea/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch areas');
            const data = await response.json();
            setMultipleUserAreas(data.map(area => ({ AreaId: area.sfAreaId, AreaName: area.sfAreaName })));
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingMultipleAreas(false);
        }
    };

    const fetchRebateSchemes = async (areaId = selectedMultipleArea) => {
        if (!areaId) {
            setRebateSchemes([]);
            return;
        }
        setLoadingRebateSchemes(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebatescheme?RebateSchemeType=CUST&AreaId=${areaId}&CustomerID=ALL`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) throw new Error('Failed to fetch rebate schemes');
            const data = await response.json();
            setRebateSchemes(data);
        } catch (error) {
            handleApiError('Failed to load rebate schemes');
            console.error('Error fetching rebate schemes:', error);
        } finally {
            setLoadingRebateSchemes(false);
        }
    };

    const fetchMultipleCustomerTypes = async () => {
        if (loadingCustomerTypes) return; // Avoid duplicate calls
        setLoadingCustomerTypes(true);
        try {
            const response = await fetch(`${local_url}/api/CustomerList/GetCustomerTypes/C`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) throw new Error('Failed to fetch customer types');
            const data = await response.json();
            setCustomerTypes(data);
        } catch (error) {
            handleApiError('Failed to load customer types');
            console.error('Error fetching customer types:', error);
        } finally {
            setLoadingCustomerTypes(false);
        }
    };

    const fetchRateSchemes = async () => {
        setLoadingRateSchemes(true);
        try {
            const dummySchemes = [
                { SchemeId: 'REGULAR-SCHEME-A', SchemeName: 'Regular Scheme A' },
                { SchemeId: 'PRE-MAPPED-SCHEME-01', SchemeName: 'Pre-Mapped Customer Scheme' },
                { SchemeId: 'FESTIVAL-OFFER', SchemeName: 'Festival Offer' },
            ];
            setRateSchemes(dummySchemes);
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingRateSchemes(false);
        }
    };

    const handleSelectExistingScheme = async (schemeId) => {
        setSelectedRateScheme(schemeId);
        if (!schemeId) {
            setExistingSchemeDetails([]);
            return;
        }
        setLoadingSchemeDetails(true);
        try {
            const dummyDetails = [
                { ItemCode: 'ITEM-001', QualifyQty: 10, Rate: 95.50 },
                { ItemCode: 'ITEM-002', QualifyQty: 5, Rate: 150.00 },
            ];
            await new Promise(resolve => setTimeout(resolve, 500));
            setExistingSchemeDetails(dummyDetails);
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingSchemeDetails(false);
        }
    };

    const handleCustomerSelect = (customer, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const id = customer.custID || customer.customerCode;
        const isCurrentlySelected = selectedCustomers.some(c => (c.custID || c.customerCode) === id);

        // Multiple selection - add or remove from selection
        if (isCurrentlySelected) {
            setSelectedCustomers(prev => prev.filter(c => (c.custID || c.customerCode) !== id));
        } else {
            setSelectedCustomers(prev => [...prev, customer]);
        }
    };

    // Check customer rebate scheme status and collect customers that need mapping
    const checkCustomersForMapping = async (customers) => {
        const customersNeedingMapping = [];
        const results = [];

        for (const customer of customers) {
            const customerId = customer.custID || customer.CustCode || customer.CustomerId || customer.custCode;
            if (!customerId || !selectedArea) {
                results.push({ customer, status: 'skipped', reason: 'Missing customer ID or area' });
                continue;
            }

            try {
                showToast(`Checking rebate scheme for ${customer.customerName}...`, 'info');

                // Check if customer exists in rebate scheme
                const response = await fetch(`${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${selectedArea}&CustomerID=${customerId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    results.push({ customer, status: 'error', reason: 'Failed to check rebate scheme' });
                    continue;
                }

                const data = await response.json();

                if (data.length === 0) {
                    // Customer doesn't exist, needs mapping
                    customersNeedingMapping.push(customer);
                } else {
                    // Customer already exists
                    results.push({ customer, status: 'exists', reason: 'Already in rebate scheme' });
                }
            } catch (error) {
                results.push({ customer, status: 'error', reason: error.message });
            }
        }

        return { customersNeedingMapping, results };
    };

    // Actually insert customers after confirmation
    const insertConfirmedCustomers = async (customers) => {
        const results = [];

        for (const customer of customers) {
            const customerId = customer.custID || customer.CustCode || customer.CustomerId || customer.custCode;

            try {
                showToast(`Adding ${customer.customerName} to rebate scheme...`, 'info');
                await insertAreawiseCustomer(selectedArea, customerId);
                results.push({ customer, status: 'inserted', reason: 'Successfully added to rebate scheme' });
            } catch (insertError) {
                results.push({ customer, status: 'insert_failed', reason: insertError.message });
            }
        }

        return results;
    };

    const handleConfirmCustomerSelection = async () => {
        if (selectedCustomers.length === 0) {
            showToast('Please select a customer.', 'warning');
            return;
        }

        // Check customers and collect those that need mapping
        showToast('Checking customer rebate scheme status...', 'info');
        const { customersNeedingMapping, results } = await checkCustomersForMapping(selectedCustomers);

        // Show immediate results for customers that already exist
        const existing = results.filter(r => r.status === 'exists').length;
        const failed = results.filter(r => r.status === 'error' || r.status === 'skipped').length;

        if (existing > 0) {
            showToast(`${existing} customer(s) already in rebate scheme.`, 'info');
        }
        if (failed > 0) {
            showToast(`${failed} customer(s) failed to check or had issues.`, 'warning');
        }

        // If there are customers that need mapping, show confirmation dialog
        if (customersNeedingMapping.length > 0) {
            // Get the area name for the selected area
            const selectedAreaData = areas.find(area => area.AreaId === selectedArea);
            const areaName = selectedAreaData ? selectedAreaData.AreaName : selectedArea;

            setCustomersToMap(customersNeedingMapping);
            setSelectedAreaName(areaName);
            setShowMappingConfirmDialog(true);
            return; // Don't proceed with the rest until user confirms
        }

        // If no customers need mapping, proceed directly
        proceedWithSchemeSelection();
    };

    const handleConfirmMapping = async () => {
        setShowMappingConfirmDialog(false);

        const insertResults = await insertConfirmedCustomers(customersToMap);

        const inserted = insertResults.filter(r => r.status === 'inserted').length;
        const insertFailed = insertResults.filter(r => r.status === 'insert_failed').length;

        let summaryMessage = '';
        if (inserted > 0) summaryMessage += `${inserted} customer(s) successfully added to rebate scheme. `;
        if (insertFailed > 0) summaryMessage += `${insertFailed} customer(s) failed to add. `;

        if (summaryMessage) {
            showToast(summaryMessage.trim(), insertFailed > 0 ? 'warning' : 'success');
        }

        // NEW: refresh the customer table immediately so newly mapped
        // customers show up right away in the active/inactive list.
        if (inserted > 0 && selectedArea) {
            fetchExistingCustomersByArea(selectedArea);
        }

        setCustomersToMap([]);
        setSelectedAreaName('');
        proceedWithSchemeSelection();
    };

    const handleCancelMapping = () => {
        setShowMappingConfirmDialog(false);
        setCustomersToMap([]);
        setSelectedAreaName('');
        // Don't proceed with scheme selection if user cancels
    };

    const proceedWithSchemeSelection = () => {
        // Check if any customer has a pre-mapped scheme
        const customersWithSchemes = selectedCustomers.filter(c => c.schemeId);
        const customersWithoutSchemes = selectedCustomers.filter(c => !c.schemeId);

        if (customersWithSchemes.length > 0) {
            // If some customers have schemes, use the first one found
            const firstMappedCustomer = customersWithSchemes[0];
            const schemeId = firstMappedCustomer.schemeId;

            if (customersWithoutSchemes.length > 0) {
                showToast(`${customersWithSchemes.length} customer(s) already mapped to schemes, ${customersWithoutSchemes.length} without schemes. Using existing scheme: ${schemeId}`, 'info');
            } else {
                showToast(`All selected customers are mapped to existing schemes. Using scheme: ${schemeId}`, 'info');
            }

            setSchemeChoice('existing');
            fetchRateSchemes().then(() => {
                setSelectedRateScheme(schemeId);
                handleSelectExistingScheme(schemeId);
            });
        } else {
            // No customers have pre-mapped schemes, default to create new
            showToast(`Customer selected. Ready to create new scheme.`, 'success');
            setSchemeChoice('create');
        }

        setShowCustomerSearchModal(false);
    };

    const handleUploadNewScheme = async () => {
        if (excelValidationErrors.length > 0 || excelDataPreview.length === 0) {
            showToast('Please resolve errors or provide valid data.', 'error');
            return;
        }

        // Validate that either area or customer is selected
        if (!selectedArea && selectedCustomers.length === 0) {
            showToast('Please select either an area or a customer before uploading.', 'error');
            return;
        }

        // Additional validation for Excel data structure
        const hasValidData = excelDataPreview.some(row => {
            const itemCode = row.ItemCode || row['Item Code'] || row['ItemId'] || row['Item ID'] || row['Product Code'] || row['SKU'];
            const qualifyQty = row.QualifyQty || row.QualifyQuantity || row['Qualify Qty'] || row['QualifyingQty'] || row['Qualifying Qty'] || row['Quantity'] || row['Qty'];
            const rate = row.Rate || row['Price'] || row['Unit Price'] || row['Rate Per Unit'] || row['Rebate Rate'] || row['Discount Rate'];

            return itemCode && (qualifyQty !== undefined && qualifyQty !== null && qualifyQty !== '') && (rate !== undefined && rate !== null && rate !== '');
        });

        if (!hasValidData) {
            showToast('Excel file must contain ItemCode, Rate, and QualifyQty columns with valid data.', 'error');
            return;
        }

        // Determine scheme type based on whether area is selected or customers are selected
        const rebateSchemeType = selectedArea ? "AREA" : "CUSTOMER";
        const isAreaWise = selectedArea ? true : false;

        await performUpload(rebateSchemeType, isAreaWise);
    };

    const handleUploadExistingScheme = async () => {
        if (!selectedRateScheme || existingSchemeDetails.length === 0) {
            showToast('Please select a valid scheme.', 'error');
            return;
        }

        // Validate that either area or customer is selected
        if (!selectedArea && selectedCustomers.length === 0) {
            showToast('Please select either an area or a customer before uploading.', 'error');
            return;
        }

        // Validate Excel data is available
        if (!excelDataPreview || excelDataPreview.length === 0) {
            showToast('Please upload and validate Excel data before proceeding.', 'error');
            return;
        }

        // Check for validation errors
        if (excelValidationErrors.length > 0) {
            showToast('Please resolve all validation errors in Excel data before uploading.', 'error');
            return;
        }

        // Validate required fields are present in Excel data
        const requiredFields = ['ItemCode', 'QualifyQty', 'Rate'];
        const missingFields = [];

        // Check if Excel data has required structure
        const sampleRow = excelDataPreview[0];
        if (sampleRow) {
            requiredFields.forEach(field => {
                const hasField = Object.keys(sampleRow).some(key =>
                    key.toLowerCase().includes(field.toLowerCase()) ||
                    (field === 'QualifyQty' && (key.toLowerCase().includes('qualify') || key.toLowerCase().includes('qty'))) ||
                    (field === 'ItemCode' && (key.toLowerCase().includes('item') || key.toLowerCase().includes('code'))) ||
                    (field === 'Rate' && key.toLowerCase().includes('rate'))
                );
                if (!hasField) {
                    missingFields.push(field);
                }
            });
        }

        if (missingFields.length > 0) {
            showToast(`Excel file is missing required columns: ${missingFields.join(', ')}. Please ensure your Excel file contains ItemCode, Rate, and QualifyQty columns.`, 'error');
            return;
        }

        // Determine scheme type based on whether area is selected or customers are selected
        const rebateSchemeType = selectedArea ? "AREA" : "CUSTOMER";
        const isAreaWise = selectedArea ? true : false;

        await performUpload(rebateSchemeType, isAreaWise);
    };
    const resetAllState = () => { setSelectedCustomers([]); setSelectedArea(''); setExcelFile(null); setExcelFileName(''); setExcelDataPreview([]); setExcelValidationErrors([]); setExistingSchemeDetails([]); setSelectedRateScheme(''); setSchemeChoice('create'); };

    // When searching without an area selected (selectedExistingArea === 'ALL'), the customer
    // list is fetched with AreaId=ALL, so each row carries its own SFAreaId. Area-specific
    // rate calls need that real area id instead of the literal "ALL".
    const resolveCustomerAreaId = (customer) =>
        (selectedExistingArea && selectedExistingArea !== 'ALL') ? selectedExistingArea : (customer?.SFAreaId || selectedExistingArea);

    // Handle existing customer selection
    const handleExistingCustomerSelect = (customer) => {
        if (existingUserMode === 'new-rate') {
            // Existing functionality for new rate
            setSelectedExistingCustomer(customer);
            // Clear previous data
            setCustomerDetailsData([]);
            setCustomerSchemeId('');
            // Clear existing user Excel data
            setExistingUserExcelFile(null);
            setExistingUserExcelFileName('');
            setExistingUserExcelData([]);
            setExistingUserValidationErrors([]);
            // Fetch real customer rate details from API
            fetchCustomerRateDetails(resolveCustomerAreaId(customer), customer.CustomerId);
            showToast(`Selected customer: ${customer.CustomerName}`, 'info');
        } else if (existingUserMode === 'delete-scheme') {
            // Multiple selection for delete mode — only active (rates exist) customers are deletable
            if (!(customer.ItemCount ?? 0)) {
                showToast('Inactive customers (no rates) cannot be deleted.', 'warning');
                return;
            }
            const customerId = customer.CustomerId;
            const isSelected = selectedCustomersForDelete.some(c => c.CustomerId === customerId);

            if (isSelected) {
                setSelectedCustomersForDelete(prev => prev.filter(c => c.CustomerId !== customerId));
                showToast(`Removed ${customer.CustomerName} from delete list`, 'info');
            } else {
                setSelectedCustomersForDelete(prev => [...prev, customer]);
                showToast(`Added ${customer.CustomerName} to delete list`, 'info');
            }
        }
    };

    // Opens the "Upload Rate" popup for a specific customer — reuses the same
    // selection + fetch logic as clicking a row, just presented in a Dialog.
    const handleOpenUploadRateDialog = (customer) => {
        setSelectedExistingCustomer(customer);
        setCustomerDetailsData([]);
        setCustomerSchemeId('');
        setExistingUserExcelFile(null);
        setExistingUserExcelFileName('');
        setExistingUserExcelData([]);
        setExistingUserValidationErrors([]);
        fetchCustomerRateDetails(resolveCustomerAreaId(customer), customer.CustomerId);
        setUploadRateDialogOpen(true);
    };

    const handleCloseUploadRateDialog = () => {
        setUploadRateDialogOpen(false);
    };

    // Formats a YYYYMMDD string (e.g. "20260805") as DD-MM-YYYY.
    const formatYyyymmdd = (value) => {
        if (!value) return '-';
        const str = String(value);
        if (str.length !== 8) return str;
        return `${str.slice(6, 8)}-${str.slice(4, 6)}-${str.slice(0, 4)}`;
    };

    // Read-only rate view — fetches the same rate data as the New Rate flow,
    // but into its own state so it doesn't disturb the edit form or selection.
    const handleViewRateClick = async (customer) => {
        setViewRateCustomer(customer);
        setViewRateDialogOpen(true);
        setViewRateData([]);
        setLoadingViewRate(true);
        try {
            const response = await fetch(
                `${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${resolveCustomerAreaId(customer)}&CustomerID=${customer.CustomerId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to fetch customer rate details');
            const data = await response.json();
            const transformedData = (data || []).map((item, index) => ({
                sno: index + 1,
                itemName: item.ItemName,
                itemCode: item.ItemCode,
                qualifyQty: item.QualifyQty,
                rate: item.Rate,
                validTill: item.validtill || item.ValidTill
            }));
            setViewRateData(transformedData);
        } catch (error) {
            showToast(error.message || 'Failed to load rate details.', 'error');
        } finally {
            setLoadingViewRate(false);
        }
    };

    const handleCloseViewRateDialog = () => {
        setViewRateDialogOpen(false);
        setViewRateCustomer(null);
        setViewRateData([]);
    };

    const handleInvalidateRateClick = (customer) => {
        setCustomerToInvalidate(customer);
        setInvalidateConfirmOpen(true);
    };

    const handleCancelInvalidateRate = () => {
        setInvalidateConfirmOpen(false);
        setCustomerToInvalidate(null);
    };

    const handleConfirmInvalidateRate = async () => {
        const customer = customerToInvalidate;
        if (!customer) return;
        setInvalidateConfirmOpen(false);

        setIsInvalidatingRate(true);
        try {
            const now = new Date();
            const businessDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

            const response = await fetch(`${local_signage}/api/v1/updateinvalidateschemerates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    AreaId: (selectedExistingArea && selectedExistingArea !== 'ALL') ? selectedExistingArea : (customer.SFAreaId || selectedExistingArea),
                    CustomerId: customer.CustomerId,
                    businessDate,
                    CreatedUserId: userId
                })
            });

            if (!response.ok) {
                let detail = '';
                try {
                    const errBody = await response.json();
                    detail = errBody?.message || errBody?.error || '';
                } catch {
                    detail = await response.text();
                }
                throw new Error(detail || 'Failed to invalidate rate');
            }

            showToast(`Rate invalidated for ${customer.CustomerName}.`, 'success');
            handleCloseViewRateDialog();
            fetchExistingCustomersByArea(selectedExistingArea);
        } catch (error) {
            showToast(error.message || 'Failed to invalidate rate.', 'error');
        } finally {
            setIsInvalidatingRate(false);
            setCustomerToInvalidate(null);
        }
    };

    const handleInvalidateAreaRateClick = (row) => {
        setAreaToInvalidate(row);
        setInvalidateAreaConfirmOpen(true);
    };

    const handleCancelInvalidateAreaRate = () => {
        setInvalidateAreaConfirmOpen(false);
        setAreaToInvalidate(null);
    };

    const handleConfirmInvalidateAreaRate = async () => {
        const row = areaToInvalidate;
        if (!row) return;
        setInvalidateAreaConfirmOpen(false);

        setIsInvalidatingAreaRate(true);
        try {
            const now = new Date();
            const businessDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

            const response = await fetch(`${local_signage}/api/v1/updateinvalidateschemerates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    AreaId: row.SFAreaId,
                    CustomerId: 'AREA',
                    businessDate,
                    CreatedUserId: userId
                })
            });

            if (!response.ok) {
                let detail = '';
                try {
                    const errBody = await response.json();
                    detail = errBody?.message || errBody?.error || '';
                } catch {
                    detail = await response.text();
                }
                throw new Error(detail || 'Failed to invalidate rate');
            }

            showToast(`Rate invalidated for ${row.SFAreaName}.`, 'success');
            handleCloseViewAreaRateDialog();
            fetchAreaRebateScheme('ALL');
        } catch (error) {
            showToast(error.message || 'Failed to invalidate rate.', 'error');
        } finally {
            setIsInvalidatingAreaRate(false);
            setAreaToInvalidate(null);
        }
    };

    // Handle delete selected customers — opens the confirmation dialog
    const handleDeleteSelectedCustomers = () => {
        if (selectedCustomersForDelete.length === 0) {
            showToast('No customers selected for deletion', 'warning');
            return;
        }
        setShowBulkDeleteConfirm(true);
    };

    const handleCancelBulkDelete = () => {
        setShowBulkDeleteConfirm(false);
    };

    // Runs after the user confirms in the dialog
    const handleConfirmBulkDelete = async () => {
        setShowBulkDeleteConfirm(false);
        setIsDeleting(true);
        setShowDeleteProgress(true);
        setDeleteProgress(0);
        setDeleteResults([]);
        setDeleteStatus('Starting deletion process...');

        const totalCustomers = selectedCustomersForDelete.length;
        const results = [];

        try {
            for (let i = 0; i < selectedCustomersForDelete.length; i++) {
                const customer = selectedCustomersForDelete[i];
                const progress = ((i + 1) / totalCustomers) * 100;

                setDeleteProgress(progress);
                setDeleteStatus(`Deleting scheme for ${customer.CustomerName}... (${i + 1}/${totalCustomers})`);

                try {
                    const response = await fetch(`${local_signage}/api/v1/deletecustomerrebatescheme`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            AreaId: selectedExistingArea,
                            CustomerId: customer.CustomerId
                        })
                    });

                    if (!response.ok) {
                        const errorData = await response.text();
                        throw new Error(`API Error (${response.status}): ${errorData}`);
                    }

                    const result = await response.json();

                    results.push({
                        customerName: customer.CustomerName,
                        success: true,
                        result: result
                    });

                    console.log(`Successfully deleted scheme for ${customer.CustomerName}:`, result);

                } catch (err) {
                    console.error(`Delete error for ${customer.CustomerName}:`, err);
                    results.push({
                        customerName: customer.CustomerName,
                        success: false,
                        error: err.message
                    });
                }

                setDeleteResults([...results]);

                // Small delay between deletions
                if (i < selectedCustomersForDelete.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }

            // Show final summary
            const successCount = results.filter(r => r.success).length;
            const failureCount = results.filter(r => !r.success).length;

            if (successCount === totalCustomers) {
                setDeleteStatus('All schemes deleted successfully!');
                showToast(`Successfully deleted schemes for all ${totalCustomers} customers!`, 'success');

                // Refresh the customer list
                fetchExistingCustomersByArea(selectedExistingArea);

                // Clear selections
                setSelectedCustomersForDelete([]);
                setExistingUserMode('new-rate');
            } else if (successCount > 0) {
                setDeleteStatus(`${successCount} deleted successfully, ${failureCount} failed`);
                showToast(`${successCount} successful deletions, ${failureCount} failed`, 'warning');
            } else {
                setDeleteStatus('All deletions failed');
                showToast('All deletions failed. Please check the errors.', 'error');
            }

            // Close progress dialog after delay
            setTimeout(() => {
                setShowDeleteProgress(false);
                setDeleteProgress(0);
                setDeleteResults([]);
            }, 3000);

        } catch (err) {
            console.error('Delete process error:', err);
            showToast(`Delete process error: ${err.message}`, 'error');
            setDeleteStatus('Delete process failed');
        } finally {
            setIsDeleting(false);
        }
    };

    // Handle existing user Excel file change
    const handleExistingUserFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setExistingUserExcelFile(file);
            setExistingUserExcelFileName(file.name);
            setExistingUserExcelData([]);
            setExistingUserValidationErrors([]);
        }
    };

    // Clear functions for each upload type
    const clearNewUserExcel = () => {
        setExcelFile(null);
        setExcelFileName('');
        setExcelDataPreview([]);
        setExcelValidationErrors([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        showToast('Excel file cleared successfully', 'info');
    };

    // ─── POS Master Upload functions ──────────────────────────────
    const clearPosMasterExcel = () => {
        setPosMasterExcelFile(null);
        setPosMasterExcelFileName('');
        setPosMasterExcelData([]);
        setPosMasterValidationErrors([]);
        setPosMasterSelectedCustomers([]);
        setPosMasterCustomerDataMap({});
        if (posMasterFileInputRef.current) posMasterFileInputRef.current.value = '';
        showToast('Excel file cleared', 'info');
    };

    const handlePosMasterFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPosMasterExcelFile(file);
            setPosMasterExcelFileName(file.name);
            setPosMasterExcelData([]);
            setPosMasterValidationErrors([]);
        }
    };

    const handlePosMasterCustomerSelect = (customer) => {
        const id = customer.custID || customer.customerCode;
        setPosMasterSelectedCustomers(prev => {
            const exists = prev.some(c => (c.custID || c.customerCode) === id);
            return exists ? prev.filter(c => (c.custID || c.customerCode) !== id) : [...prev, customer];
        });
    };

    const handlePosMasterCustomerConfirm = () => {
        setShowPosMasterSearchModal(false);
        showToast(`${posMasterSelectedCustomers.length} customer(s) selected`, 'success');
    };

    const handlePosMasterSearch = async () => {
        if (!posMasterSelectedCustomerType || !posMasterSearchInput.trim() || !posMasterSelectedArea) {
            showToast('Please select Area, Customer Type, and enter a search term.', 'warning');
            return;
        }
        setLoadingSearch(true);
        try {
            const response = await fetch(
                `${local_url}/api/POSCustomerSerch/Get/${posMasterSelectedCustomerType}/${posMasterSearchType}/${posMasterSearchInput}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error(`Search failed: ${response.statusText}`);
            const data = await response.json();
            setPosMasterSearchResults(Array.isArray(data.customers) ? data.customers : []);
            setShowPosMasterSearchModal(true);
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingSearch(false);
        }
    };

    const processExcelForPOSMaster = async () => {
        if (!posMasterExcelFile) return;
        setIsPosMasterProcessing(true);
        setShowValidationProgress(true);
        setValidationProgress(0);
        setValidationStatus('Reading Excel file...');

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                setValidationProgress(10);
                setValidationStatus('Validating Excel structure...');
                await new Promise(r => setTimeout(r, 300));

                if (jsonData.length < 2) throw new Error('No data found in Excel file.');
                const headers = jsonData[0].map(h => String(h).trim());
                if (['CustomerID', 'AreaCode', 'ItemCode', 'Rate', 'QualifyQty'].some(h => !headers.includes(h)))
                    throw new Error('Missing required columns: CustomerID, AreaCode, ItemCode, Rate, QualifyQty.');

                const hasValidityUpto = headers.includes('ValidityUpto');
                setValidationProgress(20);
                setValidationStatus('Processing rows...');
                await new Promise(r => setTimeout(r, 300));

                const processedData = [], errors = [];
                let hasPastDateError = false;
                const customerMap = {};
                const seenCombinations = new Map(); // key: "CustomerID|AreaCode|ItemCode" → first rowNum
                const dataRows = jsonData.slice(1).map((row, i) => ({ row, rowNum: i + 2 }))
                    .filter(({ row }) => row && row.some(c => c !== null && c !== undefined && String(c).trim() !== ''));

                // Fetch material description for each unique ItemCode
                const uniqueItemCodesForDesc = [...new Set(dataRows.map(({ row }) => String(row[headers.indexOf('ItemCode')] ?? '').trim()).filter(Boolean))];
                const itemDescriptionMap = {};
                await Promise.all(uniqueItemCodesForDesc.map(async (code) => {
                    try {
                        const itemResponse = await fetch(`${local_signage}/api/v1/fetchitembypartnumber?PartNumber=${encodeURIComponent(code)}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!itemResponse.ok) return;
                        const itemData = await itemResponse.json();
                        const record = Array.isArray(itemData) ? itemData[0] : itemData;
                        itemDescriptionMap[code] = record?.LongDescription || record?.ItemName || '';
                    } catch {
                        // leave description blank on failure
                    }
                }));

                // Warm the server-date cache once before validating rows, so
                // ValidityUpto is checked against the server's date, not the browser's.
                await getServerTodayStr();

                for (let i = 0; i < dataRows.length; i++) {
                    const { row, rowNum } = dataRows[i];
                    const customerId = String(row[headers.indexOf('CustomerID')] ?? '').trim();
                    const areaCode = String(row[headers.indexOf('AreaCode')] ?? '').trim();
                    const normalizedAreaCode = normalizeAreaCode(areaCode);
                    const itemCode = String(row[headers.indexOf('ItemCode')] ?? '').trim();
                    const rawQty = String(row[headers.indexOf('QualifyQty')] ?? '').trim();
                    const rawRate = String(row[headers.indexOf('Rate')] ?? '').trim();
                    const qty = Number(rawQty);
                    const rate = Number(rawRate);
                    const validity = hasValidityUpto ? String(row[headers.indexOf('ValidityUpto')] ?? '').trim() : '';
                    let hasErr = false;
                    let sfAreaId = '';
                    if (!customerId) { errors.push(`Row ${rowNum}: CustomerID is missing.`); hasErr = true; }
                    if (!normalizedAreaCode) { errors.push(`Row ${rowNum}: AreaCode is missing.`); hasErr = true; }
                    if (!itemCode) { errors.push(`Row ${rowNum}: ItemCode is missing.`); hasErr = true; }
                    if (!rawQty || isNaN(qty) || qty < 0) { errors.push(`Row ${rowNum}: QualifyQty "${rawQty}" is invalid — must be a number ≥ 0.`); hasErr = true; }
                    if (!rawRate || isNaN(rate) || rate < 0) { errors.push(`Row ${rowNum}: Rate "${rawRate}" is invalid — must be a number ≥ 0.`); hasErr = true; }
                    if (validity && !/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/.test(validity)) {
                        errors.push(`Row ${rowNum}: ValidityUpto "${validity}" is invalid — use YYYY-MM-DD format.`); hasErr = true;
                    } else if (validity && isValidityUptoBeforeToday(validity)) {
                        hasPastDateError = true; hasErr = true;
                    }
                    if (normalizedAreaCode) {
                        sfAreaId = areaCodeToIdMap[normalizedAreaCode] || '';
                        if (!sfAreaId) { errors.push(`Row ${rowNum}: AreaCode "${areaCode}" not found in system.`); hasErr = true; }
                    }
                    if (!hasErr) {
                        if (customerMap[customerId] && customerMap[customerId].sfAreaId !== sfAreaId) {
                            errors.push(`Row ${rowNum}: CustomerID "${customerId}" has multiple AreaCode values in the same file.`);
                            hasErr = true;
                        }
                    }
                    if (!hasErr) {
                        const dupKey = `${customerId}|${normalizedAreaCode}|${itemCode}`;
                        if (seenCombinations.has(dupKey)) {
                            errors.push(`Row ${rowNum}: Duplicate — CustomerID "${customerId}", AreaCode "${areaCode}", ItemCode "${itemCode}" already exists at Row ${seenCombinations.get(dupKey)}. Use a different AreaCode or ItemCode.`);
                            hasErr = true;
                        } else {
                            seenCombinations.set(dupKey, rowNum);
                        }
                    }
                    if (!hasErr) {
                        const item = { CustomerID: customerId, AreaCode: normalizedAreaCode, sfAreaId, ItemCode: itemCode, ItemDescription: itemDescriptionMap[itemCode] || '', QualifyQty: qty, Rate: parseFloat(rate.toFixed(2)) };
                        if (validity) item.ValidityUpto = validity;
                        processedData.push(item);
                        if (!customerMap[customerId]) customerMap[customerId] = { custID: customerId, customerName: customerId, areaCode: normalizedAreaCode, sfAreaId };
                        if (!customerMap[customerId]._rows) customerMap[customerId]._rows = [];
                        customerMap[customerId]._rows.push(item);
                    }
                    setValidationProgress(20 + (70 * (i + 1) / dataRows.length));
                    if ((i + 1) % 10 === 0) await new Promise(r => setTimeout(r, 50));
                }

                // Customer existence check via API
                const uniqueCustomerIds = Object.keys(customerMap);
                if (uniqueCustomerIds.length > 0) {
                    setValidationProgress(93);
                    setValidationStatus(`Validating ${uniqueCustomerIds.length} customer ID(s) with server...`);
                    try {
                        const authToken = localStorage.getItem('AuthorizationToken');
                        const custResponse = await fetch(`${retail_uat}/api/v1/getcustomerdetails`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${authToken}`
                            },
                            body: JSON.stringify({
                                Customers: uniqueCustomerIds.map(id => ({ CustomerId: id }))
                            })
                        });
                        if (custResponse.ok) {
                            const custData = await custResponse.json();
                            const returnedList = Array.isArray(custData.data) ? custData.data : [];
                            // Build a map of id → customer record from API
                            const apiCustomerMap = {};
                            returnedList.forEach(c => {
                                if (c.CustomerId) apiCustomerMap[String(c.CustomerId).trim()] = c;
                            });
                            uniqueCustomerIds.forEach(id => {
                                const apiRecord = apiCustomerMap[id];
                                if (!apiRecord || apiRecord.Status === false) {
                                    // Mark as invalid — flag in errors
                                    errors.push(`CustomerID "${id}" not found in system (invalid customer).`);
                                } else {
                                    // Update display name from API response
                                    if (customerMap[id] && apiRecord.CustomerName) {
                                        customerMap[id].customerName = apiRecord.CustomerName;
                                    }
                                }
                            });
                        } else {
                            console.warn('Customer validation API returned:', custResponse.status, await custResponse.text());
                        }
                    } catch (custErr) {
                        console.error('Customer validation API error:', custErr);
                    }
                }

                // Build per-customer data map
                const dataMap = {};
                Object.values(customerMap).forEach(c => { dataMap[c.custID] = c._rows || []; delete c._rows; });

                setPosMasterExcelData(processedData);
                setPosMasterCustomerDataMap(dataMap);
                setPosMasterSelectedCustomers(Object.values(customerMap));
                setPosMasterValidationErrors(errors);
                setValidationProgress(100);
                setValidationStatus(errors.length > 0 ? `Done with ${errors.length} error(s)` : `Validated! ${Object.keys(customerMap).length} customer(s) found.`);
                if (hasPastDateError) {
                    showToast("ValidityUpto cannot be less than today's date.", 'warning');
                } else {
                    showToast(errors.length > 0 ? `${errors.length} validation error(s) found.` : `Excel validated! ${Object.keys(customerMap).length} customer(s) ready to upload.`, errors.length > 0 ? 'warning' : 'success');
                }
                setTimeout(() => { setShowValidationProgress(false); setValidationProgress(0); }, 1500);
            } catch (err) {
                setValidationStatus('Validation failed');
                handleApiError('Error parsing Excel: ' + err.message);
                setTimeout(() => { setShowValidationProgress(false); setValidationProgress(0); }, 1500);
            } finally {
                setIsPosMasterProcessing(false);
            }
        };
        reader.readAsArrayBuffer(posMasterExcelFile);
    };

    const handlePosMasterUpload = async () => {
        if (posMasterValidationErrors.length > 0 || posMasterExcelData.length === 0) {
            showToast('Please resolve errors or provide valid Excel data.', 'error'); return;
        }
        if (posMasterSelectedCustomers.length === 0) {
            showToast('No customers found in Excel. Please check the CustomerID column.', 'error'); return;
        }
        // Check mapping status for each customer; auto-map if not yet mapped
        showToast('Checking customer mapping status...', 'info');
        for (const customer of posMasterSelectedCustomers) {
            const customerId = customer.custID;
            const areaId = customer.sfAreaId;
            if (!customerId || !areaId) continue;
            try {
                const checkResponse = await fetch(
                    `${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${areaId}&CustomerID=${customerId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (checkResponse.ok) {
                    const data = await checkResponse.json();
                    if (data.length === 0) {
                        showToast(`Mapping ${customer.customerName} to area...`, 'info');
                        await insertAreawiseCustomer(areaId, customerId);
                    }
                }
            } catch (err) {
                console.warn(`Could not check/map customer ${customerId}:`, err);
            }
        }

        await performUpload('CUST', false, posMasterSelectedCustomers, null, posMasterCustomerDataMap, true);
    };
    // ──────────────────────────────────────────────────────────────

    const clearExistingUserExcel = () => {
        setExistingUserExcelFile(null);
        setExistingUserExcelFileName('');
        setExistingUserExcelData([]);
        setExistingUserValidationErrors([]);
        // Clear the table data as well
        setCustomerDetailsData([]);
        // Reset customer details to original state if they were from Excel processing
        if (selectedExistingCustomer && customerDetailsData.length > 0 && customerDetailsData[0]?.isFromExcel) {
            fetchCustomerRateDetails(selectedExistingArea, selectedExistingCustomer.CustomerId);
        }
        if (existingUserFileInputRef.current) {
            existingUserFileInputRef.current.value = '';
        }
        showToast('Excel file cleared successfully', 'info');
    };

    const clearMasterExcel = () => {
        setMasterExcelFile(null);
        setMasterExcelFileName('');
        setMasterExcelData([]);
        setMasterValidationErrors([]);
        setMasterRowErrors({});
        if (masterFileInputRef.current) {
            masterFileInputRef.current.value = '';
        }
        showToast('Excel file cleared successfully', 'info');
    };

    // MAP Scheme Modal Handlers
    const handleMapSchemeClick = (customer) => {
        setSelectedCustomerForMapping(customer);
        setMapSchemeModalOpen(true);
        fetchAvailableRebateSchemes(selectedExistingArea);
    };

    const handleCloseMapSchemeModal = () => {
        setMapSchemeModalOpen(false);
        setSelectedCustomerForMapping(null);
        setSelectedMappingScheme('');
        setSchemeRateData([]);
    };

    const fetchAvailableRebateSchemes = async (areaId) => {
        if (!areaId) return;

        setLoadingAvailableSchemes(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebatescheme?RebateSchemeType=CUST&AreaId=${areaId}&CustomerID=ALL`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setAvailableRebateSchemes(data);
            } else {
                showToast('Failed to fetch rebate schemes', 'error');
            }
        } catch (error) {
            console.error('Error fetching rebate schemes:', error);
            showToast('Error fetching rebate schemes', 'error');
        } finally {
            setLoadingAvailableSchemes(false);
        }
    };

    const fetchSchemeRateData = async (rebateSchemeId) => {
        if (!rebateSchemeId) return;

        setLoadingSchemeRateData(true);
        try {
            const today = new Date();
            const businessDate = today.getFullYear().toString() +
                String(today.getMonth() + 1).padStart(2, '0') +
                String(today.getDate()).padStart(2, '0');

            const response = await fetch(`${local_signage}/api/v1/rebateschemeratereport?BusinessDate=${businessDate}&AreaId=ALL&RebateSchemeId=${rebateSchemeId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.RebateRate && data.RebateRate.length > 0) {
                    // Flatten the itemline data for table display
                    const tableData = [];
                    data.RebateRate.forEach(area => {
                        if (area.itemline) {
                            area.itemline.forEach(item => {
                                tableData.push({
                                    AreaName: area.AreaName,
                                    ItemName: item.ItemName,
                                    Rate: item.Rate,
                                    QualifyQty: item.QualifyQty
                                });
                            });
                        }
                    });
                    setSchemeRateData(tableData);
                } else {
                    setSchemeRateData([]);
                }
            } else {
                showToast('Failed to fetch scheme rate data', 'error');
            }
        } catch (error) {
            console.error('Error fetching scheme rate data:', error);
            showToast('Error fetching scheme rate data', 'error');
        } finally {
            setLoadingSchemeRateData(false);
        }
    };

    const handleSchemeSelection = (schemeId) => {
        setSelectedMappingScheme(schemeId);
        if (schemeId) {
            fetchSchemeRateData(schemeId);
        } else {
            setSchemeRateData([]);
        }
    };

    const handleMapSchemeConfirm = async () => {
        if (!selectedCustomerForMapping || !selectedMappingScheme) {
            showToast('Please select a scheme to map', 'warning');
            return;
        }

        try {
            // Step 1: First call insertcustomerrebatescheme
            const requestBody = {
                AreaId: selectedExistingArea,
                CustomerId: selectedCustomerForMapping.CustomerId,
                RebateSchemeId: selectedMappingScheme,
                AddOnDiscountPc: 0,
                DayCreditAllowed: true
            };

            const response = await fetch(`${local_signage}/api/v1/insertcustomerrebatescheme`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Failed to map customer to scheme: ${response.statusText}`);
            }

            const result = await response.json();

            // Step 2: Check if mapping was successful and continue the process
            if (Array.isArray(result) && result.length > 0 && result[0].Details === "success") {
                showToast(`Successfully mapped ${selectedCustomerForMapping.CustomerName} to rebate scheme!`, 'success');

                // Close modal and refresh customer list
                handleCloseMapSchemeModal();
                fetchExistingCustomersByArea(selectedExistingArea);

            } else {
                showToast('Failed to map customer to scheme', 'error');
            }

        } catch (error) {
            console.error('Error in mapping process:', error);
            showToast('Error in mapping process', 'error');
        }
    };

    // Returns true if the given YYYY-MM-DD (or ISO) date string is earlier than today.
    // "Today" is taken from the server's healthcheck API (not the browser clock),
    // since a wrong/mismatched local clock/timezone was causing false positives here.
    // Callers should `await getServerTodayStr()` once before validating a batch of
    // rows so the cache is warm; this reads that cache synchronously.
    const isValidityUptoBeforeToday = (dateStr) => {
        if (!dateStr) return false;
        const datePart = String(dateStr).split('T')[0];
        const todayStr = getCachedServerTodayStr();
        return datePart < todayStr;
    };

    // Normalizes an Excel "ValidityUpto" cell to a plain "YYYY-MM-DD" string.
    // XLSX can hand back a JS Date object, an Excel serial number, or a raw
    // string like "2026-08-09T23:59:59" depending on the cell's format —
    // this collapses all of those to just the date part.
    const normalizeValidityUpto = (cellValue) => {
        if (cellValue === null || cellValue === undefined || cellValue === '') return '';
        if (cellValue instanceof Date) {
            const yyyy = cellValue.getFullYear();
            const mm = String(cellValue.getMonth() + 1).padStart(2, '0');
            const dd = String(cellValue.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }
        if (typeof cellValue === 'number') {
            // Excel date serial (days since 1899-12-30)
            const utcDays = Math.floor(cellValue - 25569);
            const utcMillis = utcDays * 86400 * 1000;
            const date = new Date(utcMillis);
            const yyyy = date.getUTCFullYear();
            const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
            const dd = String(date.getUTCDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }
        const str = String(cellValue).trim();
        const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
        return match ? match[1] : str;
    };

    // Process existing user Excel file for customers with no existing rates
    const processExistingUserExcelForNewCustomer = (fileOverride) => {
        const fileToProcess = fileOverride || existingUserExcelFile;
        if (!fileToProcess) {
            showToast('Please upload an Excel file first.', 'error');
            return;
        }

        setIsExistingUserExcelProcessing(true);
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    throw new Error('No data found in Excel file.');
                }

                const headers = jsonData[0].map(h => String(h).trim());
                const requiredHeaders = ['ItemCode', 'Rate', 'QualifyQty', 'CustomerID', 'AreaCode', 'ValidityUpto'];
                const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

                if (missingHeaders.length > 0) {
                    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
                }

                const hasValidityUpto = headers.includes('ValidityUpto');
                const hasCustomerIdCol = headers.includes('CustomerID');
                const hasAreaCodeCol = headers.includes('AreaCode');
                const expectedCustomerId = String(selectedExistingCustomer?.CustomerId || '').trim().toUpperCase();
                const effectiveAreaIdForCode = (selectedExistingArea && selectedExistingArea !== 'ALL') ? selectedExistingArea : (selectedExistingCustomer?.SFAreaId || selectedExistingArea);
                const expectedAreaCode = String(existingUserAreas.find(a => a.AreaId === effectiveAreaIdForCode)?.AreaCode || '').trim().toUpperCase();
                const processedData = [];
                const newErrors = [];
                let hasPastDateError = false;
                let hasCustomerMismatchError = false;
                let hasAreaMismatchError = false;
                let hasCustomerMissingError = false;
                let hasAreaMissingError = false;
                let hasItemCodeMissingError = false;
                let hasQtyMissingError = false;
                let hasRateMissingError = false;
                let hasValidityMissingError = false;
                let hasInvalidItemCodeError = false;

                // Filter out empty rows and track original row numbers
                const dataRowsWithIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithIndex.push({ row, originalRowNumber: originalIndex + 2 });
                    }
                });

                // Validate each ItemCode exists in the item master
                const uniqueItemCodes = [...new Set(dataRowsWithIndex.map(({ row }) => String(row[headers.indexOf('ItemCode')] || '').trim()).filter(Boolean))];
                const invalidItemCodes = new Set();
                const itemDescriptionMap = {};
                await Promise.all(uniqueItemCodes.map(async (code) => {
                    try {
                        const itemResponse = await fetch(`${local_signage}/api/v1/fetchitembypartnumber?PartNumber=${encodeURIComponent(code)}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!itemResponse.ok) { invalidItemCodes.add(code); return; }
                        const itemData = await itemResponse.json();
                        const exists = Array.isArray(itemData) ? itemData.length > 0 : Boolean(itemData && Object.keys(itemData).length > 0);
                        if (!exists) {
                            invalidItemCodes.add(code);
                        } else {
                            const record = Array.isArray(itemData) ? itemData[0] : itemData;
                            itemDescriptionMap[code] = record?.LongDescription || record?.ItemName || '';
                        }
                    } catch {
                        invalidItemCodes.add(code);
                    }
                }));

                // Warm the server-date cache once before validating rows, so
                // ValidityUpto is checked against the server's date, not the browser's.
                await getServerTodayStr();

                dataRowsWithIndex.forEach(({ row, originalRowNumber }) => {
                    let hasRowError = false;

                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();
                    const rawQty = String(row[headers.indexOf('QualifyQty')] ?? '').trim();
                    const rawRate = String(row[headers.indexOf('Rate')] ?? '').trim();
                    const qty = Number(rawQty);
                    const rate = Number(rawRate);
                    const rawValidityUpto = hasValidityUpto ? String(row[headers.indexOf('ValidityUpto')] ?? '').trim() : '';
                    const validityUpto = hasValidityUpto ? normalizeValidityUpto(row[headers.indexOf('ValidityUpto')]) : '';

                    if (hasCustomerIdCol) {
                        const rowCustomerId = String(row[headers.indexOf('CustomerID')] || '').trim().toUpperCase();
                        if (!rowCustomerId) {
                            hasCustomerMissingError = true;
                            hasRowError = true;
                        } else if (rowCustomerId !== expectedCustomerId) {
                            hasCustomerMismatchError = true;
                            hasRowError = true;
                        }
                    }
                    if (hasAreaCodeCol) {
                        const rowAreaCode = String(row[headers.indexOf('AreaCode')] || '').trim().toUpperCase();
                        if (!rowAreaCode) {
                            hasAreaMissingError = true;
                            hasRowError = true;
                        } else if (rowAreaCode !== expectedAreaCode) {
                            hasAreaMismatchError = true;
                            hasRowError = true;
                        }
                    }

                    if (!itemCode) {
                        hasItemCodeMissingError = true;
                        hasRowError = true;
                    } else if (invalidItemCodes.has(itemCode)) {
                        hasInvalidItemCodeError = true;
                        hasRowError = true;
                    }
                    if (!rawQty) {
                        hasQtyMissingError = true;
                        hasRowError = true;
                    } else if (isNaN(qty) || qty < 0) {
                        newErrors.push(`Row ${originalRowNumber}: Invalid Quantity - cannot be negative.`);
                        hasRowError = true;
                    }
                    if (!rawRate) {
                        hasRateMissingError = true;
                        hasRowError = true;
                    } else if (isNaN(rate) || rate < 0) {
                        newErrors.push(`Row ${originalRowNumber}: Invalid Rate - cannot be negative.`);
                        hasRowError = true;
                    }
                    if (hasValidityUpto && !rawValidityUpto) {
                        hasValidityMissingError = true;
                        hasRowError = true;
                    } else if (validityUpto && !/^\d{4}-\d{2}-\d{2}$/.test(validityUpto)) {
                        newErrors.push(`Row ${originalRowNumber}: ValidityUpto "${validityUpto}" is invalid — use YYYY-MM-DD format.`);
                        hasRowError = true;
                    } else if (validityUpto && isValidityUptoBeforeToday(validityUpto)) {
                        hasPastDateError = true;
                        hasRowError = true;
                    }

                    // Business rule validation: QualifyQty and Rate relationship
                    if (!hasRowError) {
                        if (qty === 0 && rate > 0) {
                            newErrors.push(`Row ${originalRowNumber}: QualifyQty cannot be 0 when Rate is greater than 0.`);
                            hasRowError = true;
                        }
                    }

                    if (!hasRowError) {
                        const item = {
                            itemCode, qualifyQuantity: qty, rate,
                            itemDescription: itemDescriptionMap[itemCode] || '',
                            customerId: expectedCustomerId || selectedExistingCustomer?.CustomerId || '',
                            areaCode: expectedAreaCode || ''
                        };
                        if (validityUpto) item.validityUpto = validityUpto;
                        processedData.push(item);
                    }
                });

                setExistingUserExcelData(processedData);
                setExistingUserValidationErrors(newErrors);

                // Create table data for display (since customer has no existing rates)
                const integratedCustomerDetails = processedData.map((item, index) => ({
                    sno: index + 1,
                    itemName: 'New Item', // Since this is a new customer with no existing data
                    itemCode: item.itemCode,
                    qualifyQty: '-', // No existing quantity
                    rate: '-', // No existing rate
                    hasExcelData: true,
                    newQty: item.qualifyQuantity,
                    newRate: item.rate,
                    isChanged: true, // Always true for new customers
                    isFromExcel: true
                }));

                // Update the customer details to show the new Excel data in table
                setCustomerDetailsData(integratedCustomerDetails);

                if (hasCustomerMissingError) {
                    showToast('Column required: CustomerID', 'error');
                } else if (hasAreaMissingError) {
                    showToast('Column required: AreaCode', 'error');
                } else if (hasItemCodeMissingError) {
                    showToast('Column required: ItemCode', 'error');
                } else if (hasQtyMissingError) {
                    showToast('Column required: QualifyQty', 'error');
                } else if (hasRateMissingError) {
                    showToast('Column required: Rate', 'error');
                } else if (hasValidityMissingError) {
                    showToast('Column required: ValidityUpto', 'error');
                } else if (hasInvalidItemCodeError) {
                    showToast('Invalid item code', 'error');
                } else if (hasCustomerMismatchError) {
                    showToast('CustomerID in Excel does not match the selected customer.', 'warning');
                } else if (hasAreaMismatchError) {
                    showToast('AreaCode in Excel does not match the selected area.', 'warning');
                } else if (hasPastDateError) {
                    showToast("ValidityUpto cannot be less than today's date.", 'warning');
                } else if (newErrors.length === 0) {
                    showToast(`Successfully processed ${processedData.length} items. Review the data below and click "Upload Rate Updates" to save!`, 'success');
                } else {
                    showToast(`Found ${newErrors.length} validation errors. Please fix them before uploading.`, 'warning');
                }

            } catch (error) {
                console.error('Excel processing error:', error);
                showToast(`Excel processing failed: ${error.message}`, 'error');
                setExistingUserExcelData([]);
                // Don't show table for failed processing
                setCustomerDetailsData([]);
            } finally {
                setIsExistingUserExcelProcessing(false);
            }
        };

        reader.readAsArrayBuffer(fileToProcess);
    };

    // Process existing user Excel file and compare with current rates
    const processExistingUserExcel = (fileOverride) => {
        const fileToProcess = fileOverride || existingUserExcelFile;
        if (!fileToProcess || !customerDetailsData.length) {
            showToast('Please ensure you have selected a customer and uploaded an Excel file.', 'error');
            return;
        }

        setIsExistingUserExcelProcessing(true);
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    throw new Error('No data found in Excel file.');
                }

                const headers = jsonData[0].map(h => String(h).trim());
                const requiredHeaders = ['ItemCode', 'Rate', 'QualifyQty', 'CustomerID', 'AreaCode', 'ValidityUpto'];
                const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

                if (missingHeaders.length > 0) {
                    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
                }

                const hasValidityUptoCol = headers.includes('ValidityUpto');
                const hasCustomerIdCol = headers.includes('CustomerID');
                const hasAreaCodeCol = headers.includes('AreaCode');
                const expectedCustomerId = String(selectedExistingCustomer?.CustomerId || '').trim().toUpperCase();
                const effectiveAreaIdForCode = (selectedExistingArea && selectedExistingArea !== 'ALL') ? selectedExistingArea : (selectedExistingCustomer?.SFAreaId || selectedExistingArea);
                const expectedAreaCode = String(existingUserAreas.find(a => a.AreaId === effectiveAreaIdForCode)?.AreaCode || '').trim().toUpperCase();
                const processedData = [];
                const newErrors = [];
                const comparisonData = [];
                let hasPastDateError = false;
                let hasCustomerMismatchError = false;
                let hasAreaMismatchError = false;
                let hasCustomerMissingError = false;
                let hasAreaMissingError = false;
                let hasItemCodeMissingError = false;
                let hasQtyMissingError = false;
                let hasRateMissingError = false;
                let hasValidityMissingError = false;
                let hasInvalidItemCodeError = false;

                // Filter out empty rows and track original row numbers
                const dataRowsWithIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithIndex.push({ row, originalRowNumber: originalIndex + 2 });
                    }
                });

                // Validate each ItemCode exists in the item master
                const uniqueItemCodes = [...new Set(dataRowsWithIndex.map(({ row }) => String(row[headers.indexOf('ItemCode')] || '').trim()).filter(Boolean))];
                const invalidItemCodes = new Set();
                const itemDescriptionMap = {};
                await Promise.all(uniqueItemCodes.map(async (code) => {
                    try {
                        const itemResponse = await fetch(`${local_signage}/api/v1/fetchitembypartnumber?PartNumber=${encodeURIComponent(code)}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!itemResponse.ok) { invalidItemCodes.add(code); return; }
                        const itemData = await itemResponse.json();
                        const exists = Array.isArray(itemData) ? itemData.length > 0 : Boolean(itemData && Object.keys(itemData).length > 0);
                        if (!exists) {
                            invalidItemCodes.add(code);
                        } else {
                            const record = Array.isArray(itemData) ? itemData[0] : itemData;
                            itemDescriptionMap[code] = record?.LongDescription || record?.ItemName || '';
                        }
                    } catch {
                        invalidItemCodes.add(code);
                    }
                }));

                // Warm the server-date cache once before validating rows, so
                // ValidityUpto is checked against the server's date, not the browser's.
                await getServerTodayStr();

                dataRowsWithIndex.forEach(({ row, originalRowNumber }) => {
                    let hasRowError = false;

                    if (hasCustomerIdCol) {
                        const rowCustomerId = String(row[headers.indexOf('CustomerID')] || '').trim().toUpperCase();
                        if (!rowCustomerId) {
                            hasCustomerMissingError = true;
                            hasRowError = true;
                        } else if (rowCustomerId !== expectedCustomerId) {
                            hasCustomerMismatchError = true;
                            hasRowError = true;
                        }
                    }
                    if (hasAreaCodeCol) {
                        const rowAreaCode = String(row[headers.indexOf('AreaCode')] || '').trim().toUpperCase();
                        if (!rowAreaCode) {
                            hasAreaMissingError = true;
                            hasRowError = true;
                        } else if (rowAreaCode !== expectedAreaCode) {
                            hasAreaMismatchError = true;
                            hasRowError = true;
                        }
                    }

                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();
                    const rawQty = String(row[headers.indexOf('QualifyQty')] ?? '').trim();
                    const rawRate = String(row[headers.indexOf('Rate')] ?? '').trim();
                    const qty = Number(rawQty);
                    const rate = Number(rawRate);
                    const rawValidityUpto = hasValidityUptoCol ? String(row[headers.indexOf('ValidityUpto')] ?? '').trim() : '';
                    const validityUpto = hasValidityUptoCol ? normalizeValidityUpto(row[headers.indexOf('ValidityUpto')]) : '';

                    if (!itemCode) {
                        hasItemCodeMissingError = true;
                        hasRowError = true;
                    } else if (invalidItemCodes.has(itemCode)) {
                        hasInvalidItemCodeError = true;
                        hasRowError = true;
                    }
                    if (!rawQty) {
                        hasQtyMissingError = true;
                        hasRowError = true;
                    } else if (isNaN(qty) || qty < 0) {
                        newErrors.push(`Row ${originalRowNumber}: Invalid Quantity - cannot be negative.`);
                        hasRowError = true;
                    }
                    if (!rawRate) {
                        hasRateMissingError = true;
                        hasRowError = true;
                    } else if (isNaN(rate) || rate < 0) {
                        newErrors.push(`Row ${originalRowNumber}: Invalid Rate - cannot be negative.`);
                        hasRowError = true;
                    }
                    if (hasValidityUptoCol && !rawValidityUpto) {
                        hasValidityMissingError = true;
                        hasRowError = true;
                    } else if (validityUpto && !/^\d{4}-\d{2}-\d{2}$/.test(validityUpto)) {
                        newErrors.push(`Row ${originalRowNumber}: ValidityUpto "${validityUpto}" is invalid — use YYYY-MM-DD format.`);
                        hasRowError = true;
                    } else if (validityUpto && isValidityUptoBeforeToday(validityUpto)) {
                        hasPastDateError = true;
                        hasRowError = true;
                    }

                    // Business rule validation: QualifyQty and Rate relationship
                    if (!hasRowError) {
                        if (qty === 0 && rate > 0) {
                            newErrors.push(`Row ${originalRowNumber}: QualifyQty cannot be 0 when Rate is greater than 0.`);
                            hasRowError = true;
                        }
                    }

                    if (!hasRowError) {
                        const processedRow = {
                            itemCode,
                            qualifyQuantity: qty,
                            rate: parseFloat(rate.toFixed(2)),
                            hasError: false,
                            itemDescription: itemDescriptionMap[itemCode] || '',
                            customerId: expectedCustomerId || selectedExistingCustomer?.CustomerId || '',
                            areaCode: expectedAreaCode || ''
                        };
                        if (validityUpto) processedRow.validityUpto = validityUpto;
                        processedData.push(processedRow);
                    }
                });

                // Create integrated customer details with comparison data
                const integratedCustomerDetails = [];
                const existingItemsMap = new Map();

                // First, add all existing customer items
                customerDetailsData.forEach((existing, index) => {
                    existingItemsMap.set(existing.itemCode, existing);
                    integratedCustomerDetails.push({
                        ...existing,
                        sno: index + 1,
                        hasExcelData: false,
                        newQty: null,
                        newRate: null,
                        isChanged: false,
                        isFromExcel: false
                    });
                });

                // Then, process Excel data and either update existing items or add new ones
                processedData.forEach(excelItem => {
                    const existingItem = existingItemsMap.get(excelItem.itemCode);

                    if (existingItem) {
                        // Update existing item with Excel data
                        const existingIndex = integratedCustomerDetails.findIndex(item => item.itemCode === excelItem.itemCode);
                        if (existingIndex !== -1) {
                            integratedCustomerDetails[existingIndex] = {
                                ...integratedCustomerDetails[existingIndex],
                                hasExcelData: true,
                                newQty: excelItem.qualifyQuantity,
                                newRate: excelItem.rate,
                                isChanged: (existingItem.qualifyQty !== excelItem.qualifyQuantity) || (existingItem.rate !== excelItem.rate),
                                isFromExcel: false
                            };
                        }
                    } else {
                        // Add new item from Excel
                        integratedCustomerDetails.push({
                            sno: integratedCustomerDetails.length + 1,
                            itemName: 'New Item',
                            itemCode: excelItem.itemCode,
                            qualifyQty: '-',
                            rate: '-',
                            hasExcelData: true,
                            newQty: excelItem.qualifyQuantity,
                            newRate: excelItem.rate,
                            isChanged: true,
                            isFromExcel: true
                        });
                    }
                });

                setExistingUserExcelData(processedData);
                setExistingUserValidationErrors(newErrors);
                // Update the customer details to show comparison
                setCustomerDetailsData(integratedCustomerDetails);

                if (hasCustomerMissingError) {
                    showToast('Column required: CustomerID', 'error');
                } else if (hasAreaMissingError) {
                    showToast('Column required: AreaCode', 'error');
                } else if (hasItemCodeMissingError) {
                    showToast('Column required: ItemCode', 'error');
                } else if (hasQtyMissingError) {
                    showToast('Column required: QualifyQty', 'error');
                } else if (hasRateMissingError) {
                    showToast('Column required: Rate', 'error');
                } else if (hasValidityMissingError) {
                    showToast('Column required: ValidityUpto', 'error');
                } else if (hasInvalidItemCodeError) {
                    showToast('Invalid item code', 'error');
                } else if (hasCustomerMismatchError) {
                    showToast('CustomerID in Excel does not match the selected customer.', 'warning');
                } else if (hasAreaMismatchError) {
                    showToast('AreaCode in Excel does not match the selected area.', 'warning');
                } else if (hasPastDateError) {
                    showToast("ValidityUpto cannot be less than today's date.", 'warning');
                } else if (newErrors.length > 0) {
                    showToast(`Validation found ${newErrors.length} errors.`, 'warning');
                } else {
                    showToast(`Excel validated successfully! Found ${processedData.length} items for comparison.`, 'success');
                }

            } catch (err) {
                handleApiError('Error parsing Excel: ' + err.message);
            } finally {
                setIsExistingUserExcelProcessing(false);
            }
        };

        reader.readAsArrayBuffer(fileToProcess);
    };

    // Handle existing user rate upload
    const handleExistingUserUpload = async () => {
        if (existingUserValidationErrors.length > 0 || existingUserExcelData.length === 0) {
            showToast('Please resolve validation errors before uploading.', 'error');
            return;
        }

        if (!selectedExistingCustomer || !selectedExistingArea) {
            showToast('Please ensure customer and area are selected.', 'error');
            return;
        }

        setIsExistingUserUploading(true);
        setLoadingMapping(true);
        setShowMappingProgressDialog(true);
        setMappingStatus('Uploading existing user rate updates...');
        setMappingProgress(0);

        try {
            // Prepare the request body using the same structure as new users
            const requestBody = {
                uniqueId: "",
                rebateSchemeType: 'CUST',
                areaId: (selectedExistingArea && selectedExistingArea !== 'ALL') ? selectedExistingArea : (selectedExistingCustomer.SFAreaId || selectedExistingArea),
                rebateSchemeId: '',
                customerId: selectedExistingCustomer.CustomerId,
                dateOfEntry: (() => {
                    // Convert to IST (UTC+5:30)
                    const now = new Date();
                    const istOffset = 5.5 * 60 * 60 * 1000;
                    const istDate = new Date(now.getTime() + istOffset);
                    return istDate.toISOString();
                })(),
                createdUserCode: userId,
                ratedetail: existingUserExcelData.map(row => {
                    const detail = {
                        itemCode: row.itemCode,
                        qualifyQty: parseFloat(row.qualifyQuantity) || 0,
                        rate: parseFloat(row.rate) || 0,
                        validTill: toValidTill(row.validityUpto),
                        dml: "I"
                    };
                    return detail;
                })
            };

            console.log('Existing user upload request body:', requestBody);

            setMappingProgress(30);
            setMappingStatus('Sending updated rates to server...');

            const response = await fetch(`${local_url}/api/CustomerRate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            setMappingProgress(70);
            setMappingStatus('Processing response...');

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API Error (${response.status}): ${errorData}`);
            }

            const result = await response.json();
            console.log('CustomerRate API response:', result);
            console.log('Response type:', typeof result);
            console.log('Response keys:', Object.keys(result || {}));
            console.log('Description value:', result?.description);
            console.log('Result condition check:', !!(result && result.description));

            setMappingProgress(100);
            setMappingStatus('Upload successful!');

            showToast('Rate updates uploaded successfully!', 'success');

            // Close the dialogs right away.
            setShowMappingProgressDialog(false);
            setMappingProgress(0);
            setUploadRateDialogOpen(false);
            setExistingUserExcelFile(null);
            setExistingUserExcelFileName('');
            setExistingUserExcelData([]);
            setExistingUserValidationErrors([]);

            // Switch the customer list back to the active side right away — the
            // customer now has rates, so it belongs there instead of Inactive.
            setShowInactiveCustomers(false);
            fetchExistingCustomersByArea(selectedExistingArea, 'ALL', { silent: true });

        } catch (err) {
            console.error('Existing user upload error:', err);
            handleApiError(`Upload error: ${err.message}`);
            setMappingStatus('Upload failed');
            setMappingProgress(0);
        } finally {
            setIsExistingUserUploading(false);
            setLoadingMapping(false);
        }
    };

    // Function to fetch rebate scheme customer data
    const fetchRebateSchemeCustomerData = async () => {
        setIsLoadingRebateSchemeData(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebateschemecustomerbyarea?AreaId=ALL&PricingSchemeType=HORECA`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch rebate scheme data: ${response.status}`);
            }

            const data = await response.json();
            console.log('Rebate scheme customer data loaded:', data);
            setRebateSchemeCustomerData(data || []);
        } catch (error) {
            console.error('Error fetching rebate scheme customer data:', error);
            showToast('Failed to load customer validation data. Upload validation may be incomplete.', 'warning');
            setRebateSchemeCustomerData([]);
        } finally {
            setIsLoadingRebateSchemeData(false);
        }
    };

    // Helper functions for Excel processing
    const fetchBulkRates = async () => {
        setIsLoadingBulkRates(true);
        try {
            // Using AreaId=00001 as potential master/default area for rates lookup, similar to HorecaPro
            const response = await fetch(`${local_signage}/api/v1/getjsonbulkrates?AreaId=00001`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch bulk rates: ${response.status}`);
            }

            const data = await response.json();
            setBulkRates(data);
            console.log('Successfully loaded bulk rates for validation:', data);
        } catch (error) {
            console.error('Error fetching bulk rates:', error);
        } finally {
            setIsLoadingBulkRates(false);
        }
    };

    useEffect(() => {
        fetchBulkRates();
    }, []);

    const getItemName = (itemCode) => {
        if (!bulkRates || !bulkRates.RateSchemeUpdate) return '-';

        for (const parentItem of bulkRates.RateSchemeUpdate) {
            if (parentItem.childlist) {
                for (const childItem of parentItem.childlist) {
                    if (String(childItem.ChildItemId) === String(itemCode)) {
                        return childItem.chlidname || '-';
                    }
                }
            }
        }
        return '-';
    };

    const getParentName = (itemCode) => {
        if (!bulkRates || !bulkRates.RateSchemeUpdate) return '-';

        for (const parentItem of bulkRates.RateSchemeUpdate) {
            if (parentItem.childlist) {
                for (const childItem of parentItem.childlist) {
                    if (String(childItem.ChildItemId) === String(itemCode)) {
                        return parentItem.ParentName || '-';
                    }
                }
            }
        }
        return '-';
    };

    const getParentId = (itemCode) => {
        if (!bulkRates || !bulkRates.RateSchemeUpdate) return null;

        for (const parentItem of bulkRates.RateSchemeUpdate) {
            if (parentItem.childlist) {
                for (const childItem of parentItem.childlist) {
                    if (String(childItem.ChildItemId) === String(itemCode)) {
                        return parentItem.ParentItemId || null;
                    }
                }
            }
        }
        return null;
    };

    // Master view functions
    const handleMasterFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setMasterExcelFile(file);
            setMasterExcelFileName(file.name);
            setMasterExcelData([]);
            setMasterValidationErrors([]);
            setMasterRowErrors({});
        }
    };

    const processMasterExcel = () => {
        if (!masterExcelFile) return;
        setIsMasterExcelProcessing(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    throw new Error('No data found in Excel file.');
                }

                const headers = jsonData[0].map(h => String(h).trim());
  
                // Updated required headers logic
                const basicRequiredHeaders = ['AreaCode', 'CustomerID', 'ItemCode'];
                const missingBasicHeaders = basicRequiredHeaders.filter(h => !headers.includes(h));

                if (missingBasicHeaders.length > 0) {
                    throw new Error(`Missing required columns: ${missingBasicHeaders.join(', ')}`);
                }

                // Check for at least one rate column
                const rateColumns = ['RegularRate', 'BulkRate', 'VolumeRate'];
                const hasRateColumn = rateColumns.some(col => headers.includes(col));

                if (!hasRateColumn) {
                    throw new Error('At least one rate column is required: RegularRate, BulkRate, or VolumeRate');
                }

                const processedData = [];
                const newErrors = [];
                const newRowErrors = {};

                // Rate type mapping
                const rateTypeMap = {
                    'RegularRate': { type: '1R', name: 'Regular' },
                    'BulkRate': { type: '2B', name: 'Bulk' },
                    'VolumeRate': { type: '3V', name: 'Volume' }
                };

                // Filter out empty rows and track original row numbers
                const dataRowsWithIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithIndex.push({ row, originalRowNumber: originalIndex + 2 });
                    }
                });

                dataRowsWithIndex.forEach(({ row, originalRowNumber }) => {
                    const rowErrors = [];

                    const areaCodeRaw = String(row[headers.indexOf('AreaCode')] || '').trim();
                    const areaId = areaCodeRaw ? (areaCodeToIdMap[areaCodeRaw] || '') : '';

                    const customerIdRaw = String(row[headers.indexOf('CustomerID')] || '').trim();
                    const customerId = customerIdRaw ? customerIdRaw.padStart(10, '0') : '';

                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();

                    if (!areaCodeRaw) {
                        rowErrors.push('AreaCode missing');
                        newErrors.push(`Row ${originalRowNumber}: AreaCode missing.`);
                    } else if (!areaId) {
                        rowErrors.push('AreaCode invalid');
                        newErrors.push(`Row ${originalRowNumber}: AreaCode '${areaCodeRaw}' not found in system.`);
                    }
                    if (!customerId) {
                        rowErrors.push('CustomerID missing');
                        newErrors.push(`Row ${originalRowNumber}: CustomerID missing.`);
                    }
                    if (!itemCode) {
                        rowErrors.push('ItemCode missing');
                        newErrors.push(`Row ${originalRowNumber}: ItemCode missing.`);
                    }

                    // Lookups
                    const itemName = getItemName(itemCode);
                    const parentName = getParentName(itemCode);
                    const parentItemId = getParentId(itemCode);

                    // Customer-Area mapping validation
                    let mappingStatus = 'unknown';
                    let needsMapping = false;
                    if (areaId && customerId && rebateSchemeCustomerData.length > 0) {
                        const customerMappings = rebateSchemeCustomerData.filter(item =>
                            item.CustomerId === customerId
                        );

                        if (customerMappings.length === 0) {
                            mappingStatus = 'unmapped';
                            needsMapping = true;
                            rowErrors.push('Customer needs mapping');
                            newErrors.push(`Row ${originalRowNumber}: Customer ${customerId} is not mapped to any area. Needs mapping.`);
                        } else {
                            const correctAreaMapping = customerMappings.find(item =>
                                item.SFAreaId === areaId
                            );

                            if (correctAreaMapping) {
                                mappingStatus = 'correctly_mapped';
                            } else {
                                const mappedAreas = customerMappings.map(item => item.SFAreaId).join(', ');
                                mappingStatus = 'incorrectly_mapped';
                                rowErrors.push('Area mapping error');
                                newErrors.push(`Row ${originalRowNumber}: Customer ${customerId} is mapped to area(s): ${mappedAreas}, but Excel shows AreaCode: ${areaCodeRaw} (sfAreaId: ${areaId}).`);
                            }
                        }
                    }

                    // Process Rates for this Row
                    const rowData = {
                        rowNumber: originalRowNumber,
                        AreaCode: areaCodeRaw,
                        AreaID: areaId,
                        CustomerID: customerId,
                        ParentItemId: parentItemId,
                        ItemCode: itemCode,
                        ItemName: itemName,
                        ParentName: parentName,
                        RegularRate: null, RegularQty: null,
                        BulkRate: null, BulkQty: null,
                        VolumeRate: null, VolumeQty: null,
                        hasError: false,
                        errors: [],
                        mappingStatus: mappingStatus,
                        needsMapping: needsMapping
                    };

                    let hasAtLeastOneRate = false;

                    Object.entries(rateTypeMap).forEach(([rateHeader, typeInfo]) => {
                        const rateIndex = headers.indexOf(rateHeader);
                        if (rateIndex === -1) return;

                        const rateValue = row[rateIndex];

                        if (rateValue !== undefined && rateValue !== null && String(rateValue).trim() !== '') {
                            const rate = Number(rateValue);
                            let qty = 0;

                            if (isNaN(rate) || rate < 0) {
                                rowErrors.push(`Invalid ${typeInfo.name} Rate`);
                                newErrors.push(`Row ${originalRowNumber}: Invalid ${typeInfo.name} Rate - cannot be negative.`);
                            } else {
                                hasAtLeastOneRate = true;

                                // Lookup Qty (using getQualifyQty logic which might need verification if exists in this file, or we rely on the implementation plan's assumption)
                                // Assuming getQualifyQty is available or we need to implement it. Checking context...
                                // Since NewCustomerWise uses it, and we are syncing, we should ideally have it.
                                // If getQualifyQty is missing in HorecaCustomerwise, this will break.
                                // NOTE: I will use a safe access or local implementation if needed.
                                // Let's try to assume getQualifyQty is NOT in HorecaCustomerwise as I didn't see it in my targeted read.
                                // I will use the logic from NewCustomerWise:
                                const getQualifyQtyLocal = (iCode, aId, rType) => {
                                    if (!bulkRates || !bulkRates.RateSchemeUpdate) return 0;
                                    for (const parentItem of bulkRates.RateSchemeUpdate) {
                                        if (parentItem.childlist) {
                                            for (const childItem of parentItem.childlist) {
                                                if (String(childItem.ChildItemId) === String(iCode)) {
                                                    if (childItem.rates) {
                                                        for (const r of childItem.rates) {
                                                            if (r.AreaId === aId && r.BulkRateType === rType) {
                                                                return r.QualifyQty || 0;
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    return 0;
                                };

                                const lookedUpQty = getQualifyQtyLocal(itemCode, areaId, typeInfo.type);
                                qty = lookedUpQty;


                                if (qty === 0 && rate > 0) {
                                    rowErrors.push(`${typeInfo.name} Qty Mismatch`);
                                }
                                if (rate === 0 && qty > 0) {
                                    rowErrors.push(`${typeInfo.name} Rate Mismatch`);
                                }

                                // Assign to rowData
                                if (typeInfo.name === 'Regular') {
                                    rowData.RegularRate = parseFloat(rate.toFixed(2));
                                    rowData.RegularQty = qty;
                                } else if (typeInfo.name === 'Bulk') {
                                    rowData.BulkRate = parseFloat(rate.toFixed(2));
                                    rowData.BulkQty = qty;
                                } else if (typeInfo.name === 'Volume') {
                                    rowData.VolumeRate = parseFloat(rate.toFixed(2));
                                    rowData.VolumeQty = qty;
                                }
                            }
                        }
                    });

                    if (rowErrors.length > 0) {
                        newRowErrors[originalRowNumber] = rowErrors;
                        rowData.hasError = true;
                        rowData.errors = rowErrors;
                    }

                    processedData.push(rowData);
                });

                // Rate consistency validation:
                // Same CustomerID + AreaID + ParentItemId must have identical rates per rate type
                const parentRateMap = new Map();
                processedData.forEach(row => {
                    if (!row.ParentItemId || !row.CustomerID || !row.AreaID) return;
                    const groupKey = `${row.CustomerID}-${row.AreaID}-${row.ParentItemId}`;
                    if (!parentRateMap.has(groupKey)) parentRateMap.set(groupKey, new Map());
                    const ratesMap = parentRateMap.get(groupKey);

                    [['RegularRate', 'Regular'], ['BulkRate', 'Bulk'], ['VolumeRate', 'Volume']].forEach(([field, label]) => {
                        const rateVal = row[field];
                        if (rateVal === null || rateVal === undefined) return;

                        if (ratesMap.has(field)) {
                            const existing = ratesMap.get(field);
                            if (Math.abs(existing.rate - rateVal) > 0.01) {
                                const errMsg = `${label}Rate mismatch for ParentItemId ${row.ParentItemId} (Customer ${row.CustomerID}, Area ${row.AreaID}): Row ${existing.rowNumber} (ItemCode ${existing.itemCode}) has ${existing.rate}, Row ${row.rowNumber} (ItemCode ${row.ItemCode}) has ${rateVal}.`;
                                newErrors.push(`Row ${row.rowNumber}: ${errMsg}`);
                                newErrors.push(`Row ${existing.rowNumber}: ${errMsg}`);

                                const errLabel = `${label}Rate inconsistency (ParentItemId ${row.ParentItemId})`;

                                newRowErrors[row.rowNumber] = [...(newRowErrors[row.rowNumber] || []), errLabel];
                                row.hasError = true;
                                row.errors = [...(row.errors || []), errLabel];

                                newRowErrors[existing.rowNumber] = [...(newRowErrors[existing.rowNumber] || []), errLabel];
                                const firstRow = processedData.find(r => r.rowNumber === existing.rowNumber);
                                if (firstRow) {
                                    firstRow.hasError = true;
                                    firstRow.errors = [...(firstRow.errors || []), errLabel];
                                }
                            }
                        } else {
                            ratesMap.set(field, { rate: rateVal, rowNumber: row.rowNumber, itemCode: row.ItemCode });
                        }
                    });
                });

                setMasterExcelData(processedData);
                setMasterValidationErrors(newErrors);
                setMasterRowErrors(newRowErrors);

                // Collect unique unmapped customers
                const customerMap = new Map();
                processedData.forEach(row => {
                    if (row.needsMapping && !customerMap.has(row.CustomerID)) {
                        customerMap.set(row.CustomerID, {
                            CustomerID: row.CustomerID,
                            AreaID: row.AreaID,
                            rowNumbers: [row.rowNumber]
                        });
                    } else if (row.needsMapping) {
                        const entry = customerMap.get(row.CustomerID);
                        if (!entry.rowNumbers.includes(row.rowNumber)) {
                            entry.rowNumbers.push(row.rowNumber);
                        }
                    }
                });
                setMasterUnmappedCustomers(Array.from(customerMap.values()));

                if (newErrors.length > 0) {
                    const unmappedCount = Array.from(customerMap.values()).length;
                    if (unmappedCount > 0) {
                        showToast(`Validation found ${newErrors.length} errors. ${unmappedCount} customers need mapping.`, 'warning');
                    } else {
                        showToast(`Validation found ${newErrors.length} errors.`, 'warning');
                    }
                } else {
                    showToast('Excel validated successfully!', 'success');
                }

            } catch (err) {
                handleApiError('Error parsing Excel: ' + err.message);
            } finally {
                setIsMasterExcelProcessing(false);
            }
        };
        reader.readAsArrayBuffer(masterExcelFile);
    };

    const handleDownloadMasterSample = () => {
        // Build a reverse map: sfAreaId → AreaCode for use in sample generation
        const areaIdToCodeMap = {};
        areas.forEach(area => {
            if (area.AreaId && area.AreaCode) areaIdToCodeMap[area.AreaId] = area.AreaCode;
        });

        let sampleData = [];

        if (bulkRates && bulkRates.RateSchemeUpdate && bulkRates.RateSchemeUpdate.length > 0) {
            bulkRates.RateSchemeUpdate.forEach(parent => {
                if (parent.childlist) {
                    parent.childlist.forEach(child => {
                        const areaIds = new Set();
                        if (child.rates && child.rates.length > 0) {
                            child.rates.forEach(r => {
                                if (r.AreaId) areaIds.add(r.AreaId);
                            });
                        }

                        if (areaIds.size > 0) {
                            areaIds.forEach(areaId => {
                                const areaCode = areaIdToCodeMap[areaId] || areaId;
                                sampleData.push({
                                    AreaCode: areaCode,
                                    CustomerID: '',
                                    ParentItemId: parent.ParentItemId || '',
                                    ParentName: parent.ParentName || '',
                                    ItemCode: child.ChildItemId || '',
                                    ItemName: child.chlidname || '',
                                    RegularRate: '',
                                    BulkRate: '',
                                    VolumeRate: ''
                                });
                            });
                        } else {
                            sampleData.push({
                                AreaCode: '',
                                CustomerID: '',
                                ParentItemId: parent.ParentItemId || '',
                                ParentName: parent.ParentName || '',
                                ItemCode: child.ChildItemId || '',
                                ItemName: child.chlidname || '',
                                RegularRate: '',
                                BulkRate: '',
                                VolumeRate: ''
                            });
                        }
                    });
                }
            });
        }

        if (sampleData.length === 0) {
            sampleData = [
                {
                    AreaCode: 'SAMPLE_CODE',
                    CustomerID: 'CUST1001',
                    ParentItemId: '20000001',
                    ParentName: 'Sample Parent',
                    ItemCode: '1001',
                    ItemName: 'Sample Item',
                    RegularRate: 100,
                    BulkRate: 95,
                    VolumeRate: 90
                }
            ];
        }

        const worksheet = XLSX.utils.json_to_sheet(sampleData);

        const wscols = [
            { wch: 15 }, // AreaCode
            { wch: 15 }, // CustomerID
            { wch: 15 }, // ParentItemId
            { wch: 30 }, // ParentName
            { wch: 15 }, // ItemCode
            { wch: 40 }, // ItemName
            { wch: 12 }, // RegularRate
            { wch: 12 }, // BulkRate
            { wch: 12 }  // VolumeRate
        ];
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'MasterRateUpload');
        XLSX.writeFile(workbook, 'HorecaMasterRateUpload_Template.xlsx');
    };

    // Master mapping functions
    const handleMasterCustomerMapping = async (customerInfo) => {
        try {
            showToast(`Mapping customer ${customerInfo.CustomerID} to area ${customerInfo.AreaID}...`, 'info');

            const response = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    option: "I",
                    Areaid: customerInfo.AreaID,
                    CustomerId: customerInfo.CustomerID,
                    PricingSchemeType: "HORECA",
                    CreditType: "CR",
                    CreditLimitProposed: 0.00,
                    CreatedUserId: userId
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to map customer: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Mapping result for ${customerInfo.CustomerID}:`, result);

            // Update rebate scheme customer data to include the new mapping
            setRebateSchemeCustomerData(prev => [
                ...prev,
                {
                    SFAreaId: customerInfo.AreaID,
                    CustomerId: customerInfo.CustomerID,
                    PricingSchemeType: "HORECA",
                    CreditType: "CR",
                    RebateSchemeId: null,
                    RebateSchemeName: null,
                    CustomerName: `Customer-${customerInfo.CustomerID}`,
                    Mobile: "0000000000",
                    CreatedDate: new Date().toISOString()
                }
            ]);

            // Remove from unmapped customers
            setMasterUnmappedCustomers(prev =>
                prev.filter(c => c.CustomerID !== customerInfo.CustomerID)
            );

            // Update Excel data to reflect correct mapping
            setMasterExcelData(prev => prev.map(row => {
                if (row.CustomerID === customerInfo.CustomerID && row.AreaID === customerInfo.AreaID) {
                    const updatedRow = {
                        ...row,
                        mappingStatus: 'correctly_mapped',
                        needsMapping: false,
                        hasError: row.errors.filter(err => err !== 'Customer needs mapping').length > 0,
                        errors: row.errors.filter(err => err !== 'Customer needs mapping')
                    };
                    return updatedRow;
                }
                return row;
            }));

            // Update validation errors
            setMasterValidationErrors(prev =>
                prev.filter(err => !err.includes(`Customer ${customerInfo.CustomerID}`) || !err.includes('not mapped to any area'))
            );

            showToast(`Successfully mapped customer ${customerInfo.CustomerID} to area ${customerInfo.AreaID}`, 'success');

        } catch (error) {
            console.error('Error mapping customer:', error);
            showToast(`Failed to map customer ${customerInfo.CustomerID}: ${error.message}`, 'error');
        }
    };

    const handleBulkMasterMapping = async () => {
        if (masterUnmappedCustomers.length === 0) return;

        showToast('Starting bulk customer mapping...', 'info');
        let successCount = 0;

        for (const customerInfo of masterUnmappedCustomers) {
            try {
                await handleMasterCustomerMapping(customerInfo);
                successCount++;
            } catch (error) {
                console.error(`Failed to map customer ${customerInfo.CustomerID}:`, error);
            }
        }

        if (successCount === masterUnmappedCustomers.length) {
            showToast(`Successfully mapped all ${successCount} customers`, 'success');
            setShowMasterMappingDialog(false);
        } else {
            showToast(`Mapped ${successCount} out of ${masterUnmappedCustomers.length} customers`, 'warning');
        }
    };

    const handleMasterUpload = async () => {
        if (masterValidationErrors.length > 0 || masterExcelData.length === 0) {
            showToast('Please resolve validation errors before uploading.', 'error');
            return;
        }

        setIsMasterUploading(true);
        setIsApiChainProcessing(true);
        setApiChainMessage('Processing master upload data...');
        try {
            // Group data by AreaID first, then by CustomerID
            const groupedData = {};

            masterExcelData.forEach(row => {
                const areaId = String(row.AreaID || '').trim();
                const customerId = String(row.CustomerID || '').trim();

                if (!areaId || !customerId) return;

                if (!groupedData[areaId]) {
                    groupedData[areaId] = {};
                }

                if (!groupedData[areaId][customerId]) {
                    groupedData[areaId][customerId] = [];
                }

                // Extract rates from the row (Matrix structure)
                const ratesToAdd = [];

                if (row.RegularRate !== null && row.RegularRate !== undefined) {
                    ratesToAdd.push({
                        itemCode: String(row.ItemCode || ''),
                        qualifyQty: Number(row.RegularQty) || 0,
                        rate: Number(row.RegularRate) || 0,
                        dml: "I"
                    });
                }

                if (row.BulkRate !== null && row.BulkRate !== undefined) {
                    ratesToAdd.push({
                        itemCode: String(row.ItemCode || ''),
                        qualifyQty: Number(row.BulkQty) || 0,
                        rate: Number(row.BulkRate) || 0,
                        dml: "I"
                    });
                }

                if (row.VolumeRate !== null && row.VolumeRate !== undefined) {
                    ratesToAdd.push({
                        itemCode: String(row.ItemCode || ''),
                        qualifyQty: Number(row.VolumeQty) || 0,
                        rate: Number(row.VolumeRate) || 0,
                        dml: "I"
                    });
                }

                if (ratesToAdd.length > 0) {
                    groupedData[areaId][customerId].push(...ratesToAdd);
                }
            });

            const apiErrors = [];
            let successCount = 0;

            // Make API calls for each area and customer combination
            for (const areaId of Object.keys(groupedData)) {
                for (const customerId of Object.keys(groupedData[areaId])) {
                    const rateDetails = groupedData[areaId][customerId];

                    try {
                        const requestBody = {
                            uniqueId: "",
                            rebateSchemeType: 'CUST',
                            areaId: areaId,
                            rebateSchemeId: '',
                            customerId: customerId,
                            dateOfEntry: (() => {
                                const now = new Date();
                                const istOffset = 5.5 * 60 * 60 * 1000;
                                const istDate = new Date(now.getTime() + istOffset);
                                return istDate.toISOString();
                            })(),
                            createdUserCode: userId,
                            ratedetail: rateDetails
                        };

                        // Log Master Upload API request details
                        const apiUrl = `${local_url}/api/CustomerRate`;
                        const apiPayload = JSON.stringify(requestBody);

                        console.log('═══════════════════════════════════════════════════════════════');
                        console.log(`📤 MASTER UPLOAD POST API REQUEST - ${new Date().toISOString()}`);
                        console.log('═══════════════════════════════════════════════════════════════');
                        console.log('Area ID:', areaId);
                        console.log('Customer ID:', customerId);
                        console.log('URL:', apiUrl);
                        console.log('Request Body:', JSON.parse(apiPayload));
                        console.log('═══════════════════════════════════════════════════════════════');

                        const response = await fetch(apiUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: apiPayload
                        });

                        if (!response.ok) {
                            const errorData = await response.text();
                            console.error('❌ MASTER UPLOAD API ERROR RESPONSE:', errorData);
                            apiErrors.push(`Area ${areaId}, Customer ${customerId}: ${errorData}`);
                        } else {
                            const result = await response.json();
                            console.log('✅ MASTER UPLOAD POST API RESPONSE:', result);

                            if (result?.result === 0) {
                                const errMsg = result?.description
                                    ? result.description.replace(/^Fail/, '').trim()
                                    : 'Upload failed';
                                apiErrors.push(`Area ${areaId}, Customer ${customerId}: ${errMsg}`);
                            } else {

                            successCount++;
                            }  // closes result?.result === 0 else
                        }

                    } catch (error) {
                        apiErrors.push(`Area ${areaId}, Customer ${customerId}: ${error.message}`);
                    }
                }
            }

            if (apiErrors.length > 0) {
                setApiChainMessage(`Upload completed with ${successCount} successes and ${apiErrors.length} errors`);
                showToast(`Upload completed with ${successCount} successes and ${apiErrors.length} errors. Check details.`, 'warning');
                console.log('API Errors:', apiErrors);
            } else {
                setApiChainMessage(`Master upload successful! ${successCount} combinations processed.`);
                showToast(`Master rate data uploaded successfully! ${successCount} customer-area combinations processed.`, 'success');
            }

            // Reset master state after upload attempt
            setMasterExcelFile(null);
            setMasterExcelFileName('');
            setMasterExcelData([]);
            setMasterValidationErrors([]);
            setMasterRowErrors({});

        } catch (err) {
            setApiChainMessage('Upload error occurred');
            handleApiError(`Upload error: ${err.message}`);
        } finally {
            setIsMasterUploading(false);
            // Hide loader after 2 seconds to show final message
            setTimeout(() => {
                setIsApiChainProcessing(false);
                setApiChainMessage('');
            }, 2000);
        }
    };

    // Fetches one customer's rate line-items and returns rows ready for the Excel sheet
    const buildCustomerRateRows = async (c) => {
        const rows = [];
        const baseInfo = {
            'Customer ID': c.CustomerId || '-',
            'Mobile': c.Mobile || '-',
            'Customer Name': c.CustomerName || '-',
            'Scheme ID': c.RebateSchemeId || '-',
            'Scheme Name': c.RebateSchemeName || '-',
        };
        const emptyRateRow = { 'Item Code': '-', 'Item Name': '-', 'Qualify Qty': '-', 'Rate': '-', 'Valid Till': '-' };

        if (!c.ItemCount) {
            rows.push({ ...baseInfo, ...emptyRateRow });
            return rows;
        }

        try {
            const response = await fetch(
                `${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${resolveCustomerAreaId(c)}&CustomerID=${c.CustomerId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = response.ok ? await response.json() : [];

            if (data.length > 0) {
                data.forEach(item => {
                    rows.push({
                        ...baseInfo,
                        'Item Code': item.ItemCode || '-',
                        'Item Name': item.ItemName || '-',
                        'Qualify Qty': item.QualifyQty ?? '-',
                        'Rate': item.Rate ?? '-',
                        'Valid Till': formatYyyymmdd(item.validtill || item.ValidTill),
                    });
                });
            } else {
                rows.push({ ...baseInfo, ...emptyRateRow });
            }
        } catch (err) {
            console.error(`Failed to fetch rates for ${c.CustomerId}:`, err);
            rows.push({ ...baseInfo, ...emptyRateRow });
        }

        return rows;
    };

    const writeCustomerRatesWorkbook = (rows, filename) => {
        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet['!cols'] = [
            { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 25 },
            { wch: 14 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Rates');
        XLSX.writeFile(workbook, filename);
    };

    const exportExistingCustomersToExcel = async () => {
        if (filteredExistingCustomers.length === 0) {
            showToast('No customer data to export', 'warning');
            return;
        }

        setIsExportingCustomers(true);
        showToast('Preparing export — fetching rate details...', 'info');

        try {
            const rows = [];
            for (const c of filteredExistingCustomers) {
                rows.push(...(await buildCustomerRateRows(c)));
            }

            const areaLabel = existingUserAreas.find(a => a.AreaId === selectedExistingArea)?.AreaName || 'All';
            writeCustomerRatesWorkbook(rows, `Existing_Customers_Rates_${areaLabel}_${new Date().toISOString().split('T')[0]}.xlsx`);
            showToast(`Exported rate details for ${filteredExistingCustomers.length} customer(s)!`, 'success');
        } catch (err) {
            console.error('Export error:', err);
            showToast('Failed to export customer data', 'error');
        } finally {
            setIsExportingCustomers(false);
        }
    };

    const exportSingleCustomerToExcel = async (customer) => {
        setExportingCustomerId(customer.CustomerId);
        try {
            const rows = await buildCustomerRateRows(customer);
            writeCustomerRatesWorkbook(rows, `Customer_${customer.CustomerId}_Rates_${new Date().toISOString().split('T')[0]}.xlsx`);
            showToast(`Exported rate details for ${customer.CustomerName}!`, 'success');
        } catch (err) {
            console.error('Export error:', err);
            showToast('Failed to export customer data', 'error');
        } finally {
            setExportingCustomerId(null);
        }
    };

    // Filtered customers for the customer list
    const filteredExistingCustomers = existingCustomers.filter(c => {
        const matchesSearch = (c.CustomerId || '').toLowerCase().includes(customerListSearch.toLowerCase()) ||
            (c.CustomerName || '').toLowerCase().includes(customerListSearch.toLowerCase()) ||
            (c.Mobile || '').toLowerCase().includes(customerListSearch.toLowerCase());

        const isActive = (c.ItemCount ?? 0) > 0;
        const matchesStatus = globalSearchInput.trim()
            ? true
            : (showInactiveCustomers ? !isActive : isActive);

        return matchesSearch && matchesStatus;
    }).sort((a, b) => {
        // Sort by RebateSchemeName, with customers without schemes at the end
        const aScheme = a.RebateSchemeName || '';
        const bScheme = b.RebateSchemeName || '';

        // If both have schemes, sort alphabetically by scheme name
        if (aScheme && bScheme) {
            return aScheme.localeCompare(bScheme);
        }

        // If only one has a scheme, put the one with scheme first
        if (aScheme && !bScheme) return -1;
        if (!aScheme && bScheme) return 1;

        // If neither has a scheme, sort by customer name
        return (a.CustomerName || '').localeCompare(b.CustomerName || '');
    });

    // Calculate customers with and without schemes
    const customersWithSchemes = filteredExistingCustomers.filter(c => c.RebateSchemeName && c.RebateSchemeName.trim() !== '').length;
    const customersWithoutSchemes = filteredExistingCustomers.length - customersWithSchemes;

    // Filtered rate details for the table
    const filteredCustomerDetailsData = customerDetailsData.filter(row =>
        (row.itemName || '').toLowerCase().includes(rateTableSearch.toLowerCase()) ||
        (row.itemCode || '').toLowerCase().includes(rateTableSearch.toLowerCase())
    );

    // Filtered rows for the Area Rate Upload table
    const filteredAreaRebateSchemeData = areaRebateSchemeData.filter(row =>
        (row.SFAreaId || '').toLowerCase().includes(areaRateSearch.toLowerCase()) ||
        (row.SFAreaName || '').toLowerCase().includes(areaRateSearch.toLowerCase()) ||
        (row.RebateSchemeId || '').toLowerCase().includes(areaRateSearch.toLowerCase()) ||
        (row.RebateSchemeName || '').toLowerCase().includes(areaRateSearch.toLowerCase())
    );

    // --- Main Render ---
    return (
        <>
            <Header />
            <Box sx={{
                width: '100vw',
                height: '90vh',
                background: colors.backgroundAccent,
                overflow: 'auto',
                p: 0.5,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Container maxWidth="xl" sx={{ p: '0 !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Paper elevation={0} sx={{
                        ...glassmorphismStyle,
                        p: 1,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        background: colors.backgroundCard,
                        border: `1px solid ${colors.border}`,
                        boxShadow: colors.shadowXl
                    }}>
                        {/* Fixed Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonSearchIcon sx={{ mr: 1.5, color: colors.primary, fontSize: '28px' }} />
                                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary }}>
                                    {userFilterType === 'area-wise'
                                        ? 'Area Rate Upload'
                                        : 'Customer Rate Upload'}
                                </Typography>
                            </Box>

                            {/* User Filter Toggle Buttons */}
                            <Box sx={{
                                display: 'flex',
                                gap: 0.5,
                                p: 0.5,
                                background: colors.backgroundGray,
                                borderRadius: '12px',
                                border: `1px solid ${colors.border}`,
                                boxShadow: 'inset 0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <Button
                                    onClick={() => {
                                        setUserFilterType('existing');
                                        setActiveView('customer');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: userFilterType === 'existing' ? colors.primaryGradient : 'transparent',
                                        color: userFilterType === 'existing' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: userFilterType === 'existing' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: userFilterType === 'existing'
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: userFilterType === 'existing' ? colors.white : colors.white,
                                            transform: userFilterType === 'existing' ? 'none' : 'translateY(-1px)',
                                            boxShadow: userFilterType === 'existing' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    Customer Rate Upload
                                </Button>
                                <Button
                                    onClick={() => {
                                        setUserFilterType('area-wise');
                                        setActiveView('customer');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: userFilterType === 'area-wise' ? colors.primaryGradient : 'transparent',
                                        color: userFilterType === 'area-wise' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: userFilterType === 'area-wise' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: userFilterType === 'area-wise'
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: colors.white,
                                            transform: userFilterType === 'area-wise' ? 'none' : 'translateY(-1px)',
                                            boxShadow: userFilterType === 'area-wise' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    Area Rate Upload
                                </Button>
                                {/* <Button
                                    onClick={() => {
                                        setUserFilterType('multiple');
                                        setActiveView('customer');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: userFilterType === 'multiple' ? colors.primaryGradient : 'transparent',
                                        color: userFilterType === 'multiple' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: userFilterType === 'multiple' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: userFilterType === 'multiple'
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: userFilterType === 'multiple' ? colors.white : colors.white,
                                            transform: userFilterType === 'multiple' ? 'none' : 'translateY(-1px)',
                                            boxShadow: userFilterType === 'multiple' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    Multiple User
                                </Button>
                                <Button
                                    onClick={() => {
                                        setActiveView('master');
                                        setUserFilterType('master'); // Set this to distinguish from customer views
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: activeView === 'master' ? colors.primaryGradient : 'transparent',
                                        color: activeView === 'master' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: activeView === 'master' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: activeView === 'master'
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: activeView === 'master' ? colors.white : colors.white,
                                            transform: activeView === 'master' ? 'none' : 'translateY(-1px)',
                                            boxShadow: activeView === 'master' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    Master Upload
                                </Button> */}
                            </Box>
                        </Box>

                        {userFilterType === 'existing' && (
                            <>
                                {/* Existing User - Compact Area Selection and Mode Buttons */}
                                <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                    <Grid container spacing={1.5} alignItems="center">
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="select-area-existing-label" sx={{ fontSize: '0.8rem' }}>Select Area</InputLabel>
                                                <Select
                                                    labelId="select-area-existing-label"
                                                    value={selectedExistingArea}
                                                    label="Select Area"
                                                    onChange={(e) => {
                                                        setSelectedExistingArea(e.target.value);
                                                        fetchExistingCustomersByArea(e.target.value);
                                                        setSelectedExistingCustomer(null);
                                                        setCustomerDetailsData([]);
                                                    }}
                                                    disabled={loadingAreas}
                                                    sx={{
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem key="ALL" value="ALL" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                        ALL
                                                    </MenuItem>
                                                    {existingUserAreas.map((area) => (
                                                        <MenuItem key={area.AreaId} value={area.AreaId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {area.AreaName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {loadingExistingCustomers && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.5,
                                                background: colors.backgroundGray,
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                px: 1, py: '1px',
                                                '&:focus-within': { borderColor: colors.primary }
                                            }}>
                                                <SearchIcon sx={{ color: colors.textTertiary, fontSize: '15px', flexShrink: 0 }} />
                                                <TextField
                                                    variant="standard" fullWidth
                                                    placeholder="Search by Customer ID/Name/Mobile"
                                                    value={globalSearchInput}
                                                    onChange={(e) => setGlobalSearchInput(e.target.value)}
                                                    onKeyPress={handleGlobalSearchKeyPress}
                                                    InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', color: colors.textPrimary, '& input::placeholder': { color: colors.textTertiary, opacity: 1 } } }}
                                                />
                                                {globalSearchInput && (
                                                    <IconButton size="small" onClick={handleCloseGlobalSearch} sx={{ p: '1px', color: colors.textTertiary, '&:hover': { color: colors.error } }}>
                                                        <CloseIcon sx={{ fontSize: '12px' }} />
                                                    </IconButton>
                                                )}
                                                <IconButton onClick={handleGlobalSearch} disabled={loadingExistingCustomers}
                                                    sx={{ background: colors.primaryGradient, color: colors.white, borderRadius: '4px', p: '2px 6px', '&:hover': { opacity: 0.9 }, '&:disabled': { opacity: 0.5 } }}
                                                >
                                                    {loadingExistingCustomers ? <CircularProgress size={12} color="inherit" /> : <SearchIcon sx={{ fontSize: '14px' }} />}
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <Typography variant="body2" sx={{
                                                color: colors.textSecondary,
                                                fontSize: '0.75rem',
                                                textAlign: { xs: 'left', sm: 'center' },
                                                lineHeight: 1.2
                                            }}>
                                                {selectedExistingArea
                                                    ? `${customersWithSchemes} with schemes, ${customersWithoutSchemes} without schemes`
                                                    : 'Select area first'
                                                }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={4.5} />
                                    </Grid>
                                </Box>

                                {/* Customer List Header */}
                                {selectedExistingArea && (
                                    <Box sx={{ mb: 2 }}>

                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1" sx={{
                                                    color: colors.textPrimary,
                                                    fontSize: '0.9rem',
                                                    fontWeight: 600
                                                }}>
                                                    {existingUserMode === 'new-rate' ? 'Select Customer for New Rate' : 'Select Customers to Delete'}
                                                </Typography>
                                                {existingUserMode === 'delete-scheme' && selectedCustomersForDelete.length > 0 && (
                                                    <Chip
                                                        label={`${selectedCustomersForDelete.length} Selected`}
                                                        size="small"
                                                        sx={{
                                                            height: '22px',
                                                            fontSize: '0.7rem',
                                                            backgroundColor: colors.error,
                                                            color: 'white'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Button
                                                    onClick={() => {
                                                        setShowNewCustomerInExisting(prev => {
                                                            const next = !prev;
                                                            if (next && selectedExistingArea) setSelectedArea(selectedExistingArea);
                                                            return next;
                                                        });
                                                    }}
                                                    variant="outlined"
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                        px: 2,
                                                        py: 0.6,
                                                        borderRadius: '8px',
                                                        minWidth: 'auto',
                                                        borderColor: colors.primary,
                                                        color: colors.primary,
                                                        '&:hover': {
                                                            borderColor: colors.primaryDark,
                                                            backgroundColor: `${colors.primary}10`,
                                                        }
                                                    }}
                                                >
                                                    {showNewCustomerInExisting ? 'Close Map New Customer' : 'Map New Customer'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setShowBulkRateUploadInExisting(prev => !prev)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                        px: 2,
                                                        py: 0.6,
                                                        borderRadius: '8px',
                                                        minWidth: 'auto',
                                                        borderColor: colors.primary,
                                                        color: colors.primary,
                                                        '&:hover': {
                                                            borderColor: colors.primaryDark,
                                                            backgroundColor: `${colors.primary}10`,
                                                        }
                                                    }}
                                                >
                                                    {showBulkRateUploadInExisting ? 'Close Bulk Customer Rate Upload' : 'Bulk Customer Rate Upload'}
                                                </Button>
                                                {existingCustomers.length > 0 && (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={exportExistingCustomersToExcel}
                                                            disabled={isExportingCustomers}
                                                            startIcon={isExportingCustomers ? <CircularProgress size={14} /> : <DownloadIcon sx={{ fontSize: '16px' }} />}
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                px: 2,
                                                                py: 0.6,
                                                                borderRadius: '8px',
                                                                minWidth: 'auto',
                                                                borderColor: colors.primary,
                                                                color: colors.primary,
                                                                '&:hover': {
                                                                    borderColor: colors.primaryDark,
                                                                    backgroundColor: `${colors.primary}10`,
                                                                }
                                                            }}
                                                        >
                                                            {isExportingCustomers ? 'Exporting...' : 'Export All'}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant={existingUserMode === 'delete-scheme' ? 'contained' : 'outlined'}
                                                            color="error"
                                                            onClick={() => {
                                                                setExistingUserMode(existingUserMode === 'delete-scheme' ? 'new-rate' : 'delete-scheme');
                                                                setSelectedCustomersForDelete([]);
                                                            }}
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                textTransform: 'none',
                                                                py: 0.3,
                                                                px: 1.2,
                                                                minHeight: 'auto'
                                                            }}
                                                        >
                                                            {existingUserMode === 'delete-scheme' ? 'Cancel' : 'Delete'}
                                                        </Button>
                                                        {existingUserMode === 'delete-scheme' && selectedCustomersForDelete.length > 0 && (
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                size="small"
                                                                onClick={handleDeleteSelectedCustomers}
                                                                disabled={isDeleting}
                                                                startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : <CloseIcon sx={{ fontSize: '16px' }} />}
                                                                sx={{
                                                                    backgroundColor: colors.error,
                                                                    '&:hover': { backgroundColor: colors.errorDark },
                                                                    fontSize: '0.7rem',
                                                                    textTransform: 'none',
                                                                    py: 0.3,
                                                                    px: 1.2,
                                                                    minHeight: 'auto'
                                                                }}
                                                            >
                                                                {isDeleting
                                                                    ? 'Deleting...'
                                                                    : `Delete Schemes (${selectedCustomersForDelete.length})`
                                                                }
                                                            </Button>
                                                        )}
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={showInactiveCustomers}
                                                                    onChange={(e) => setShowInactiveCustomers(e.target.checked)}
                                                                />
                                                            }
                                                            label="Show Inactive"
                                                            sx={{ mr: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            placeholder="Search by ID/ Name/ Mobile"
                                                            value={customerListSearch}
                                                            onChange={e => setCustomerListSearch(e.target.value)}
                                                            sx={{
                                                                width: { xs: '100%', sm: '220px' },
                                                                background: colors.backgroundGray,
                                                                borderRadius: 2,
                                                                '& .MuiInputBase-root': {
                                                                    height: '32px',
                                                                    fontSize: '0.8rem'
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    padding: '6px 8px'
                                                                }
                                                            }}
                                                            InputProps={{
                                                                startAdornment: <SearchIcon sx={{ mr: 1, color: colors.textSecondary, fontSize: '16px' }} />
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            </Box>
                                        </Box>

                                        <Dialog
                                            open={showNewCustomerInExisting}
                                            onClose={() => setShowNewCustomerInExisting(false)}
                                            maxWidth="lg"
                                            fullWidth
                                        >
                                            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                New Customer
                                                <IconButton size="small" onClick={() => setShowNewCustomerInExisting(false)}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </DialogTitle>
                                            <DialogContent dividers>
                                                <Grid container spacing={1.5}>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel sx={{ fontSize: '0.8rem' }}>Area</InputLabel>
                                                            <Select
                                                                value={selectedArea}
                                                                label="Area"
                                                                onChange={(e) => setSelectedArea(e.target.value)}
                                                                disabled={loadingAreas}
                                                                sx={{
                                                                    height: '32px',
                                                                    fontSize: '0.8rem',
                                                                    '& .MuiSelect-select': {
                                                                        padding: '6px 8px',
                                                                        fontSize: '0.8rem'
                                                                    }
                                                                }}
                                                            >
                                                                {areas.map((area) => (
                                                                    <MenuItem key={area.AreaId} value={area.AreaId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                                        {area.AreaName}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel sx={{ fontSize: '0.8rem' }}>Customer Type</InputLabel>
                                                            <Select
                                                                value={selectedCustomerType}
                                                                label="Customer Type"
                                                                onChange={(e) => setSelectedCustomerType(e.target.value)}
                                                                disabled={loadingCustomerTypes}
                                                                sx={{
                                                                    height: '32px',
                                                                    fontSize: '0.8rem',
                                                                    '& .MuiSelect-select': {
                                                                        padding: '6px 8px',
                                                                        fontSize: '0.8rem'
                                                                    }
                                                                }}
                                                            >
                                                                {customerTypes.map((type) => (
                                                                    <MenuItem key={type.customerTypeCode} value={type.customerTypeCode} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                                        {type.customerTypeName}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <RadioGroup
                                                            row
                                                            value={searchType}
                                                            onChange={(e) => setSearchType(e.target.value)}
                                                            sx={{ justifyContent: 'flex-start', flexWrap: 'nowrap', gap: 0.5 }}
                                                        >
                                                            <FormControlLabel
                                                                value="CUSTCODE"
                                                                control={<Radio size="small" sx={{ py: 0.5 }} />}
                                                                label="Code"
                                                                sx={{ whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                                            />
                                                            <FormControlLabel
                                                                value="MOBILE"
                                                                control={<Radio size="small" sx={{ py: 0.5 }} />}
                                                                label="Mobile"
                                                                sx={{ whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                                            />
                                                            <FormControlLabel
                                                                value="CUSTNAME"
                                                                control={<Radio size="small" sx={{ py: 0.5 }} />}
                                                                label="Name"
                                                                sx={{ whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                                            />
                                                        </RadioGroup>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label={getSearchInputLabel()}
                                                                value={searchInput}
                                                                onChange={(e) => setSearchInput(e.target.value)}
                                                                onKeyPress={handleKeyPress}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: '8px',
                                                                        height: '32px'
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        padding: '6px 8px',
                                                                        fontSize: '0.8rem'
                                                                    },
                                                                    '& .MuiInputLabel-root': {
                                                                        fontSize: '0.8rem'
                                                                    }
                                                                }}
                                                            />
                                                            <Button
                                                                variant="contained"
                                                                onClick={handleCustomerSearch}
                                                                disabled={loadingSearch}
                                                                size="small"
                                                                sx={{
                                                                    minWidth: '32px',
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    p: 0,
                                                                    backgroundColor: colors.primary,
                                                                    '&:hover': { backgroundColor: colors.primaryDark }
                                                                }}
                                                            >
                                                                {loadingSearch ? <CircularProgress size={16} color="inherit" /> : <SearchIcon sx={{ fontSize: '16px' }} />}
                                                            </Button>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </DialogContent>
                                        </Dialog>

                                        {existingCustomers.length > 0 ? (
                                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '520px' }}>
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            {existingUserMode === 'delete-scheme' && (() => {
                                                                const deletableFilteredCustomers = filteredExistingCustomers.filter(c => (c.ItemCount ?? 0) > 0);
                                                                return (
                                                                <TableCell padding="checkbox">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={deletableFilteredCustomers.length > 0 && deletableFilteredCustomers.every(c => selectedCustomersForDelete.some(sc => sc.CustomerId === c.CustomerId))}
                                                                        indeterminate={
                                                                            deletableFilteredCustomers.some(c => selectedCustomersForDelete.some(sc => sc.CustomerId === c.CustomerId)) &&
                                                                            !deletableFilteredCustomers.every(c => selectedCustomersForDelete.some(sc => sc.CustomerId === c.CustomerId))
                                                                        }
                                                                        disabled={deletableFilteredCustomers.length === 0}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedCustomersForDelete(prev => {
                                                                                    const existingIds = new Set(prev.map(c => c.CustomerId));
                                                                                    const toAdd = deletableFilteredCustomers.filter(c => !existingIds.has(c.CustomerId));
                                                                                    return [...prev, ...toAdd];
                                                                                });
                                                                            } else {
                                                                                setSelectedCustomersForDelete(prev => prev.filter(c => !deletableFilteredCustomers.some(fc => fc.CustomerId === c.CustomerId)));
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                );
                                                            })()}
                                                            <TableCell sx={{ fontWeight: 700 }}>Customer ID</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Customer Name</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Area ID</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Area Name</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Scheme ID</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Scheme Name</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Item Count</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>View Rate</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Upload Rate</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Export</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {filteredExistingCustomers.map((customer, index) => {
                                                            const isSelectedNewRate = existingUserMode === 'new-rate' && selectedExistingCustomer?.CustomerId === customer.CustomerId;
                                                            const isSelectedForDelete = existingUserMode === 'delete-scheme' && selectedCustomersForDelete.some(c => c.CustomerId === customer.CustomerId);
                                                            const isSelected = isSelectedNewRate || isSelectedForDelete;
                                                            const isRowActive = (customer.ItemCount ?? 0) > 0;
                                                            return (
                                                                <TableRow
                                                                    key={customer.CustomerId || index}
                                                                    hover
                                                                    onClick={() => handleExistingCustomerSelect(customer)}
                                                                    selected={isSelected}
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        backgroundColor: isSelectedNewRate
                                                                            ? `${colors.success}10`
                                                                            : isSelectedForDelete
                                                                                ? `${colors.error}10`
                                                                                : undefined
                                                                    }}
                                                                >
                                                                    {existingUserMode === 'delete-scheme' && (
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox
                                                                                size="small"
                                                                                checked={isSelectedForDelete}
                                                                                disabled={!(customer.ItemCount ?? 0)}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell sx={{ fontSize: '0.75rem', color: isRowActive ? colors.textSecondary : colors.error }}>
                                                                        {customer.CustomerId}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            icon={<PhoneIcon sx={{ fontSize: '10px' }} />}
                                                                            label={customer.Mobile || 'N/A'}
                                                                            size="small"
                                                                            sx={{ fontSize: '0.7rem', backgroundColor: colors.backgroundGray, height: '20px' }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', color: isRowActive ? undefined : colors.error }}>
                                                                            <PersonOutlineIcon sx={{ mr: 0.5, fontSize: '14px', color: isRowActive ? undefined : colors.error }} />
                                                                            {customer.CustomerName}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                                                                        {customer.SFAreaId || selectedExistingArea || '-'}
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                                                                        {existingUserAreas.find(a => a.AreaId === (customer.SFAreaId || selectedExistingArea))?.AreaName || '-'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={customer.RebateSchemeId || 'NO SCHEME'}
                                                                            size="small"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                fontSize: '0.7rem',
                                                                                color: customer.RebateSchemeId ? colors.primary : colors.error,
                                                                                backgroundColor: customer.RebateSchemeId ? colors.primary + '10' : colors.error + '10',
                                                                                border: `1px solid ${customer.RebateSchemeId ? colors.primary + '30' : colors.error + '30'}`
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {customer.RebateSchemeName ? (
                                                                            <Chip
                                                                                label={customer.RebateSchemeName}
                                                                                size="small"
                                                                                sx={{
                                                                                    fontSize: '0.7rem',
                                                                                    backgroundColor: colors.primary + '15',
                                                                                    color: colors.primary,
                                                                                    height: '20px'
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <Button
                                                                                variant="contained"
                                                                                size="small"
                                                                                onClick={(e) => { e.stopPropagation(); handleMapSchemeClick(customer); }}
                                                                                sx={{
                                                                                    backgroundColor: colors.primary,
                                                                                    color: 'white',
                                                                                    fontSize: '0.65rem',
                                                                                    py: 0.3,
                                                                                    px: 1,
                                                                                    minWidth: 'auto',
                                                                                    minHeight: 'auto',
                                                                                    height: '20px',
                                                                                    borderRadius: '4px',
                                                                                    textTransform: 'none',
                                                                                    '&:hover': { backgroundColor: colors.primaryDark }
                                                                                }}
                                                            >
                                                                                Map Scheme
                                                                            </Button>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontSize: '0.75rem', color: colors.textSecondary, textAlign: 'center' }}>
                                                                        {customer.ItemCount ?? '-'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            disabled={!customer.ItemCount}
                                                                            onClick={(e) => { e.stopPropagation(); handleViewRateClick(customer); }}
                                                                            sx={{
                                                                                fontSize: '0.65rem',
                                                                                py: 0.3,
                                                                                px: 1,
                                                                                minWidth: 'auto',
                                                                                minHeight: 'auto',
                                                                                height: '20px',
                                                                                textTransform: 'none',
                                                                                borderColor: colors.primary,
                                                                                color: colors.primary
                                                                            }}
                                                                        >
                                                                            View Rate
                                                                        </Button>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            onClick={(e) => { e.stopPropagation(); handleOpenUploadRateDialog(customer); }}
                                                                            sx={{
                                                                                fontSize: '0.65rem',
                                                                                py: 0.3,
                                                                                px: 1,
                                                                                minWidth: 'auto',
                                                                                minHeight: 'auto',
                                                                                height: '20px',
                                                                                textTransform: 'none',
                                                                                backgroundColor: colors.primary,
                                                                                '&:hover': { backgroundColor: colors.primaryDark }
                                                                            }}
                                                                        >
                                                                            Upload Rate
                                                                        </Button>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            onClick={(e) => { e.stopPropagation(); exportSingleCustomerToExcel(customer); }}
                                                                            disabled={exportingCustomerId === customer.CustomerId}
                                                                            startIcon={exportingCustomerId === customer.CustomerId
                                                                                ? <CircularProgress size={12} />
                                                                                : <DownloadIcon sx={{ fontSize: '14px' }} />}
                                                                            sx={{
                                                                                fontSize: '0.65rem',
                                                                                py: 0.3,
                                                                                px: 1,
                                                                                minWidth: 'auto',
                                                                                minHeight: 'auto',
                                                                                height: '20px',
                                                                                textTransform: 'none',
                                                                                borderColor: colors.primary,
                                                                                color: colors.primary
                                                                            }}
                                                                        >
                                                                            Export
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Box sx={{
                                                textAlign: 'center',
                                                py: 6,
                                                color: colors.textSecondary,
                                                border: `1px dashed ${colors.borderDark}`,
                                                borderRadius: '8px',
                                                backgroundColor: colors.backgroundGray
                                            }}>
                                                <PersonSearchIcon sx={{ fontSize: 40, color: colors.borderDark, mb: 1 }} />
                                                <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 0.5 }}>
                                                    {loadingExistingCustomers ? 'Loading customers...' : 'No customers found in this area'}
                                                </Typography>
                                                {!loadingExistingCustomers && (
                                                    <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                        Click "Map New Customer" above to add one.
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}

                                    </Box>
                                )}

                                {/* Loading State for Customer Details */}
                                {existingUserMode === 'new-rate' && selectedExistingCustomer && loadingCustomerDetails && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                        <CircularProgress size={40} sx={{ mb: 2, color: colors.primary }} />
                                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                            Loading rate details for {selectedExistingCustomer.CustomerName}...
                                        </Typography>
                                    </Box>
                                )}

                                {/* Customer Details Table with Filter Toggle */}
                                {existingUserMode === 'new-rate' && selectedExistingCustomer && customerDetailsData.length > 0 && !loadingCustomerDetails && (
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                        {/* Customers List Section */}
                                        {detailsViewFilter === 'customers' && (
                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary }}>
                                                        Customers in Scheme: {customerSchemeId}
                                                    </Typography>
                                                    {selectedCustomersToDelete.length > 0 && (
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={handleDeleteSelectedCustomers}
                                                            size="small"
                                                            sx={{ borderRadius: '8px' }}
                                                        >
                                                            Delete Selected ({selectedCustomersToDelete.length})
                                                        </Button>
                                                    )}
                                                </Box>

                                                {loadingCustomersList ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                                        <CircularProgress size={40} sx={{ color: colors.primary }} />
                                                        <Typography variant="body2" sx={{ mt: 2, color: colors.textSecondary }}>
                                                            Loading customers list...
                                                        </Typography>
                                                    </Box>
                                                ) : customersListData.length > 0 ? (
                                                    <TableContainer
                                                        component={Paper}
                                                        elevation={0}
                                                        sx={{
                                                            ...modernCardStyle,
                                                            flex: 1,
                                                            overflow: 'auto',
                                                            maxHeight: '400px',
                                                            background: colors.backgroundCard
                                                        }}
                                                    >
                                                        <Table stickyHeader size="small">
                                                            <TableHead>
                                                                <TableRow sx={{
                                                                    '& .MuiTableCell-root': {
                                                                        background: colors.backgroundAccent,
                                                                        fontWeight: 700,
                                                                        position: 'sticky',
                                                                        top: 0,
                                                                        zIndex: 1,
                                                                        color: colors.textPrimary,
                                                                        borderBottom: `2px solid ${colors.primary}`,
                                                                        py: 2,
                                                                        textAlign: 'center',
                                                                        fontSize: '0.8rem'
                                                                    }
                                                                }}>
                                                                    <TableCell align="center" sx={{ width: 50 }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedCustomersToDelete.length === customersListData.length && customersListData.length > 0}
                                                                            onChange={(e) => handleSelectAllCustomers(e.target.checked)}
                                                                            style={{ cursor: 'pointer' }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="center">Customer Name</TableCell>
                                                                    <TableCell align="center">Area Name</TableCell>
                                                                    <TableCell align="center">Rebate Scheme Name</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {customersListData.map((customer, index) => (
                                                                    <TableRow
                                                                        key={customer.CustomerId}
                                                                        hover
                                                                        sx={{
                                                                            backgroundColor: selectedCustomersToDelete.includes(customer.CustomerId)
                                                                                ? `${colors.error}08`
                                                                                : 'transparent',
                                                                            '&:hover': {
                                                                                backgroundColor: selectedCustomersToDelete.includes(customer.CustomerId)
                                                                                    ? `${colors.error}15`
                                                                                    : colors.backgroundGray
                                                                            }
                                                                        }}
                                                                    >
                                                                        <TableCell align="center">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedCustomersToDelete.includes(customer.CustomerId)}
                                                                                onChange={(e) => handleCustomerSelection(customer.CustomerId, e.target.checked)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                                                            {customer.CustomerName}
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>
                                                                            {customer.AreaName}
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>
                                                                            {customer.RebateSchemeName}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                ) : (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                                        <PeopleIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 1 }} />
                                                        <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 1 }}>
                                                            No Customers Found
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: colors.textTertiary, textAlign: 'center' }}>
                                                            No customers are currently assigned to this rebate scheme.
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                )}

                            </>
                        )}



                        {userFilterType === 'multiple' && (
                            <>
                                {/* Multiple User - Area and Customer Selection */}
                                <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                    <Grid container spacing={1.5} alignItems="center">
                                        {/* Area Dropdown */}
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="select-area-multiple-label" sx={{ fontSize: '0.8rem' }}>Select Area</InputLabel>
                                                <Select
                                                    labelId="select-area-multiple-label"
                                                    value={selectedMultipleArea}
                                                    label="Select Area"
                                                    onChange={(e) => {
                                                        setSelectedMultipleArea(e.target.value);
                                                        fetchRebateSchemes(e.target.value);
                                                    }}
                                                    disabled={loadingMultipleAreas}
                                                    sx={{
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        }
                                                    }}
                                                >
                                                    {multipleUserAreas.map((area) => (
                                                        <MenuItem key={area.AreaId} value={area.AreaId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {area.AreaName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Rebate Scheme Dropdown */}
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Rebate Scheme</InputLabel>
                                                <Select
                                                    value={selectedRebateScheme}
                                                    label="Rebate Scheme"
                                                    onChange={(e) => setSelectedRebateScheme(e.target.value)}
                                                    disabled={loadingRebateSchemes}
                                                    sx={{
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        }
                                                    }}
                                                >
                                                    {rebateSchemes.map((scheme) => (
                                                        <MenuItem key={scheme.RebateSchemeId} value={scheme.RebateSchemeId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {scheme.RebateSchemeName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Customer Type Dropdown */}
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Customer Type</InputLabel>
                                                <Select
                                                    value={selectedCustomerType}
                                                    label="Customer Type"
                                                    onChange={(e) => setSelectedCustomerType(e.target.value)}
                                                    disabled={loadingCustomerTypes}
                                                    sx={{
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        }
                                                    }}
                                                >
                                                    {customerTypes.map((type) => (
                                                        <MenuItem key={type.customerTypeCode} value={type.customerTypeCode} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {type.customerTypeName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Search Controls */}
                                <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                    <Grid container spacing={1.5} alignItems="end">
                                        {/* Search By Radio Group */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                                                Search By:
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Button
                                                    size="small"
                                                    onClick={() => setMultipleSearchType('CUSTCODE')}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        minWidth: 'auto',
                                                        borderRadius: '6px',
                                                        background: multipleSearchType === 'CUSTCODE' ? colors.primaryGradient : 'transparent',
                                                        color: multipleSearchType === 'CUSTCODE' ? colors.white : colors.textSecondary,
                                                        border: `1px solid ${multipleSearchType === 'CUSTCODE' ? 'transparent' : colors.border}`,
                                                        '&:hover': {
                                                            backgroundColor: multipleSearchType === 'CUSTCODE' ? '' : `${colors.primary}10`
                                                        }
                                                    }}
                                                >
                                                    Customer Code
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => setMultipleSearchType('MOBILE')}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        minWidth: 'auto',
                                                        borderRadius: '6px',
                                                        background: multipleSearchType === 'MOBILE' ? colors.primaryGradient : 'transparent',
                                                        color: multipleSearchType === 'MOBILE' ? colors.white : colors.textSecondary,
                                                        border: `1px solid ${multipleSearchType === 'MOBILE' ? 'transparent' : colors.border}`,
                                                        '&:hover': {
                                                            backgroundColor: multipleSearchType === 'MOBILE' ? '' : `${colors.primary}10`
                                                        }
                                                    }}
                                                >
                                                    Mobile
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => setMultipleSearchType('CUSTNAME')}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        minWidth: 'auto',
                                                        borderRadius: '6px',
                                                        background: multipleSearchType === 'CUSTNAME' ? colors.primaryGradient : 'transparent',
                                                        color: multipleSearchType === 'CUSTNAME' ? colors.white : colors.textSecondary,
                                                        border: `1px solid ${multipleSearchType === 'CUSTNAME' ? 'transparent' : colors.border}`,
                                                        '&:hover': {
                                                            backgroundColor: multipleSearchType === 'CUSTNAME' ? '' : `${colors.primary}10`
                                                        }
                                                    }}
                                                >
                                                    Customer Name
                                                </Button>
                                            </Box>
                                        </Grid>

                                        {/* Search Input and Button */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={multipleSearchInput}
                                                    onChange={(e) => setMultipleSearchInput(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleMultipleCustomerSearch()}
                                                    placeholder={multipleSearchType === 'CUSTCODE' ? 'Enter Customer Code' :
                                                        multipleSearchType === 'MOBILE' ? 'Enter Mobile Number' :
                                                            'Enter Customer Name'}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '32px',
                                                            fontSize: '0.8rem',
                                                            '& input': {
                                                                padding: '6px 8px',
                                                                fontSize: '0.8rem'
                                                            }
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleMultipleCustomerSearch}
                                                    disabled={loadingMultipleSearch || !selectedCustomerType || !multipleSearchInput.trim()}
                                                    sx={{
                                                        minWidth: '40px',
                                                        px: 1.5,
                                                        fontSize: '0.7rem',
                                                        borderRadius: '6px',
                                                        background: colors.primaryGradient,
                                                        fontWeight: 600,
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    {loadingMultipleSearch ? <CircularProgress size={14} color="inherit" /> : <SearchIcon sx={{ fontSize: '14px' }} />}
                                                </Button>
                                            </Box>
                                        </Grid>

                                        {/* Selected Count */}
                                        <Grid item xs={12} sm={12} md={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                                {multipleSelectedCustomers.length > 0 && (
                                                    <Chip
                                                        label={`${multipleSelectedCustomers.length} selected`}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ fontSize: '0.7rem', height: '24px' }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Search Results */}
                                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                    <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                                        <SearchIcon sx={{ mr: 1, fontSize: '16px', color: colors.primary }} />
                                        Search Results ({multipleSearchResults.length})
                                    </Typography>

                                    <Box sx={{
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        p: 1
                                    }}>
                                        {multipleSearchResults.length > 0 ? (
                                            <Grid container spacing={1.5}>
                                                {multipleSearchResults.map((customer, index) => {
                                                    const customerId = customer.custID || customer.customerCode;
                                                    const isSelected = multipleSelectedCustomers.some(c => (c.custID || c.customerCode) === customerId);
                                                    return (
                                                        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                                            <Fade in={true} timeout={300 + (index * 30)}>
                                                                <Card
                                                                    onClick={() => handleMultipleCustomerSelect(customer)}
                                                                    sx={{
                                                                        borderRadius: '8px',
                                                                        backgroundColor: isSelected ? '#f0fdf4' : colors.backgroundCard,
                                                                        border: isSelected ? `2px solid ${colors.success}` : `1px solid ${colors.border}`,
                                                                        transition: 'all 0.2s ease',
                                                                        cursor: 'pointer',
                                                                        position: 'relative',
                                                                        '&:hover': {
                                                                            transform: 'translateY(-1px)',
                                                                            boxShadow: colors.shadowMd,
                                                                            borderColor: isSelected ? colors.success : colors.primary
                                                                        },
                                                                        '&::before': isSelected ? {
                                                                            content: '"✓"',
                                                                            position: 'absolute',
                                                                            top: '4px',
                                                                            right: '4px',
                                                                            width: '16px',
                                                                            height: '16px',
                                                                            backgroundColor: colors.success,
                                                                            color: 'white',
                                                                            borderRadius: '50%',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '10px',
                                                                            fontWeight: 'bold',
                                                                            zIndex: 1
                                                                        } : {}
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                                fontSize: '0.8rem',
                                                                                mb: 0.5,
                                                                                color: colors.textPrimary,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                lineHeight: 1.2
                                                                            }}
                                                                        >
                                                                            <PersonOutlineIcon sx={{ mr: 0.5, fontSize: '12px', color: isSelected ? colors.success : colors.primary }} />
                                                                            {customer.customerName || 'N/A'}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                                            <Chip
                                                                                icon={<PhoneIcon sx={{ fontSize: '8px' }} />}
                                                                                label={customer.mobile || 'N/A'}
                                                                                variant="outlined"
                                                                                size="small"
                                                                                sx={{
                                                                                    fontSize: '0.65rem',
                                                                                    height: '16px',
                                                                                    fontWeight: 500,
                                                                                    borderColor: colors.success,
                                                                                    color: colors.successDark,
                                                                                    backgroundColor: '#ecfdf5',
                                                                                    '& .MuiChip-icon': {
                                                                                        fontSize: '6px',
                                                                                        color: colors.successDark
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <Chip
                                                                                label={customerId || 'N/A'}
                                                                                variant="outlined"
                                                                                size="small"
                                                                                sx={{
                                                                                    fontSize: '0.65rem',
                                                                                    height: '16px',
                                                                                    fontWeight: 500,
                                                                                    borderColor: colors.info,
                                                                                    color: colors.infoDark,
                                                                                    backgroundColor: '#eff6ff'
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Fade>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        ) : multipleSearchInput && !loadingMultipleSearch ? (
                                            <Box sx={{
                                                textAlign: 'center',
                                                py: 4,
                                                color: colors.textSecondary,
                                                border: `1px dashed ${colors.borderDark}`,
                                                borderRadius: '8px',
                                                backgroundColor: colors.backgroundGray
                                            }}>
                                                <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 0.5 }}>
                                                    No customers found
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                    Try adjusting your search criteria
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{
                                                textAlign: 'center',
                                                py: 4,
                                                color: colors.textSecondary,
                                                border: `1px dashed ${colors.borderDark}`,
                                                borderRadius: '8px',
                                                backgroundColor: colors.backgroundGray
                                            }}>
                                                <SearchIcon sx={{ fontSize: '40px', color: colors.borderDark, mb: 2 }} />
                                                <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 0.5 }}>
                                                    Ready to search
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                    Select area, rebate scheme, customer type and search for customers
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </>
                        )}

                        {/* Selected Customers Display for Multiple User */}
                        {userFilterType === 'multiple' && multipleSelectedCustomers.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center' }}>
                                        <GroupIcon sx={{ mr: 1, fontSize: '16px', color: colors.primary }} />
                                        Selected Customers ({multipleSelectedCustomers.length})
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        onClick={handleSaveMultipleCustomers}
                                        disabled={!selectedRebateScheme || savingMultipleCustomers}
                                        sx={{
                                            fontSize: '0.75rem',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: colors.success,
                                            '&:hover': {
                                                backgroundColor: colors.successDark
                                            }
                                        }}
                                    >
                                        {savingMultipleCustomers ? (
                                            <>
                                                <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />
                                                Saving...
                                            </>
                                        ) : (
                                            `Save Selected (${multipleSelectedCustomers.length})`
                                        )}
                                    </Button>
                                </Box>

                                <Box sx={{
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '8px',
                                    p: 2,
                                    backgroundColor: colors.backgroundCard,
                                    maxHeight: '120px',
                                    overflowY: 'auto'
                                }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {multipleSelectedCustomers.map((customer, index) => {
                                            const customerId = customer.custID || customer.customerCode;
                                            return (
                                                <Chip
                                                    key={index}
                                                    label={customer.customerName || 'N/A'}
                                                    onDelete={() => handleMultipleCustomerSelect(customer)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: customer.mappingStatus === 'Mapped' ? colors.success :
                                                            customer.needsMapping ? colors.warning : colors.primary,
                                                        color: 'white',
                                                        fontSize: '0.75rem',
                                                        height: '24px',
                                                        fontWeight: 500,
                                                        '& .MuiChip-deleteIcon': {
                                                            color: 'white',
                                                            '&:hover': {
                                                                color: 'rgba(255,255,255,0.8)'
                                                            }
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>

                                    {/* Show customers that need mapping */}
                                    {multipleSelectedCustomers.some(c => c.needsMapping) && (
                                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: colors.textSecondary, mb: 1, display: 'block' }}>
                                                Customers needing area mapping:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {multipleSelectedCustomers.filter(c => c.needsMapping).map((customer, index) => (
                                                    <Button
                                                        key={index}
                                                        size="small"
                                                        variant="contained"
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            height: '20px',
                                                            minWidth: 'auto',
                                                            px: 1,
                                                            backgroundColor: colors.primary,
                                                            '&:hover': {
                                                                backgroundColor: colors.primaryDark
                                                            }
                                                        }}
                                                        onClick={() => handleMapCustomer(customer)}
                                                    >
                                                        Map {customer.customerName}
                                                    </Button>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Master View (if needed in future) */}
                        {activeView === 'master' && (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {/* Master Upload Controls */}
                                <Box sx={{ flexShrink: 0, mb: 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={8}>
                                            <StepBox title="1. Import Excel File" icon={<UploadIcon sx={{ color: colors.primary }} />}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="contained"
                                                        component="label"
                                                        size="small"
                                                        sx={{
                                                            ...gradientButtonStyle,
                                                            fontSize: '0.875rem',
                                                            py: 1,
                                                            px: 2.5,
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        Choose File
                                                        <input
                                                            type="file"
                                                            accept=".xlsx,.xls"
                                                            hidden
                                                            onChange={handleMasterFileChange}
                                                            ref={masterFileInputRef}
                                                        />
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={handleDownloadMasterSample}
                                                        sx={{
                                                            borderColor: '#4caf50',
                                                            color: '#4caf50',
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            px: 2.5,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                borderColor: '#388e3c',
                                                                background: 'rgba(76, 175, 80, 0.04)',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: colors.shadow
                                                            }
                                                        }}
                                                    >
                                                        Download Sample
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={processMasterExcel}
                                                        disabled={!masterExcelFile || isMasterExcelProcessing}
                                                        sx={{
                                                            borderColor: colors.primary,
                                                            color: colors.primary,
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            px: 2.5,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                borderColor: colors.primaryDark,
                                                                background: `${colors.primary}10`,
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: colors.shadow
                                                            }
                                                        }}
                                                    >
                                                        {isMasterExcelProcessing ? <CircularProgress size={16} /> : 'Validate'}
                                                    </Button>
                                                    {masterExcelFile && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="error"
                                                            onClick={clearMasterExcel}
                                                            sx={{
                                                                borderColor: colors.error,
                                                                color: colors.error,
                                                                borderRadius: '12px',
                                                                fontWeight: 600,
                                                                textTransform: 'none',
                                                                py: 1,
                                                                px: 2.5,
                                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                '&:hover': {
                                                                    borderColor: colors.errorDark,
                                                                    backgroundColor: `${colors.error}10`,
                                                                    transform: 'translateY(-1px)',
                                                                    boxShadow: colors.shadow
                                                                }
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    )}
                                                </Box>
                                                {masterExcelFileName && (
                                                    <Chip
                                                        label={masterExcelFileName}
                                                        size="small"
                                                        sx={{
                                                            background: colors.successGradient,
                                                            color: colors.white,
                                                            fontWeight: 500,
                                                            borderRadius: '8px',
                                                            '& .MuiChip-label': {
                                                                fontSize: '0.75rem'
                                                            }
                                                        }}
                                                    />
                                                )}
                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.textSecondary }}>
                                                    Required columns: ItemCode, Rate, QualifyQty
                                                </Typography>
                                            </StepBox>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <StepBox title="2. Upload Data" icon={<CheckCircleIcon sx={{ color: colors.success }} />}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleMasterUpload}
                                                    disabled={masterValidationErrors.length > 0 || masterExcelData.length === 0 || isMasterUploading}
                                                    fullWidth
                                                    sx={{
                                                        background: colors.successGradient,
                                                        color: colors.white,
                                                        borderRadius: '12px',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        py: 1.5,
                                                        boxShadow: colors.shadowMd,
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        '&:hover': {
                                                            background: `linear-gradient(135deg, ${colors.successLight} 0%, ${colors.successDark} 100%)`,
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: colors.shadowLg
                                                        },
                                                        '&:disabled': {
                                                            background: colors.borderLight,
                                                            color: colors.textTertiary,
                                                            transform: 'none',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                    startIcon={isMasterUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                                                >
                                                    {isMasterUploading ? 'Uploading...' : 'Upload Master Data'}
                                                </Button>
                                                {masterValidationErrors.length > 0 && (
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.error }}>
                                                        Fix {masterValidationErrors.length} errors before upload
                                                    </Typography>
                                                )}

                                                {/* Customer Mapping Button */}
                                                {masterUnmappedCustomers.length > 0 && (
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => setShowMasterMappingDialog(true)}
                                                        fullWidth
                                                        sx={{
                                                            mt: 2,
                                                            borderColor: colors.warning,
                                                            color: colors.warning,
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            '&:hover': {
                                                                borderColor: colors.warningDark,
                                                                backgroundColor: `${colors.warning}10`,
                                                                color: colors.warningDark
                                                            }
                                                        }}
                                                        startIcon={<PersonAddIcon />}
                                                    >
                                                        Map {masterUnmappedCustomers.length} Customer{masterUnmappedCustomers.length !== 1 ? 's' : ''}
                                                    </Button>
                                                )}
                                            </StepBox>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Validation Errors Alert */}
                                {masterValidationErrors.length > 0 && (
                                    <Box sx={{ mb: 2, flexShrink: 0 }}>
                                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                                            Found {masterValidationErrors.length} validation error{masterValidationErrors.length > 1 ? 's' : ''} in {Object.keys(masterRowErrors).length} row{Object.keys(masterRowErrors).length > 1 ? 's' : ''}. Please fix these issues:
                                        </Alert>
                                        <Paper sx={{ p: 2, maxHeight: 250, overflow: 'auto', backgroundColor: '#fef2f2', border: `1px solid ${colors.error}` }}>
                                            {masterValidationErrors.map((error, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
                                                    <Box sx={{
                                                        minWidth: 24,
                                                        height: 24,
                                                        backgroundColor: colors.error,
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mr: 2,
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {index + 1}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: colors.errorDark, fontWeight: 500 }}>
                                                        {error}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Paper>
                                    </Box>
                                )}

                                {/* Data Table */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FactCheckIcon sx={{ mr: 1.5, color: colors.success, fontSize: '28px' }} />
                                            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary }}>
                                                Data Preview & Validation
                                            </Typography>
                                        </Box>
                                        {masterExcelData.length > 0 && (
                                            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                                {masterExcelData.length} rows • {masterExcelData.filter(row => row.hasError).length} errors
                                            </Typography>
                                        )}
                                    </Box>

                                    <TableContainer
                                        component={Paper}
                                        elevation={0}
                                        sx={{
                                            ...modernCardStyle,
                                            flex: 1,
                                            overflow: 'auto',
                                            maxHeight: '100%',
                                            background: colors.backgroundCard
                                        }}
                                    >
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow sx={{
                                                    '& .MuiTableCell-root': {
                                                        background: colors.backgroundAccent,
                                                        fontWeight: 700,
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 1,
                                                        color: colors.textPrimary,
                                                        borderBottom: `2px solid ${colors.primary}`,
                                                        py: 2,
                                                        textAlign: 'center'
                                                    }
                                                }}>
                                                    <TableCell align="center" sx={{ width: 60 }}>Row</TableCell>
                                                    <TableCell align="center">Area Code</TableCell>
                                                    <TableCell align="center">Customer ID</TableCell>
                                                    <TableCell align="center">Parent Item ID</TableCell>
                                                    <TableCell align="left">Parent Name</TableCell>
                                                    <TableCell align="center">Item Code</TableCell>
                                                    <TableCell align="left">Item Name</TableCell>
                                                    <TableCell align="right">Reg Rate</TableCell>
                                                    <TableCell align="center" sx={{ backgroundColor: '#fff9c4' }}>Reg Qty</TableCell>
                                                    <TableCell align="right">Bulk Rate</TableCell>
                                                    <TableCell align="center" sx={{ backgroundColor: '#fff9c4' }}>Bulk Qty</TableCell>
                                                    <TableCell align="right">Vol Rate</TableCell>
                                                    <TableCell align="center" sx={{ backgroundColor: '#fff9c4' }}>Vol Qty</TableCell>
                                                    <TableCell align="center">Errors</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {masterExcelData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={14} align="center" sx={{ p: 4, color: colors.textSecondary }}>
                                                            {masterExcelFile ? 'Click "Validate" to process the Excel file' : 'Import an Excel file to see data preview'}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    masterExcelData.map((row, index) => (
                                                        <TableRow
                                                            key={index}
                                                            hover
                                                            sx={{
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': {
                                                                    backgroundColor: row.hasError
                                                                        ? 'rgba(244, 67, 54, 0.08)'
                                                                        : colors.backgroundGray,
                                                                    transform: 'scale(1.005)'
                                                                },
                                                                backgroundColor: row.hasError
                                                                    ? 'rgba(244, 67, 54, 0.03)'
                                                                    : 'transparent',
                                                                borderLeft: row.hasError
                                                                    ? `4px solid ${colors.error}`
                                                                    : 'none'
                                                            }}
                                                        >
                                                            <TableCell align="center" sx={{ fontWeight: 500, color: colors.textSecondary }}>
                                                                {row.rowNumber || index + 1}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={row.AreaCode || row.AreaID}
                                                                    size="small"
                                                                    sx={{
                                                                        height: 24,
                                                                        fontSize: '0.75rem',
                                                                        bgcolor: colors.backgroundGray
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                    {row.CustomerID}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: colors.textSecondary }}>
                                                                    {row.ParentItemId || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="left">
                                                                {row.ParentName || '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={row.ItemCode}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        height: 24,
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="left">
                                                                {row.ItemName || '-'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: row.RegularRate ? 600 : 400 }}>
                                                                {row.RegularRate || '-'}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ backgroundColor: '#fffde7', color: colors.textSecondary }}>
                                                                {row.RegularQty || '-'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: row.BulkRate ? 600 : 400 }}>
                                                                {row.BulkRate || '-'}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ backgroundColor: '#fffde7', color: colors.textSecondary }}>
                                                                {row.BulkQty || '-'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: row.VolumeRate ? 600 : 400 }}>
                                                                {row.VolumeRate || '-'}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ backgroundColor: '#fffde7', color: colors.textSecondary }}>
                                                                {row.VolumeQty || '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {row.hasError ? (
                                                                    <Tooltip title={
                                                                        <Box>
                                                                            {row.errors.map(e => <div key={e}>• {e}</div>)}
                                                                        </Box>
                                                                    } arrow>
                                                                        <IconButton size="small" color="error">
                                                                            <ErrorIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                        )}

                        {/* ═══ Area Wise Upload View ═══ */}
                        {userFilterType === 'area-wise' && (
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowBulkAreaUpload(prev => !prev)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            px: 2,
                                            py: 0.6,
                                            borderRadius: '8px',
                                            minWidth: 'auto',
                                            borderColor: colors.primary,
                                            color: colors.primary,
                                            '&:hover': {
                                                borderColor: colors.primaryDark,
                                                backgroundColor: `${colors.primary}10`,
                                            }
                                        }}
                                    >
                                        {showBulkAreaUpload ? 'Close Bulk Area Rate Upload' : 'Bulk Area Rate Upload'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={exportAllAreaRatesToExcel}
                                        disabled={isExportingAllAreaRates}
                                        startIcon={isExportingAllAreaRates ? <CircularProgress size={14} /> : <DownloadIcon sx={{ fontSize: '16px' }} />}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            px: 2,
                                            py: 0.6,
                                            borderRadius: '8px',
                                            minWidth: 'auto',
                                            borderColor: colors.primary,
                                            color: colors.primary,
                                            '&:hover': {
                                                borderColor: colors.primaryDark,
                                                backgroundColor: `${colors.primary}10`,
                                            }
                                        }}
                                    >
                                        {isExportingAllAreaRates ? 'Exporting...' : 'Export All'}
                                    </Button>
                                    <TextField
                                        size="small"
                                        placeholder="Search by Area ID, Scheme ID"
                                        value={areaRateSearch}
                                        onChange={e => setAreaRateSearch(e.target.value)}
                                        sx={{
                                            width: { xs: '100%', sm: '340px' },
                                            background: colors.backgroundGray,
                                            borderRadius: 2,
                                            '& .MuiInputBase-root': {
                                                height: '32px',
                                                fontSize: '0.8rem'
                                            },
                                            '& .MuiInputBase-input': {
                                                padding: '6px 8px'
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.textSecondary, fontSize: '16px' }} />
                                        }}
                                    />
                                    </Box>
                                </Box>

                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Area ID</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Area Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Scheme ID</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Scheme Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Item Count</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>View Rate</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Upload Rate</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Export</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loadingAreaRebateScheme ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                                        <CircularProgress size={20} />
                                                    </TableCell>
                                                </TableRow>
                                            ) : filteredAreaRebateSchemeData.length > 0 ? (
                                                filteredAreaRebateSchemeData.map((row, index) => (
                                                    <TableRow key={row.RebateSchemeId || index}>
                                                        <TableCell>{row.SFAreaId || '-'}</TableCell>
                                                        <TableCell>{row.SFAreaName || '-'}</TableCell>
                                                        <TableCell>{row.RebateSchemeId || '-'}</TableCell>
                                                        <TableCell>{row.RebateSchemeName || '-'}</TableCell>
                                                        <TableCell>{row.ItemCount ?? 0}</TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => handleViewAreaRateClick(row)}
                                                                disabled={!row.ItemCount}
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    py: 0.3,
                                                                    px: 1,
                                                                    minWidth: 'auto',
                                                                    minHeight: 'auto',
                                                                    height: '20px',
                                                                    textTransform: 'none',
                                                                    borderColor: colors.primary,
                                                                    color: colors.primary
                                                                }}
                                                            >
                                                                View Rate
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => setShowBulkAreaUpload(true)}
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    py: 0.3,
                                                                    px: 1,
                                                                    minWidth: 'auto',
                                                                    minHeight: 'auto',
                                                                    height: '20px',
                                                                    textTransform: 'none',
                                                                    backgroundColor: colors.primary,
                                                                    '&:hover': { backgroundColor: colors.primaryDark }
                                                                }}
                                                            >
                                                                Upload Rate
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => exportAreaRateToExcel(row)}
                                                                disabled={exportingAreaRateId === row.RebateSchemeId}
                                                                startIcon={exportingAreaRateId === row.RebateSchemeId
                                                                    ? <CircularProgress size={12} />
                                                                    : <DownloadIcon sx={{ fontSize: '14px' }} />}
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    py: 0.3,
                                                                    px: 1,
                                                                    minWidth: 'auto',
                                                                    minHeight: 'auto',
                                                                    height: '20px',
                                                                    textTransform: 'none',
                                                                    borderColor: colors.primary,
                                                                    color: colors.primary
                                                                }}
                                                            >
                                                                Export
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: colors.textSecondary }}>
                                                        {areaRebateSchemeData.length > 0
                                                            ? 'No results match your search'
                                                            : 'No rebate scheme found'}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Dialog open={viewAreaRateDialogOpen} onClose={handleCloseViewAreaRateDialog} maxWidth="md" fullWidth>
                                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        Rate Details {viewAreaRateRow ? `— ${viewAreaRateRow.SFAreaName}` : ''}
                                        <IconButton size="small" onClick={handleCloseViewAreaRateDialog}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent dividers>
                                        {loadingViewAreaRate ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                                <CircularProgress size={28} />
                                            </Box>
                                        ) : viewAreaRateData.length === 0 ? (
                                            <Typography variant="body2" sx={{ textAlign: 'center', color: colors.textSecondary, p: 3 }}>
                                                No rate details found.
                                            </Typography>
                                        ) : (
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700 }}>Item Code</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Qualify Qty</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Rate</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Valid Till</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {viewAreaRateData.map((item, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>{item.ItemCode || '-'}</TableCell>
                                                                <TableCell>{item.ItemName || '-'}</TableCell>
                                                                <TableCell>{item.QualifyQty ?? '-'}</TableCell>
                                                                <TableCell>{item.Rate ?? '-'}</TableCell>
                                                                <TableCell>{formatYyyymmdd(item.validtill || item.ValidTill)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </DialogContent>
                                    <DialogActions>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            disabled={loadingViewAreaRate || viewAreaRateData.length === 0 || isInvalidatingAreaRate}
                                            startIcon={isInvalidatingAreaRate ? <CircularProgress size={14} color="inherit" /> : null}
                                            onClick={() => handleInvalidateAreaRateClick(viewAreaRateRow)}
                                        >
                                            {isInvalidatingAreaRate ? 'Invalidating...' : 'Invalidate Rate'}
                                        </Button>
                                    </DialogActions>
                                </Dialog>

                                <Dialog
                                    open={invalidateAreaConfirmOpen}
                                    onClose={handleCancelInvalidateAreaRate}
                                    maxWidth="xs"
                                    fullWidth
                                >
                                    <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
                                        Are you sure?
                                    </DialogTitle>
                                    <DialogContent>
                                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                            By clicking this, the system will disable all rates applied to this area scheme
                                            {areaToInvalidate?.SFAreaName ? <> (<strong>{areaToInvalidate.SFAreaName}</strong>)</> : ''}.
                                            This action cannot be undone.
                                        </Typography>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCancelInvalidateAreaRate} disabled={isInvalidatingAreaRate}>
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={handleConfirmInvalidateAreaRate}
                                            disabled={isInvalidatingAreaRate}
                                            startIcon={isInvalidatingAreaRate ? <CircularProgress size={14} color="inherit" /> : null}
                                        >
                                            {isInvalidatingAreaRate ? 'Invalidating...' : 'Yes, Invalidate'}
                                        </Button>
                                    </DialogActions>
                                </Dialog>

                                <Dialog
                                    open={showBulkAreaUpload}
                                    onClose={() => setShowBulkAreaUpload(false)}
                                    maxWidth="lg"
                                    fullWidth
                                >
                                    <HorecaUpload embedded onClose={() => setShowBulkAreaUpload(false)} />
                                </Dialog>
                            </Box>
                        )}

                        {/* ═══ POS Master Upload View (Bulk Rate Upload popup) ═══ */}
                        <Dialog
                            open={showBulkRateUploadInExisting}
                            onClose={() => setShowBulkRateUploadInExisting(false)}
                            maxWidth="lg"
                            fullWidth
                        >
                            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                Bulk Customer Rate Upload
                                <IconButton size="small" onClick={() => setShowBulkRateUploadInExisting(false)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent dividers>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 1.5 }}>

                                {/* Info banner */}
                                <Box sx={{ flexShrink: 0, p: 1.5, borderRadius: 2, background: `${colors.primary}10`, border: `1px solid ${colors.primary}30`, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.78rem' }}>
                                        Include <strong>CustomerID</strong> and <strong>AreaCode</strong> columns in the Excel — customers are derived automatically from the file. Download the sample to see the required format.
                                    </Typography>
                                </Box>

                                {/* Customers derived from Excel */}
                                {posMasterSelectedCustomers.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ color: colors.textSecondary, mr: 0.5, fontSize: '0.75rem' }}>Customers from Excel:</Typography>
                                        {posMasterSelectedCustomers.map(c => (
                                            <Chip
                                                key={c.custID}
                                                label={`${c.custID}${c.areaCode ? ` (${c.areaCode})` : ''}`}
                                                size="small"
                                                sx={{ backgroundColor: `${colors.primary}15`, fontSize: '0.72rem', height: '22px' }}
                                            />
                                        ))}
                                    </Box>
                                )}

                                {/* Step boxes */}
                                <Box sx={{ flexShrink: 0 }}>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} lg={5}>
                                            <StepBox title="1. Import Excel (CustomerID · AreaCode · ItemCode · Rate · QualifyQty)" icon={<UploadIcon sx={{ color: colors.primary }} />}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="contained"
                                                        component="label"
                                                        size="small"
                                                        sx={{ ...gradientButtonStyle, fontSize: '0.75rem', px: 1.5, py: 0.5, height: '28px' }}
                                                    >
                                                        Choose File
                                                        <input type="file" accept=".xlsx,.xls" hidden onChange={handlePosMasterFileChange} ref={posMasterFileInputRef} />
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={processExcelForPOSMaster}
                                                        disabled={!posMasterExcelFile || isPosMasterProcessing}
                                                        sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5, height: '28px' }}
                                                    >
                                                        {isPosMasterProcessing ? <CircularProgress size={14} /> : 'Validate'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DownloadIcon sx={{ fontSize: '16px' }} />}
                                                        onClick={downloadPosMasterSampleExcel}
                                                        sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5, height: '28px', borderColor: colors.success, color: colors.success, '&:hover': { borderColor: colors.successDark, backgroundColor: `${colors.success}10` } }}
                                                    >
                                                        Sample
                                                    </Button>
                                                    {posMasterExcelFile && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="error"
                                                            onClick={clearPosMasterExcel}
                                                            sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5, height: '28px' }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    )}
                                                    {posMasterExcelFileName && (
                                                        <Typography variant="caption" sx={{ color: colors.textSecondary, ml: 0.5, fontStyle: 'italic' }} noWrap>
                                                            {posMasterExcelFileName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </StepBox>
                                        </Grid>
                                        <Grid item xs={12} lg={7}>
                                            <StepBox title="2. Upload to Selected Customers" icon={<PlaylistAddCheckIcon sx={{ color: colors.primary }} />}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={handlePosMasterUpload}
                                                        disabled={posMasterExcelData.length === 0 || posMasterValidationErrors.length > 0 || posMasterSelectedCustomers.length === 0 || loadingMapping}
                                                        startIcon={loadingMapping ? <CircularProgress size={14} color="inherit" /> : <UploadIcon />}
                                                        sx={{ ...gradientButtonStyle, fontSize: '0.8rem', py: 0.75, px: 2 }}
                                                    >
                                                        Upload to {posMasterSelectedCustomers.length} Customer{posMasterSelectedCustomers.length !== 1 ? 's' : ''}
                                                    </Button>
                                                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                                        {posMasterExcelData.length > 0 ? `${posMasterExcelData.length} rows ready` : 'Upload & validate Excel first'}
                                                    </Typography>
                                                </Box>
                                                {posMasterValidationErrors.length > 0 && (
                                                    <Alert severity="error" sx={{ mt: 1, py: 0.5, fontSize: '0.75rem' }}>
                                                        <Box sx={{ fontWeight: 700, mb: 0.5 }}>
                                                            {posMasterValidationErrors.length} validation error(s) — fix before uploading:
                                                        </Box>
                                                        <Box
                                                            component="ul"
                                                            sx={{
                                                                m: 0,
                                                                pl: 2,
                                                                maxHeight: '160px',
                                                                overflowY: 'auto',
                                                                fontSize: '0.72rem',
                                                                lineHeight: 1.6
                                                            }}
                                                        >
                                                            {posMasterValidationErrors.map((err, i) => (
                                                                <li key={i}>{err}</li>
                                                            ))}
                                                        </Box>
                                                    </Alert>
                                                )}
                                            </StepBox>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Preview table */}
                                <TableContainer
                                    component={Paper}
                                    elevation={0}
                                    sx={{ ...modernCardStyle, flex: 1, overflow: 'auto', background: colors.backgroundCard }}
                                >
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow sx={{ '& .MuiTableCell-root': { background: colors.backgroundAccent, fontWeight: 700, position: 'sticky', top: 0, zIndex: 1, color: colors.textPrimary, borderBottom: `2px solid ${colors.primary}`, py: 1.5, textAlign: 'center' } }}>
                                                <TableCell align="center" sx={{ width: 50 }}>#</TableCell>
                                                <TableCell align="center">Customer ID</TableCell>
                                                <TableCell align="center">Area Code</TableCell>
                                                <TableCell align="center">Item Code</TableCell>
                                                <TableCell align="center">Material Description</TableCell>
                                                <TableCell align="right">Qualify Qty</TableCell>
                                                <TableCell align="right">Rate</TableCell>
                                                <TableCell align="center">Validity Upto</TableCell>
                                                <TableCell align="center">Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {posMasterExcelData.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={9} align="center" sx={{ p: 4, color: colors.textSecondary }}>
                                                        {posMasterExcelFile ? 'Click "Validate" to process the Excel file' : 'Import an Excel file to see data preview'}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                posMasterExcelData.map((row, index) => (
                                                    <TableRow key={index} hover sx={{ transition: 'all 0.2s ease', '&:hover': { backgroundColor: colors.backgroundGray }, backgroundColor: index % 2 === 0 ? 'transparent' : colors.backgroundGray }}>
                                                        <TableCell align="center" sx={{ fontWeight: 500, color: colors.textSecondary }}>{index + 1}</TableCell>
                                                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{row.CustomerID || '-'}</TableCell>
                                                        <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>{row.AreaCode || '-'}</TableCell>
                                                        <TableCell align="center">
                                                            <Chip label={row.ItemCode} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>{row.ItemDescription || '-'}</TableCell>
                                                        <TableCell align="right">{row.QualifyQty ?? '-'}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{row.Rate ?? '-'}</TableCell>
                                                        <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>{row.ValidityUpto || '-'}</TableCell>
                                                        <TableCell align="center"><CheckCircleIcon color="success" fontSize="small" /></TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                            </DialogContent>
                        </Dialog>

                    </Paper>
                </Container>

                <CustomerSearchModal
                    open={showCustomerSearchModal}
                    onClose={() => { setShowCustomerSearchModal(false); setSelectedCustomers([]); }}
                    searchResults={searchResults}
                    selectedCustomers={selectedCustomers}
                    handleCustomerSelect={handleCustomerSelect}
                    handleConfirmCustomerSelection={handleConfirmCustomerSelection}
                />
                <CustomerSearchModal
                    open={showPosMasterSearchModal}
                    onClose={() => setShowPosMasterSearchModal(false)}
                    searchResults={posMasterSearchResults}
                    selectedCustomers={posMasterSelectedCustomers}
                    handleCustomerSelect={handlePosMasterCustomerSelect}
                    handleConfirmCustomerSelection={handlePosMasterCustomerConfirm}
                />
                <MappingProgressDialog
                    open={showMappingProgressDialog}
                    loadingMapping={loadingMapping}
                    onClose={() => { setShowMappingProgressDialog(false); if (mappingProgress === 100) resetAllState(); }}
                    mappingProgress={mappingProgress}
                    mappingStatus={mappingStatus}
                    totalCustomersToUpload={totalCustomersToUpload}
                    currentCustomerUploadIndex={currentCustomerUploadIndex}
                    uploadResults={uploadResults}
                />
                <CustomerMappingConfirmDialog
                    open={showMappingConfirmDialog}
                    onClose={handleCancelMapping}
                    customers={customersToMap}
                    areaName={selectedAreaName}
                    onConfirm={handleConfirmMapping}
                />
                <ValidationProgressDialog
                    open={showValidationProgress}
                    validationProgress={validationProgress}
                    validationStatus={validationStatus}
                    totalCustomers={selectedCustomers.length}
                    onClose={() => setShowValidationProgress(false)}
                />
                <MapSchemeModal
                    open={mapSchemeModalOpen}
                    onClose={handleCloseMapSchemeModal}
                    customer={selectedCustomerForMapping}
                    availableSchemes={availableRebateSchemes}
                    selectedScheme={selectedMappingScheme}
                    onSchemeSelect={handleSchemeSelection}
                    schemeRateData={schemeRateData}
                    loadingSchemes={loadingAvailableSchemes}
                    loadingRateData={loadingSchemeRateData}
                    onMapScheme={handleMapSchemeConfirm}
                />
                <MasterCustomerMappingDialog
                    open={showMasterMappingDialog}
                    onClose={() => setShowMasterMappingDialog(false)}
                    unmappedCustomers={masterUnmappedCustomers}
                    onMapCustomer={handleMasterCustomerMapping}
                    onBulkMapping={handleBulkMasterMapping}
                />
                <DeleteProgressDialog
                    open={showDeleteProgress}
                    deleteProgress={deleteProgress}
                    deleteStatus={deleteStatus}
                    totalCustomers={selectedCustomersForDelete.length}
                    deleteResults={deleteResults}
                    onClose={() => setShowDeleteProgress(false)}
                />

                <Dialog
                    open={viewRateDialogOpen}
                    onClose={handleCloseViewRateDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        Rate Details {viewRateCustomer ? `— ${viewRateCustomer.CustomerName}` : ''}
                        <IconButton size="small" onClick={handleCloseViewRateDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {loadingViewRate ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={32} sx={{ color: colors.primary }} />
                            </Box>
                        ) : viewRateData.length === 0 ? (
                            <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', py: 3 }}>
                                No rate details found for this customer.
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Sr.No.</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Item Code</TableCell>
                                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Qualify Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Rate</TableCell>
                                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Valid Till</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {viewRateData.map((item) => (
                                            <TableRow key={item.sno}>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.sno}</TableCell>
                                                <TableCell>{item.itemName}</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.itemCode}</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.qualifyQty}</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.rate}</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatYyyymmdd(item.validTill)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={loadingViewRate || viewRateData.length === 0 || isInvalidatingRate}
                            startIcon={isInvalidatingRate ? <CircularProgress size={14} color="inherit" /> : null}
                            onClick={() => handleInvalidateRateClick(viewRateCustomer)}
                        >
                            {isInvalidatingRate ? 'Invalidating...' : 'Invalidate Rate'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={invalidateConfirmOpen}
                    onClose={handleCancelInvalidateRate}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        Are you sure?
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                            By clicking this, the system will disable all rates applied to this scheme/customer
                            {customerToInvalidate?.CustomerName ? <> (<strong>{customerToInvalidate.CustomerName}</strong>)</> : ''}.
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelInvalidateRate} disabled={isInvalidatingRate}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmInvalidateRate}
                            disabled={isInvalidatingRate}
                            startIcon={isInvalidatingRate ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {isInvalidatingRate ? 'Invalidating...' : 'Yes, Invalidate'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showBulkDeleteConfirm}
                    onClose={handleCancelBulkDelete}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        Are you sure?
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                            Are you sure you want to delete the rate scheme for {selectedCustomersForDelete.length} customer{selectedCustomersForDelete.length === 1 ? '' : 's'}?
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                            {selectedCustomersForDelete.map(c => c.CustomerName).join(', ')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: colors.error }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelBulkDelete} disabled={isDeleting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmBulkDelete}
                            disabled={isDeleting}
                            startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={uploadRateDialogOpen}
                    onClose={handleCloseUploadRateDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        Upload Rate {selectedExistingCustomer ? `— ${selectedExistingCustomer.CustomerName}` : ''}
                        <IconButton size="small" onClick={handleCloseUploadRateDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {loadingCustomerDetails ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={32} sx={{ color: colors.primary }} />
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2, color: colors.textPrimary, display: 'flex', alignItems: 'center' }}>
                                        <UploadIcon sx={{ mr: 1, color: colors.primary }} />
                                        Upload New Rates
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                <Button
                                                    variant="contained"
                                                    component="label"
                                                    size="small"
                                                    sx={{ ...gradientButtonStyle, fontSize: '0.875rem', py: 1, px: 2.5, flexShrink: 0 }}
                                                >
                                                    Choose Excel File
                                                    <input
                                                        type="file"
                                                        accept=".xlsx,.xls"
                                                        hidden
                                                        onChange={handleExistingUserFileChange}
                                                        ref={existingUserFileInputRef}
                                                    />
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => (customerDetailsData.length > 0 ? processExistingUserExcel() : processExistingUserExcelForNewCustomer())}
                                                    disabled={!existingUserExcelFile || isExistingUserExcelProcessing}
                                                    sx={{
                                                        borderColor: colors.primary,
                                                        color: colors.primary,
                                                        borderRadius: '12px',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        py: 1,
                                                        px: 2.5
                                                    }}
                                                >
                                                    {isExistingUserExcelProcessing ? <CircularProgress size={16} /> : 'Validate Data'}
                                                </Button>
                                                {existingUserExcelFile && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="error"
                                                        onClick={clearExistingUserExcel}
                                                        sx={{
                                                            borderColor: colors.error,
                                                            color: colors.error,
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            px: 2.5
                                                        }}
                                                    >
                                                        Clear
                                                    </Button>
                                                )}
                                            </Box>
                                            {existingUserExcelFileName && (
                                                <Chip
                                                    label={existingUserExcelFileName}
                                                    size="small"
                                                    sx={{
                                                        mt: 1,
                                                        background: colors.successGradient,
                                                        color: colors.white,
                                                        fontWeight: 500,
                                                        borderRadius: '8px',
                                                        '& .MuiChip-label': { fontSize: '0.75rem' }
                                                    }}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Button
                                                variant="contained"
                                                onClick={handleExistingUserUpload}
                                                disabled={existingUserValidationErrors.length > 0 || existingUserExcelData.length === 0 || isExistingUserUploading}
                                                fullWidth
                                                sx={{
                                                    background: colors.successGradient,
                                                    color: colors.white,
                                                    borderRadius: '12px',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    py: 1.5,
                                                    boxShadow: colors.shadowMd,
                                                    '&:disabled': {
                                                        background: colors.borderLight,
                                                        color: colors.textTertiary,
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                                startIcon={isExistingUserUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                                            >
                                                {isExistingUserUploading ? 'Uploading...' : 'Upload Rate Updates'}
                                            </Button>
                                            {existingUserValidationErrors.length > 0 && (
                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.error }}>
                                                    Fix {existingUserValidationErrors.length} errors before upload
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>

                                    <Typography variant="caption" sx={{ display: 'block', color: colors.textSecondary }}>
                                        Required columns: ItemCode, Rate, QualifyQty
                                    </Typography>
                                </Box>

                                {existingUserExcelData.length > 0 && existingUserValidationErrors.length === 0 && (
                                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1.5, border: '1px solid #c8e6c9' }}>
                                        <Typography variant="subtitle1" sx={{ color: '#2e7d32', fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                                            <CheckCircleIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                            Data validated successfully. {existingUserExcelData.length} valid record{existingUserExcelData.length > 1 ? 's' : ''} ready to upload.
                                        </Typography>
                                    </Box>
                                )}

                                {existingUserExcelData.length > 0 && existingUserValidationErrors.length === 0 && (
                                    <TableContainer component={Paper} elevation={0} sx={{ ...modernCardStyle, maxHeight: 300, overflow: 'auto', mb: 2 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Customer ID</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Area Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Item Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Material Description</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Qualify Qty</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Rate</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Validity Upto</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {existingUserExcelData.map((item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{item.customerId || '-'}</TableCell>
                                                        <TableCell>{item.areaCode || '-'}</TableCell>
                                                        <TableCell>{item.itemCode}</TableCell>
                                                        <TableCell>{item.itemDescription || '-'}</TableCell>
                                                        <TableCell>{item.qualifyQuantity}</TableCell>
                                                        <TableCell>{item.rate}</TableCell>
                                                        <TableCell>{item.validityUpto || '-'}</TableCell>
                                                        <TableCell align="center"><CheckCircleIcon color="success" fontSize="small" /></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {existingUserValidationErrors.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                                            Found {existingUserValidationErrors.length} validation error{existingUserValidationErrors.length > 1 ? 's' : ''}. Please fix these issues in your Excel file:
                                        </Alert>
                                        <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto', backgroundColor: '#fef2f2', border: `1px solid ${colors.error}` }}>
                                            {existingUserValidationErrors.map((error, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
                                                    <Box sx={{
                                                        minWidth: 24,
                                                        height: 24,
                                                        backgroundColor: colors.error,
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mr: 2,
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {index + 1}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: colors.errorDark, fontWeight: 500 }}>
                                                        {error}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Paper>
                                    </Box>
                                )}

                                {selectedExistingCustomer && customerDetailsData.length === 0 && !loadingCustomerDetails && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                        <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 1 }}>
                                            No Rate Details Found
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', mb: 2 }}>
                                            No rate scheme details are available for {selectedExistingCustomer.CustomerName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.primary, textAlign: 'center', fontWeight: 600 }}>
                                            ✨ Upload an Excel file above and click "Upload Excel" to create rates for this customer!
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </DialogContent>
                </Dialog>

                {/* --- Self-Contained Toast Component --- */}
                <Slide direction="up" in={toast.open} mountOnEnter unmountOnExit>
                    <Alert
                        severity={toast.severity}
                        variant={toast.severity === 'error' ? 'filled' : 'standard'}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            minWidth: 300,
                            zIndex: 1400,
                            borderRadius: '8px',
                            alignItems: 'center',
                            ...(toast.severity === 'error' ? {} : glassmorphismStyle),
                            border: toast.severity === 'error' ? 'none' : `1px solid ${colors[toast.severity] || colors.border}`
                        }}
                        action={
                            <IconButton size="small" color="inherit" onClick={handleCloseToast}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        }
                        iconMapping={{
                            success: <CheckCircleIcon fontSize="inherit" sx={{ color: colors.success }} />,
                            warning: <CloseIcon fontSize="inherit" sx={{ color: colors.warning }} />,
                            info: <CloseIcon fontSize="inherit" sx={{ color: colors.primary }} />,
                        }}
                    >
                        {toast.message}
                    </Alert>
                </Slide>
            </Box>

            {/* Central API Chain Processing Loader */}
            {isApiChainProcessing && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                        backdropFilter: 'blur(8px)',
                    }}
                >
                    <Paper
                        elevation={10}
                        sx={{
                            p: 4,
                            borderRadius: '16px',
                            background: colors.backgroundCard,
                            border: `2px solid ${colors.primary}`,
                            maxWidth: '500px',
                            textAlign: 'center',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.02)' },
                                '100%': { transform: 'scale(1)' }
                            }
                        }}
                    >
                        <CircularProgress
                            size={60}
                            sx={{
                                color: colors.primary,
                                mb: 3
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: colors.primary,
                                fontWeight: 'bold',
                                mb: 2
                            }}
                        >
                            Processing ....
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: colors.textSecondary,
                                mb: 3,
                                lineHeight: 1.6
                            }}
                        >
                            {'Please wait while we process your request ...'}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: colors.error,
                                fontWeight: '600',
                                background: `${colors.error}15`,
                                p: 1.5,
                                borderRadius: '8px',
                                border: `1px solid ${colors.error}30`
                            }}
                        >
                            ⚠️ Please do not close this window or navigate away
                        </Typography>
                    </Paper>
                </Box>
            )}
        </>
    );
};

export default UploadRateCustomerLookup;
